import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { PharmaCostReportInclusionCheckbox } from "@/components/expenses/PharmaCostReportInclusionCheckbox";
import { cn } from "@/lib/utils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { expensesApi, ExpenseMeta, AdmissionPickerItem, ExpenseEmployeeRow, ExpensePaymentStatus, ExpenseFundingSource, ImprestAdvanceRow, type ExpensePaginatedList, type ImprestTxnRow } from "@/api/expenses";
import SalaryRosterPanel from "./SalaryRosterPanel";
import { pharmacyApi } from "@/api/pharmacy";
import { Loader2, IndianRupee, ChevronsUpDown, Paperclip, ExternalLink, Pencil, Trash2, Download } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { superAdminApi } from "@/api/superAdmin";
import { doctorApi } from "@/api/doctor";
import { labApi } from "@/api/lab";
import { ensureDoctorPrefix } from "@/lib/doctorName";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { buildStaffOrVendorPayeeOptions } from "@/lib/payeeSelectOptions";
import { downloadBlob } from "@/lib/downloadBlob";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { chainApi, type ChainHospital } from "@/api/chain";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { formatIstDatetimeLocal, parseIstDatetimeLocalToIso } from "@/lib/istDatetimeLocal";

function unwrapAxios<T>(res: { data?: { data?: T; message?: string } }): T | undefined {
  return res.data?.data;
}

/** YYYY-MM-DD in local calendar for `<input type="date">`. */
function todayLocalISODate(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function defaultExpenseListFromDate(): string {
  const d = new Date();
  d.setDate(d.getDate() - 90);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function parseExpenseListPayload(raw: unknown): ExpensePaginatedList {
  if (Array.isArray(raw)) {
    return { rows: raw, total: raw.length, page: 1, pageSize: raw.length };
  }
  const o = raw as ExpensePaginatedList | null | undefined;
  if (o && Array.isArray(o.rows)) {
    return {
      rows: o.rows,
      total: o.total ?? o.rows.length,
      page: o.page ?? 1,
      pageSize: o.pageSize ?? o.rows.length,
      summary: o.summary,
    };
  }
  return { rows: [], total: 0, page: 1, pageSize: 50 };
}

/** Hospital reporting timezone (expense dates are calendar days, often stored as UTC midnight). */
const EXPENSE_DISPLAY_TZ = "Asia/Kolkata";

/** Show expense calendar date in IST without bogus 5:30 AM from UTC midnight. */
function formatExpenseDateIst(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-IN", {
    timeZone: EXPENSE_DISPLAY_TZ,
    day: "numeric",
    month: "numeric",
    year: "numeric",
  });
}

/** When the expense was marked paid (`paidAt`), IST date + time. */
function formatExpensePaidAtIst(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-IN", {
    timeZone: EXPENSE_DISPLAY_TZ,
    day: "numeric",
    month: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Table cell: expense date as IST calendar day; if missing, show created-at as IST date+time. */
function formatExpenseRowDateField(
  expenseDate: string | null | undefined,
  createdAt: string | null | undefined
): string {
  if (expenseDate) return formatExpenseDateIst(expenseDate);
  if (createdAt) {
    const d = new Date(createdAt);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString("en-IN", { timeZone: EXPENSE_DISPLAY_TZ });
  }
  return "—";
}

function AdmissionCostLinePaymentBadge({
  line,
}: {
  line: { source: string; paymentStatus?: ExpensePaymentStatus | null; paidAmount?: number | null };
}) {
  if (line.source !== "PATIENT_EXPENSE") {
    return <span className="text-muted-foreground">—</span>;
  }
  const status = line.paymentStatus ?? "UNPAID";
  if (status === "PAID") {
    return (
      <Badge variant="outline" className="text-emerald-800 border-emerald-300">
        Paid
      </Badge>
    );
  }
  if (status === "PARTIALLY_PAID") {
    return (
      <span className="inline-flex flex-col items-start gap-0.5">
        <Badge variant="outline" className="text-amber-800 border-amber-300">
          Partial
        </Badge>
        {line.paidAmount != null && (
          <span className="text-muted-foreground text-[10px]">
            ₹{Number(line.paidAmount).toLocaleString("en-IN")}
          </span>
        )}
      </span>
    );
  }
  return (
    <Badge variant="outline" className="text-amber-800 border-amber-300">
      Unpaid
    </Badge>
  );
}

/** YYYY-MM-DD for `<input type="date">` from stored instant (IST calendar; avoids UTC `.toISOString().slice(0,10)` shifting the day). */
function expenseIsoToIstYmd(iso: string | null | undefined, fallback: () => string): string {
  if (!iso) return fallback();
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return fallback();
  const s = new Intl.DateTimeFormat("en-CA", {
    timeZone: EXPENSE_DISPLAY_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(d);
  return s && /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : fallback();
}

/** YYYY-MM for month inputs from stored instant (IST). */
function expenseIsoToIstYm(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: EXPENSE_DISPLAY_TZ,
    year: "numeric",
    month: "2-digit",
  }).formatToParts(d);
  const y = parts.find((p) => p.type === "year")?.value;
  const m = parts.find((p) => p.type === "month")?.value;
  return y && m ? `${y}-${m}` : "";
}

const canApprovePatientExpenses = (role?: string) =>
  role === "HOSPITAL_ADMIN" || role === "BUSINESS_ANALYST";

const canAppendOrCorrectExpenses = (role?: string) =>
  role === "HOSPITAL_ADMIN" ||
  role === "OPERATIONS_MANAGER" ||
  role === "BUSINESS_ANALYST";

const canMarkExpensePaidRole = (role?: string) =>
  role === "RECEPTIONIST" ||
  role === "OPERATIONS_MANAGER" ||
  role === "HOSPITAL_ADMIN" ||
  role === "BUSINESS_ANALYST";

const PATIENT_PAYOUT_CATEGORIES = new Set([
  "SURGEON_PAYOUT",
  "ANAESTHETIST_PAYOUT",
  "ASSISTANT_SURGEON_PAYOUT",
  "VISITING_CONSULTANT_FEE",
  "DOCTOR_VISIT_NON_BILLABLE",
  "OUTSIDE_LAB_PAYOUT",
  "CUSTOM_PAYOUT"
]);

/** B2B marketplace / partner payout — partner name stored in subCategory. */
const B2B_PARTNER_PAYOUT_CATEGORY = "B2B_PARTNER_PAYOUT";

/** Central categories that use the doctor dropdown + optional free-text name (same as OPD payout). */
const CENTRAL_DOCTOR_PAYEE_CATEGORIES = new Set([
  "OPD_PAYOUT",
  "LOCUM",
  "SURGEON_PAYOUT",
  "ANESTHETIST_PAYOUT"
]);

function isCentralDoctorPayeeCategory(category: string): boolean {
  return CENTRAL_DOCTOR_PAYEE_CATEGORIES.has(category);
}

/** Vendor name field with live suggestions from saved central expenses. */
function CentralVendorNameTypeahead({
  value,
  onChange,
  category,
  searchAllCategories = false,
  placeholder = "Supplier or vendor",
  className,
  disabled,
  hospitalId,
  queryEnabled = true,
}: {
  value: string;
  onChange: (v: string) => void;
  category?: string;
  searchAllCategories?: boolean;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  hospitalId?: string;
  /** False for super admin until a hospital is selected. */
  queryEnabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const enabled = queryEnabled && (searchAllCategories || !!category);

  const { data: suggestions = [], isFetching } = useQuery({
    queryKey: [
      "expense-central-vendor-suggest",
      searchAllCategories,
      category,
      value,
      hospitalId,
    ],
    queryFn: async () => {
      const r = await expensesApi.listCentralPayeeVendors({
        ...(searchAllCategories ? { all: true } : { category: category! }),
        q: value,
        limit: 25,
        ...(hospitalId ? { hospitalId } : {}),
      });
      return (unwrapAxios(r) as { names?: string[] } | undefined)?.names ?? [];
    },
    enabled: enabled && open,
    staleTime: 10_000,
  });

  const showList = open && (isFetching || suggestions.length > 0);

  return (
    <div className="relative">
      <Input
        className={className}
        value={value}
        disabled={disabled}
        placeholder={placeholder}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => window.setTimeout(() => setOpen(false), 180)}
        autoComplete="off"
      />
      {showList && (
        <ul
          className="bg-popover text-popover-foreground absolute z-50 mt-1 max-h-52 w-full overflow-y-auto rounded-md border shadow-md"
          role="listbox"
        >
          {isFetching && (
            <li className="text-muted-foreground flex items-center gap-2 px-3 py-2 text-sm">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading vendors…
            </li>
          )}
          {!isFetching &&
            suggestions.map((name) => (
              <li key={name}>
                <button
                  type="button"
                  role="option"
                  className="hover:bg-muted w-full px-3 py-2 text-left text-sm"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    onChange(name);
                    setOpen(false);
                  }}
                >
                  {name}
                </button>
              </li>
            ))}
          {!isFetching && value.trim() && !suggestions.includes(value.trim()) && (
            <li className="text-muted-foreground border-t px-3 py-2 text-xs">
              Press Save to use &quot;{value.trim()}&quot; as a new vendor
            </li>
          )}
        </ul>
      )}
    </div>
  );
}

const EXPENSE_CATEGORY_ACRONYMS = new Set(["OPD", "EMI", "IPD", "TDS", "LAB", "OT"]);

/** Consistent title-style labels for expense category enums (not raw UPPER_SNAKE). */
function formatExpenseCategoryLabel(category: string): string {
  switch (category) {
    case "ONLINE_MARKETING":
      return "Online marketing";
    case "OFFLINE_MARKETING":
      return "Offline marketing";
    case "LOCUM":
      return "Locum";
    case "SURGEON_PAYOUT":
      return "Surgeon payout";
    case "ANESTHETIST_PAYOUT":
      return "Anesthetist payout";
    case "SURGENIX_PAYOUT":
      return "Surgenix (legacy)";
    case "CARELANE_PAYOUT":
      return "Carelane (legacy)";
    case "HEXA_PAYOUT":
      return "Hexa (legacy)";
    case "PRACTO_PAYOUT":
      return "Practo (legacy)";
    case "B2B_PARTNER_PAYOUT":
      return "B2B Partner Payout (Carelane, Hexa, Practo, Surgenix etc)";
    case "CUSTOM_PAYOUT":
      return "Custom payout";
    case "EMI_FINANCE_CHARGES":
      return "EMI/Reimbursement Cost (Finance Partner Payout)";
    case "PRACTO_CENTRAL":
      return "Practo";
    case "CAPEX_HOSPITAL":
      return "Capex hospital";
    case "REFERRAL":
      return "Referral";
    case "SECURITY_DEPOSIT":
      return "Security deposit";
    case "FUNDING_EXPENSES":
      return "Funding Expenses";
    case "TEAM_ENGAGEMENT":
      return "Team Engagement";
    case "CLIENT_ENGAGEMENT":
      return "Client Engagement";
    case "BIOMEDICAL_WASTE":
      return "Biomedical Waste";
    case "OTHER_EXPENSES":
    case "OTHER_CENTRAL":
      return "Other Expenses";
    default:
      return category
        .split("_")
        .map((word) => {
          const upper = word.toUpperCase();
          if (EXPENSE_CATEGORY_ACRONYMS.has(upper)) return upper;
          const lower = word.toLowerCase();
          return lower.charAt(0).toUpperCase() + lower.slice(1);
        })
        .join(" ");
  }
}

function sortExpenseCategoriesByLabel(categories: string[] | undefined): string[] {
  if (!categories?.length) return [];
  return [...categories].sort((a, b) =>
    formatExpenseCategoryLabel(a).localeCompare(formatExpenseCategoryLabel(b), "en", {
      sensitivity: "base"
    })
  );
}

const RUDRAM_HOSPITAL_LABEL = "Rudram (A Unit of TRUE Hospitals)";

/** Fallback labels for stored hospitalUnit enum (filters / legacy rows). */
const CENTRAL_EXPENSE_HOSPITAL_UNITS = [
  { value: "SEC_52", label: RUDRAM_HOSPITAL_LABEL },
  { value: "LAJPAT_NAGAR", label: "TRUE ARTIS South Delhi" },
  { value: "CENTRAL", label: "Central" },
] as const;

const CENTRAL_HOSPITAL_SELECT_CENTRAL = "CENTRAL";

function formatCentralExpenseHospitalUnitLabel(unit: string | null | undefined): string {
  const row = CENTRAL_EXPENSE_HOSPITAL_UNITS.find((u) => u.value === unit);
  return row?.label ?? RUDRAM_HOSPITAL_LABEL;
}

function inferCentralHospitalUnitFromHospitalName(name: string | null | undefined): string {
  const n = name ?? "";
  if (/rudram|gurugram|gurgaon|122003|sec(?:tor)?\s*52/i.test(n)) return "SEC_52";
  if (/lajpat|south\s*delhi|110024|ring\s*road|true\s*artis\s*south/i.test(n)) return "LAJPAT_NAGAR";
  return "SEC_52";
}

function formatCentralExpenseHospitalRowLabel(
  row: { hospitalUnit?: string | null; hospitalId?: string | null },
  hospitals: ChainHospital[]
): string {
  if (row.hospitalId) {
    const match = hospitals.find((h) => h.id === row.hospitalId);
    if (match?.name) return match.name;
  }
  return formatCentralExpenseHospitalUnitLabel(row.hospitalUnit);
}

function imprestSettlementExpenseSummary(t: ImprestTxnRow): {
  expenseNumber: string;
  category: string;
  vendor: string;
  enteredBy: string;
  status: string;
  description: string;
  patientName?: string;
} | null {
  const ce = t.centralExpense;
  if (ce) {
    return {
      expenseNumber: ce.expenseNumber || "—",
      category: formatExpenseCategoryLabel(ce.category),
      vendor: ce.vendorName || "—",
      enteredBy: ce.createdBy?.name || ce.createdByName || "—",
      status: ce.workflowStatus,
      description: ce.description?.trim() || "—",
    };
  }
  const pe = t.patientExpense;
  if (pe) {
    const patientName = pe.patient?.name?.trim();
    return {
      expenseNumber: pe.expenseNumber || "—",
      category: formatExpenseCategoryLabel(pe.category),
      vendor: pe.payeeVendorName || "—",
      enteredBy: pe.createdByName || "—",
      status: pe.workflowStatus,
      description: pe.description?.trim() || "—",
      patientName: patientName || undefined,
    };
  }
  return null;
}

function expenseEmployeeScopeFromHospitalSelect(selectValue: string): "CENTRAL" | "UNIT" {
  return selectValue === CENTRAL_HOSPITAL_SELECT_CENTRAL ? "CENTRAL" : "UNIT";
}

/** Show Mark paid for draft, pending, or approved (not paid/rejected). */
function canShowMarkPaidForStatus(workflowStatus: string): boolean {
  return ["DRAFT", "PENDING_APPROVAL", "APPROVED"].includes(workflowStatus);
}

/** Draft / pending: expense staff can attach; approved/paid: hospital admin only. */
function canUploadExpenseProof(workflowStatus: string, role?: string): boolean {
  if (workflowStatus === "DRAFT") return true;
  if (workflowStatus === "PENDING_APPROVAL") {
    if (role === "HOSPITAL_ADMIN" || role === "BUSINESS_ANALYST") return true;
    return ["RECEPTIONIST", "OPERATIONS_MANAGER", "NURSE", "DOCTOR", "RMO", "PHARMACIST"].includes(role ?? "");
  }
  if (["APPROVED", "PAID"].includes(workflowStatus)) {
    return (
      role === "HOSPITAL_ADMIN" ||
      role === "OPERATIONS_MANAGER" ||
      role === "BUSINESS_ANALYST"
    );
  }
  return false;
}

type ExpenseProofRow = { id?: string; url: string; fileName: string };

function expenseProofList(row: {
  proofUrl?: string | null;
  proofFileName?: string | null;
  proofCount?: number;
  proofs?: Array<{ id: string; url: string; fileName: string }> | null;
}): ExpenseProofRow[] {
  if (Array.isArray(row.proofs) && row.proofs.length > 0) {
    return row.proofs.map((p) => ({ id: p.id, url: p.url, fileName: p.fileName }));
  }
  if (row.proofUrl) {
    return [{ url: row.proofUrl, fileName: row.proofFileName || "View proof" }];
  }
  if (row.proofCount && row.proofCount > 0) {
    return [{ url: row.proofUrl || "#", fileName: `${row.proofCount} file(s) attached` }];
  }
  return [];
}

const PROOF_FILE_ACCEPT =
  ".pdf,.doc,.docx,.xls,.xlsx,image/jpeg,image/png,image/webp,image/gif";
const MAX_EXPENSE_PROOF_FILES = 10;

export default function ExpensesPage() {
  const { user, isChainAdmin, activeHospitalId } = useAuth();
  const qc = useQueryClient();
  const [expenseTab, setExpenseTab] = useState("patient");
  const [patientPage, setPatientPage] = useState(1);
  const [centralPage, setCentralPage] = useState(1);
  const [patientListFrom, setPatientListFrom] = useState(defaultExpenseListFromDate);
  const [patientListTo, setPatientListTo] = useState(todayLocalISODate);
  const [centralListFrom, setCentralListFrom] = useState(defaultExpenseListFromDate);
  const [centralListTo, setCentralListTo] = useState(todayLocalISODate);
  const expensePageSize = 50;
  const isSuperAdmin = user?.role === "SUPER_ADMIN";
  const isHospitalAdmin =
    user?.role === "HOSPITAL_ADMIN" || user?.role === "BUSINESS_ANALYST";
  const isStrictHospitalAdmin = user?.role === "HOSPITAL_ADMIN";
  const isSubAdmin =
    user?.role === "HOSPITAL_ADMIN" && user?.adminScope !== "CHAIN";
  const canIssueImprest = isChainAdmin || isSubAdmin;
  const canManageSalaryRosterAdmin = isChainAdmin || isSubAdmin;
  const canManageSalaryRosterOps = user?.role === "OPERATIONS_MANAGER";
  const canManageSalaryRoster = canManageSalaryRosterAdmin || canManageSalaryRosterOps;
  const canMarkPaid = canMarkExpensePaidRole(user?.role);
  const readOnlySuperAdmin = isSuperAdmin;
  const [hospitalId, setHospitalId] = useState("");
  /** accrual = costs when admin approves (APPROVED+PAID); cash = only after mark paid (PAID only) */
  const [reportCostBasis, setReportCostBasis] = useState<"accrual" | "cash">("accrual");

  const lockedCentralHospitalUnit = useMemo(() => {
    const hid = activeHospitalId || user?.hospitalId || hospitalId || "";
    const name =
      user?.hospitals?.find((h) => h.id === hid)?.name ??
      user?.hospitals?.[0]?.name ??
      null;
    return inferCentralHospitalUnitFromHospitalName(name);
  }, [activeHospitalId, user?.hospitalId, user?.hospitals, hospitalId]);

  const lockedHospitalName = useMemo(() => {
    const hid = activeHospitalId || user?.hospitalId || hospitalId || "";
    return (
      user?.hospitals?.find((h) => h.id === hid)?.name ??
      user?.hospitals?.[0]?.name ??
      formatCentralExpenseHospitalUnitLabel(lockedCentralHospitalUnit)
    );
  }, [activeHospitalId, user?.hospitalId, user?.hospitals, hospitalId, lockedCentralHospitalUnit]);

  const { data: chainHospitals = [] } = useQuery({
    queryKey: ["chain-group-hospitals-expenses"],
    queryFn: async () => {
      const r = await chainApi.getGroup();
      const group = r.data?.data as { hospitals?: ChainHospital[] } | undefined;
      return group?.hospitals ?? [];
    },
    enabled: isChainAdmin,
    staleTime: 60_000,
  });

  const chainHospitalOptions = useMemo(() => {
    const hospitals = (chainHospitals || []).filter((h) => h?.id && h?.name);
    return [
      ...hospitals.map((h) => ({ value: h.id, label: h.name })),
      { value: CENTRAL_HOSPITAL_SELECT_CENTRAL, label: "Central" },
    ];
  }, [chainHospitals]);

  const defaultChainHospitalSelect = useMemo(() => {
    if (!isChainAdmin) return lockedCentralHospitalUnit;
    const hid = activeHospitalId || user?.hospitalId || "";
    if (hid && chainHospitalOptions.some((o) => o.value === hid)) return hid;
    const rudram = chainHospitalOptions.find((o) =>
      /rudram|sec(?:tor)?\s*52/i.test(o.label)
    );
    return rudram?.value ?? chainHospitalOptions[0]?.value ?? CENTRAL_HOSPITAL_SELECT_CENTRAL;
  }, [isChainAdmin, lockedCentralHospitalUnit, activeHospitalId, user?.hospitalId, chainHospitalOptions]);

  /** Chain admin: hospital id or CENTRAL. Sub-admin: locked unit tag (display only). */
  const [cHospitalSelect, setCHospitalSelect] = useState(CENTRAL_HOSPITAL_SELECT_CENTRAL);

  useEffect(() => {
    if (isChainAdmin) {
      setCHospitalSelect((prev) =>
        chainHospitalOptions.some((o) => o.value === prev) ? prev : defaultChainHospitalSelect
      );
    } else {
      setCHospitalSelect(lockedCentralHospitalUnit);
    }
  }, [isChainAdmin, lockedCentralHospitalUnit, defaultChainHospitalSelect, chainHospitalOptions]);

  const centralExpenseTargetHospitalId = useMemo(() => {
    if (isChainAdmin && cHospitalSelect !== CENTRAL_HOSPITAL_SELECT_CENTRAL) {
      return cHospitalSelect;
    }
    if (isSuperAdmin && hospitalId.trim()) return hospitalId.trim();
    return activeHospitalId || user?.hospitalId || undefined;
  }, [
    isChainAdmin,
    cHospitalSelect,
    isSuperAdmin,
    hospitalId,
    activeHospitalId,
    user?.hospitalId,
  ]);

  const reportParams = useMemo(
    () => (isSuperAdmin && hospitalId ? { hospitalId } : undefined),
    [isSuperAdmin, hospitalId]
  );

  const { data: hospitals } = useQuery({
    queryKey: ["super-admin-hospitals-expenses"],
    queryFn: () => superAdminApi.getHospitals(),
    enabled: isSuperAdmin
  });

  const { data: meta, isLoading: metaLoading } = useQuery({
    queryKey: ["expenses-meta", reportParams?.hospitalId],
    queryFn: async () => {
      const r = await expensesApi.getMeta(
        reportParams?.hospitalId ? { hospitalId: reportParams.hospitalId } : undefined
      );
      return unwrapAxios(r) as ExpenseMeta | undefined;
    },
    enabled: !isSuperAdmin || !!hospitalId
  });

  const centralCategoriesSorted = useMemo(
    () => sortExpenseCategoriesByLabel(meta?.centralCategories),
    [meta?.centralCategories]
  );
  const centralCategoriesAllSorted = useMemo(
    () => sortExpenseCategoriesByLabel(meta?.centralCategoriesAll),
    [meta?.centralCategoriesAll]
  );
  const patientCategoriesSorted = useMemo(
    () => sortExpenseCategoriesByLabel(meta?.patientCategories),
    [meta?.patientCategories]
  );

  const pharmaVendorListHospitalId =
    centralExpenseTargetHospitalId ?? reportParams?.hospitalId;

  const { data: pharmaVendors = [] } = useQuery({
    queryKey: ["pharmacy-vendors", pharmaVendorListHospitalId],
    queryFn: async () => {
      const res = await pharmacyApi.listVendors(
        pharmaVendorListHospitalId ? { hospitalId: pharmaVendorListHospitalId } : undefined
      );
      return ((res.data as { data?: { id: string; name: string; phone: string }[] })?.data ?? []) as {
        id: string;
        name: string;
        phone: string;
      }[];
    },
    enabled:
      !readOnlySuperAdmin &&
      (!isSuperAdmin || !!hospitalId) &&
      (!isChainAdmin || !!pharmaVendorListHospitalId),
  });

  const [pAdmission, setPAdmission] = useState("");
  const [admissionPickerOpen, setAdmissionPickerOpen] = useState(false);
  const [filterPatientPickerOpen, setFilterPatientPickerOpen] = useState(false);
  const [admissionSearchInput, setAdmissionSearchInput] = useState("");
  const [filterPatientSearchInput, setFilterPatientSearchInput] = useState("");
  const [debouncedAdmissionQ, setDebouncedAdmissionQ] = useState("");
  const [debouncedFilterPatientQ, setDebouncedFilterPatientQ] = useState("");
  const [selectedAdmissionLabel, setSelectedAdmissionLabel] = useState("");
  const [selectedAdmissionDates, setSelectedAdmissionDates] = useState<{
    admissionDate: string | null;
    dischargeDate: string | null;
  } | null>(null);
  const [selectedAdmissionDoctor, setSelectedAdmissionDoctor] = useState<{
    name: string;
    specialisation?: string | null;
  } | null>(null);
  const [selectedAdmissionSurgeries, setSelectedAdmissionSurgeries] = useState<
    Array<{ specialisation?: string | null; surgeryName: string }>
  >([]);
  /** Admin: show all IPD expenses for this patient (all admissions). */
  const [filterPatientId, setFilterPatientId] = useState<string | null>(null);
  const [filterPatientLabel, setFilterPatientLabel] = useState("");

  // List filters (search)
  const [patientVendorSearch, setPatientVendorSearch] = useState("");
  const [patientTdsFilter, setPatientTdsFilter] = useState<"ALL" | "YES" | "NO">("ALL");
  const [patientCategoryFilter, setPatientCategoryFilter] = useState<string>("ALL");
  const [patientNameSearch, setPatientNameSearch] = useState("");
  const [patientEnteredBySearch, setPatientEnteredBySearch] = useState("");
  const [centralVendorSearch, setCentralVendorSearch] = useState("");
  const [centralEnteredBySearch, setCentralEnteredBySearch] = useState("");
  const [centralCategoryFilter, setCentralCategoryFilter] = useState<string>("ALL");
  const [centralHospitalUnitFilter, setCentralHospitalUnitFilter] = useState<string>("ALL");
  const [centralTdsFilter, setCentralTdsFilter] = useState<"ALL" | "YES" | "NO">("ALL");
  const [unpaidPatientSearch, setUnpaidPatientSearch] = useState("");
  const [unpaidVendorSearch, setUnpaidVendorSearch] = useState("");
  const [unpaidCategoryFilter, setUnpaidCategoryFilter] = useState<string>("ALL");
  const [unpaidIpdSearch, setUnpaidIpdSearch] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setDebouncedAdmissionQ(admissionSearchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [admissionSearchInput]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedFilterPatientQ(filterPatientSearchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [filterPatientSearchInput]);

  const { data: admissionOptions = [], isFetching: admissionsFetching } = useQuery({
    queryKey: ["expense-admissions", debouncedAdmissionQ, reportParams?.hospitalId],
    queryFn: async () => {
      const r = await expensesApi.searchAdmissions({
        q: debouncedAdmissionQ || undefined,
        limit: 30,
        ...(reportParams?.hospitalId ? { hospitalId: reportParams.hospitalId } : {})
      });
      return (unwrapAxios(r) as AdmissionPickerItem[]) ?? [];
    },
    enabled: (!isSuperAdmin || !!hospitalId) && admissionPickerOpen
  });

  const canSeeAdmissionModuleCosts = isHospitalAdmin || isSuperAdmin;

  // When creating a patient expense, show costs for this IPD admission (pharma/lab for admin + business analyst).
  const { data: admissionCostData, isLoading: selectedPatientExpensesLoading } = useQuery({
    queryKey: ["expenses-admission-cost-lines", reportParams?.hospitalId, pAdmission, canSeeAdmissionModuleCosts],
    queryFn: async () => {
      if (!pAdmission) return { lines: [], pharmaItems: [], labItems: [], totals: { patientExpense: 0, pharma: 0, lab: 0, labInternal: 0, labExternal: 0, all: 0 } };
      const r = await expensesApi.getAdmissionCostLines(
        pAdmission,
        reportParams?.hospitalId ? { hospitalId: reportParams.hospitalId } : undefined
      );
      return r.data.data;
    },
    enabled: !!pAdmission && (!isSuperAdmin || !!hospitalId)
  });

  const selectedAdmissionCostLines = admissionCostData?.lines ?? [];
  const selectedAdmissionCostTotals = admissionCostData?.totals;
  const selectedPharmaCostItems = admissionCostData?.pharmaItems ?? [];
  const selectedLabCostItems = admissionCostData?.labItems ?? [];
  const [moduleCostBreakdown, setModuleCostBreakdown] = useState<"pharma" | "lab" | null>(null);

  const { data: admissionBillingSummary, isLoading: admissionBillingLoading } = useQuery({
    queryKey: ["expense-admission-billing", reportParams?.hospitalId, pAdmission],
    queryFn: async () => {
      if (!pAdmission) return null;
      const r = await expensesApi.getAdmissionBillingSummary(
        pAdmission,
        reportParams?.hospitalId ? { hospitalId: reportParams.hospitalId } : undefined
      );
      return unwrapAxios(r) as {
        ipdNumber: string;
        admissionStatus: string;
        billNumber: string | null;
        billStatus: string | null;
        netAmount: number | null;
        partnerName?: string | null;
        referrerName?: string | null;
        salesPersonName?: string | null;
        financingPartnerName?: string | null;
        totalAccrued?: number | null;
        totalDeposits?: number | null;
        deskDeposits?: number | null;
        thirdPartyCollected?: number | null;
        thirdPartyPending?: number | null;
        outstanding?: number | null;
        pendingAmount?: number | null;
        excessAmount?: number | null;
        pendingThirdPartyCredit?: number | null;
        isFullyCollected?: boolean;
      };
    },
    enabled:
      !!pAdmission &&
      (isHospitalAdmin || isSuperAdmin) &&
      (!isSuperAdmin || !!hospitalId)
  });

  const { data: filterPatientAdmissionOptions = [], isFetching: filterPatientAdmissionsFetching } =
    useQuery({
      queryKey: ["expense-admissions-filter-patient", debouncedFilterPatientQ, reportParams?.hospitalId],
      queryFn: async () => {
        const r = await expensesApi.searchAdmissions({
          q: debouncedFilterPatientQ || undefined,
          limit: 30,
          ...(reportParams?.hospitalId ? { hospitalId: reportParams.hospitalId } : {})
        });
        return (unwrapAxios(r) as AdmissionPickerItem[]) ?? [];
      },
      enabled:
        (!isSuperAdmin || !!hospitalId) &&
        filterPatientPickerOpen &&
        (isHospitalAdmin || isSuperAdmin)
    });

  const [pCategory, setPCategory] = useState("");
  const [pAmount, setPAmount] = useState("");
  const [pPaymentMode, setPPaymentMode] = useState("");
  const [pFundingSource, setPFundingSource] = useState<ExpenseFundingSource>("REGULAR");
  const [pImprestStaffId, setPImprestStaffId] = useState("");
  const [cFundingSource, setCFundingSource] = useState<ExpenseFundingSource>("REGULAR");
  const [cImprestStaffId, setCImprestStaffId] = useState("");
  const [imprestStaffId, setImprestStaffId] = useState("");
  const [imprestAmount, setImprestAmount] = useState("");
  const [imprestPaymentMode, setImprestPaymentMode] = useState("CASH");
  const [imprestNotes, setImprestNotes] = useState("");
  const [imprestTxnAdvanceId, setImprestTxnAdvanceId] = useState<string | null>(null);
  const [imprestEditTxn, setImprestEditTxn] = useState<{
    id: string;
    amount: string;
    notes: string;
  } | null>(null);
  /** Chain admin: hospital id or CENTRAL for issue + filtered balances. */
  const [imprestHospitalSelect, setImprestHospitalSelect] = useState(CENTRAL_HOSPITAL_SELECT_CENTRAL);
  const [pTdsDeducted, setPTdsDeducted] = useState(false);
  const [pExpenseDate, setPExpenseDate] = useState(todayLocalISODate);
  const [pServiceMonth, setPServiceMonth] = useState("");
  const [pDesc, setPDesc] = useState("");
  const [pVendor, setPVendor] = useState("");
  const [pPayeeHospitalStaffId, setPPayeeHospitalStaffId] = useState("");
  const [pLabExternalVendorId, setPLabExternalVendorId] = useState("");
  const [pB2bPartner, setPB2bPartner] = useState("");
  const [editPatientB2bPartner, setEditPatientB2bPartner] = useState("");
  const [editPatientOpen, setEditPatientOpen] = useState(false);
  const [editPatientCategory, setEditPatientCategory] = useState("");
  const [addB2bPartnerOpen, setAddB2bPartnerOpen] = useState(false);
  const [newB2bPartnerName, setNewB2bPartnerName] = useState("");
  const [pInitial, setPInitial] = useState<"DRAFT" | "PENDING_APPROVAL">("DRAFT");
  const [pProofFile, setPProofFile] = useState<File[]>([]);
  const [pProofInputKey, setPProofInputKey] = useState(0);

  useEffect(() => {
    // Reset payee fields when category changes
    setPPayeeHospitalStaffId("");
    setPLabExternalVendorId("");
    setPB2bPartner("");
    // keep vendor text; user may be typing (only clear when moving away from payout categories)
    if (!PATIENT_PAYOUT_CATEGORIES.has(pCategory) && pCategory !== B2B_PARTNER_PAYOUT_CATEGORY) {
      setPVendor("");
    }
  }, [pCategory]);

  const { data: b2bPartnerNames = [], isLoading: b2bPartnersLoading } = useQuery({
    queryKey: ["expense-b2b-partners", reportParams?.hospitalId],
    queryFn: async () => {
      const r = await expensesApi.listB2bPartners({
        ...(reportParams?.hospitalId ? { hospitalId: reportParams.hospitalId } : {}),
      });
      return (unwrapAxios(r) as { names?: string[] } | undefined)?.names ?? [];
    },
    enabled:
      (!isSuperAdmin || !!hospitalId) &&
      (pCategory === B2B_PARTNER_PAYOUT_CATEGORY ||
        (editPatientOpen && editPatientCategory === B2B_PARTNER_PAYOUT_CATEGORY)),
    staleTime: 30_000,
  });

  const createB2bPartnerMut = useMutation({
    mutationFn: async (name: string) => {
      const r = await expensesApi.createB2bPartner({
        name,
        ...(reportParams?.hospitalId ? { hospitalId: reportParams.hospitalId } : {}),
      });
      return (unwrapAxios(r) as { name?: string } | undefined)?.name ?? name;
    },
    onSuccess: (name) => {
      toast.success(`Partner "${name}" saved`);
      setPB2bPartner(name);
      setEditPatientB2bPartner(name);
      setPVendor(name);
      setEditPatientVendor(name);
      setNewB2bPartnerName("");
      setAddB2bPartnerOpen(false);
      qc.invalidateQueries({ queryKey: ["expense-b2b-partners"] });
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(typeof msg === "string" ? msg : "Failed to save partner");
    },
  });

  const duplicatePatientExpenseMatches = useMemo(() => {
    const amt = Number(pAmount);
    if (!pAdmission) return [];
    if (!Number.isFinite(amt) || amt <= 0) return [];
    const vend = (pVendor || "").trim().toLowerCase();
    if (!vend) return [];
    return selectedAdmissionCostLines.filter((r) => {
      if (r.source !== "PATIENT_EXPENSE") return false;
      const rv = String(r.label ?? "").trim().toLowerCase();
      const ra = Number(r.amount ?? 0);
      if (!rv || !Number.isFinite(ra)) return false;
      if (rv !== vend) return false;
      if (Math.abs(ra - amt) > 0.0001) return false;
      return true;
    });
  }, [pAdmission, pAmount, pVendor, selectedAdmissionCostLines]);

  const { data: doctors = [] } = useQuery({
    queryKey: ["expense-doctors", reportParams?.hospitalId],
    queryFn: async () => {
      const rows = await doctorApi.getDoctorsByHospital();
      return (rows as Array<{ id: string; name: string; role?: string }>) ?? [];
    },
    enabled: !isSuperAdmin || !!hospitalId
  });

  useEffect(() => {
    if (isChainAdmin) {
      setImprestHospitalSelect(defaultChainHospitalSelect);
    }
  }, [isChainAdmin, defaultChainHospitalSelect]);

  const imprestIssueScope = useMemo(() => {
    if (!isChainAdmin) {
      return {
        staffHospitalId: reportParams?.hospitalId || activeHospitalId || user?.hospitalId || undefined,
        targetHospitalId: undefined as string | undefined,
        hospitalUnit: undefined as string | undefined,
      };
    }
    if (imprestHospitalSelect === CENTRAL_HOSPITAL_SELECT_CENTRAL) {
      return {
        staffHospitalId: activeHospitalId || user?.hospitalId || undefined,
        targetHospitalId: undefined as string | undefined,
        hospitalUnit: "CENTRAL",
      };
    }
    return {
      staffHospitalId: imprestHospitalSelect,
      targetHospitalId: imprestHospitalSelect,
      hospitalUnit: undefined as string | undefined,
    };
  }, [
    isChainAdmin,
    imprestHospitalSelect,
    reportParams?.hospitalId,
    activeHospitalId,
    user?.hospitalId,
  ]);

  const { data: imprestData } = useQuery({
    queryKey: [
      "expenses-imprest",
      reportParams?.hospitalId,
      isChainAdmin ? imprestHospitalSelect : "own",
    ],
    queryFn: async () => {
      const r = await expensesApi.listImprest({
        hospitalId: reportParams?.hospitalId,
        includeClosed: false,
        ...(isChainAdmin
          ? imprestHospitalSelect === CENTRAL_HOSPITAL_SELECT_CENTRAL
            ? { hospitalUnit: "CENTRAL" }
            : { targetHospitalId: imprestHospitalSelect }
          : {}),
      });
      return unwrapAxios(r);
    },
    enabled: !!user && (!isSuperAdmin || !!hospitalId),
  });

  const { data: hospitalStaff = [] } = useQuery({
    queryKey: [
      "expenses-imprest-staff",
      imprestIssueScope.staffHospitalId,
      imprestIssueScope.targetHospitalId,
      imprestIssueScope.hospitalUnit,
    ],
    queryFn: async () => {
      const r = await expensesApi.listImprestPayees({
        hospitalId: reportParams?.hospitalId,
        ...(isChainAdmin
          ? imprestHospitalSelect === CENTRAL_HOSPITAL_SELECT_CENTRAL
            ? { hospitalUnit: "CENTRAL" }
            : { targetHospitalId: imprestHospitalSelect }
          : {}),
      });
      return unwrapAxios(r)?.employees ?? [];
    },
    enabled:
      !!user &&
      canIssueImprest &&
      (!isSuperAdmin || !!hospitalId) &&
      (isChainAdmin ? !!imprestHospitalSelect : !!imprestIssueScope.staffHospitalId),
  });

  useEffect(() => {
    const def = imprestData?.defaultFundingSource;
    if (def === "IMPREST" || def === "REGULAR") {
      setPFundingSource(def);
      setCFundingSource(def);
    }
    if (imprestData?.myAdvanceId && (imprestData.myBalance ?? 0) > 0) {
      const mine = imprestData.advances?.find((a) => a.id === imprestData.myAdvanceId);
      if (mine) {
        setPImprestStaffId(mine.hospitalStaffId);
        setCImprestStaffId(mine.hospitalStaffId);
      }
    }
  }, [imprestData]);

  const openImprestAdvances: ImprestAdvanceRow[] = imprestData?.advances ?? [];
  const imprestStaffOptions = useMemo(() => {
    return openImprestAdvances
      .filter((a) => a.remainingBalance > 0)
      .map((a) => ({
        id: a.hospitalStaffId,
        name: a.hospitalStaff?.name || a.hospitalStaffId,
        balance: a.remainingBalance,
        advanceId: a.id,
      }));
  }, [openImprestAdvances]);

  const { data: imprestTxns = [] } = useQuery({
    queryKey: ["expenses-imprest-txns", imprestTxnAdvanceId, reportParams?.hospitalId],
    queryFn: async () => {
      if (!imprestTxnAdvanceId) return [];
      const r = await expensesApi.listImprestTxns(imprestTxnAdvanceId, {
        hospitalId: reportParams?.hospitalId,
      });
      return unwrapAxios(r)?.transactions ?? [];
    },
    enabled: !!imprestTxnAdvanceId,
  });

  const updateImprestSettlementMut = useMutation({
    mutationFn: async () => {
      if (!imprestEditTxn) throw new Error("No settlement selected");
      const amount = Number(imprestEditTxn.amount);
      if (!Number.isFinite(amount) || amount <= 0) throw new Error("Enter a valid amount");
      const r = await expensesApi.updateImprestSettlement(
        imprestEditTxn.id,
        { amount, notes: imprestEditTxn.notes.trim() || null },
        reportParams?.hospitalId ? { hospitalId: reportParams.hospitalId } : undefined
      );
      return r.data?.data;
    },
    onSuccess: () => {
      toast.success("Imprest expense updated");
      setImprestEditTxn(null);
      qc.invalidateQueries({ queryKey: ["expenses-imprest"] });
      qc.invalidateQueries({ queryKey: ["expenses-imprest-txns"] });
      qc.invalidateQueries({ queryKey: ["expenses-patient"] });
      qc.invalidateQueries({ queryKey: ["expenses-central"] });
    },
    onError: (err: unknown) => {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (err instanceof Error ? err.message : "Could not update settlement");
      toast.error(typeof msg === "string" ? msg : "Could not update settlement");
    },
  });

  const deleteImprestSettlementMut = useMutation({
    mutationFn: async (txnId: string) => {
      const r = await expensesApi.deleteImprestSettlement(
        txnId,
        reportParams?.hospitalId ? { hospitalId: reportParams.hospitalId } : undefined
      );
      return r.data?.data;
    },
    onSuccess: (data) => {
      toast.success(
        data?.restoredAmount
          ? `Expense deleted · ₹${Number(data.restoredAmount).toLocaleString("en-IN")} restored to imprest`
          : "Expense deleted"
      );
      qc.invalidateQueries({ queryKey: ["expenses-imprest"] });
      qc.invalidateQueries({ queryKey: ["expenses-imprest-txns"] });
      qc.invalidateQueries({ queryKey: ["expenses-patient"] });
      qc.invalidateQueries({ queryKey: ["expenses-central"] });
    },
    onError: (err: unknown) => {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        "Could not delete settlement";
      toast.error(typeof msg === "string" ? msg : "Could not delete settlement");
    },
  });

  const issueImprestMut = useMutation({
    mutationFn: async () => {
      const r = await expensesApi.issueImprest({
        hospitalStaffId: imprestStaffId,
        amount: Number(imprestAmount),
        paymentMode: imprestPaymentMode,
        notes: imprestNotes.trim() || undefined,
        hospitalId: reportParams?.hospitalId,
        ...(isChainAdmin
          ? imprestHospitalSelect === CENTRAL_HOSPITAL_SELECT_CENTRAL
            ? { hospitalUnit: "CENTRAL" }
            : { targetHospitalId: imprestHospitalSelect }
          : {}),
      });
      return unwrapAxios(r);
    },
    onSuccess: () => {
      toast.success("Imprest advance issued");
      qc.invalidateQueries({ queryKey: ["expenses-imprest"] });
      setImprestAmount("");
      setImprestNotes("");
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(typeof msg === "string" ? msg : "Could not issue imprest");
    },
  });

  const { data: payeeVendorNames = [] } = useQuery({
    queryKey: ["expense-patient-payee-vendors", pCategory, reportParams?.hospitalId],
    queryFn: async () => {
      const r = await expensesApi.listPatientPayeeVendors({
        category: pCategory,
        ...(reportParams?.hospitalId ? { hospitalId: reportParams.hospitalId } : {})
      });
      return (unwrapAxios(r) as { names?: string[] } | undefined)?.names ?? [];
    },
    enabled:
      (!isSuperAdmin || !!hospitalId) &&
      PATIENT_PAYOUT_CATEGORIES.has(pCategory) &&
      pCategory !== "OUTSIDE_LAB_PAYOUT"
  });

  const { data: labExternalVendors = [] } = useQuery({
    queryKey: ["lab-external-vendors", reportParams?.hospitalId],
    queryFn: async () => {
      const r = await labApi.listExternalVendors();
      return (unwrapAxios(r) as Array<{ id: string; name: string }>) ?? [];
    },
    enabled: !isSuperAdmin || !!hospitalId
  });

  const [uploadingPatientProofId, setUploadingPatientProofId] = useState<string | null>(null);
  const [uploadingCentralProofId, setUploadingCentralProofId] = useState<string | null>(null);

  const uploadPatientProofMut = useMutation({
    mutationFn: async ({ id, files }: { id: string; files: File[] }) => {
      for (const file of files) {
        await expensesApi.uploadPatientProof(id, file);
      }
    },
    onMutate: ({ id }) => setUploadingPatientProofId(id),
    onSettled: () => setUploadingPatientProofId(null),
    onSuccess: () => {
      toast.success("Invoice / proof uploaded");
      qc.invalidateQueries({ queryKey: ["expenses-patient"] });
      qc.invalidateQueries({ queryKey: ["expenses-pending"] });
      qc.invalidateQueries({ queryKey: ["expenses-unpaid"] });
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(typeof msg === "string" ? msg : "Upload failed");
    }
  });

  const deletePatientProofMut = useMutation({
    mutationFn: (id: string) => expensesApi.deletePatientProof(id),
    onSuccess: () => {
      toast.success("All proofs removed");
      qc.invalidateQueries({ queryKey: ["expenses-patient"] });
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(typeof msg === "string" ? msg : "Remove failed");
    }
  });

  const deletePatientProofByIdMut = useMutation({
    mutationFn: ({ id, proofId }: { id: string; proofId: string }) =>
      expensesApi.deletePatientProofById(id, proofId),
    onSuccess: () => {
      toast.success("Proof removed");
      qc.invalidateQueries({ queryKey: ["expenses-patient"] });
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(typeof msg === "string" ? msg : "Remove failed");
    }
  });

  const deletePatientExpenseMut = useMutation({
    mutationFn: (id: string) => expensesApi.deletePatient(id),
    onSuccess: () => {
      toast.success("Expense deleted");
      qc.invalidateQueries({ queryKey: ["expenses-patient"] });
      qc.invalidateQueries({ queryKey: ["expenses-pending"] });
      qc.invalidateQueries({ queryKey: ["expenses-unpaid"] });
      qc.invalidateQueries({ queryKey: ["expenses-admission-cost-lines"] });
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(typeof msg === "string" ? msg : "Delete failed");
    }
  });

  const uploadCentralProofMut = useMutation({
    mutationFn: async ({ id, files }: { id: string; files: File[] }) => {
      for (const file of files) {
        await expensesApi.uploadCentralProof(id, file);
      }
    },
    onMutate: ({ id }) => setUploadingCentralProofId(id),
    onSettled: () => setUploadingCentralProofId(null),
    onSuccess: () => {
      toast.success("Invoice / proof uploaded");
      qc.invalidateQueries({ queryKey: ["expenses-central"] });
      qc.invalidateQueries({ queryKey: ["expenses-pending"] });
      qc.invalidateQueries({ queryKey: ["expenses-unpaid"] });
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(typeof msg === "string" ? msg : "Upload failed");
    }
  });

  const deleteCentralProofMut = useMutation({
    mutationFn: (id: string) => expensesApi.deleteCentralProof(id),
    onSuccess: () => {
      toast.success("All proofs removed");
      qc.invalidateQueries({ queryKey: ["expenses-central"] });
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(typeof msg === "string" ? msg : "Remove failed");
    }
  });

  const deleteCentralProofByIdMut = useMutation({
    mutationFn: ({ id, proofId }: { id: string; proofId: string }) =>
      expensesApi.deleteCentralProofById(id, proofId),
    onSuccess: () => {
      toast.success("Proof removed");
      qc.invalidateQueries({ queryKey: ["expenses-central"] });
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(typeof msg === "string" ? msg : "Remove failed");
    }
  });

  const deleteCentralExpenseMut = useMutation({
    mutationFn: (id: string) => expensesApi.deleteCentral(id),
    onSuccess: () => {
      toast.success("Expense deleted");
      qc.invalidateQueries({ queryKey: ["expenses-central"] });
      qc.invalidateQueries({ queryKey: ["expenses-pending"] });
      qc.invalidateQueries({ queryKey: ["expenses-unpaid"] });
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(typeof msg === "string" ? msg : "Delete failed");
    }
  });

  const createPatient = useMutation({
    mutationFn: async () => {
      const r = await expensesApi.createPatient({
        admissionId: pAdmission.trim(),
        category: pCategory,
        amount: Number(pAmount),
        tdsDeducted: pTdsDeducted,
        description: pDesc || undefined,
        payeeVendorName:
          (pCategory === B2B_PARTNER_PAYOUT_CATEGORY
            ? pVendor.trim() || pB2bPartner.trim()
            : pVendor.trim()) || undefined,
        ...(pCategory === B2B_PARTNER_PAYOUT_CATEGORY && pB2bPartner.trim()
          ? { subCategory: pB2bPartner.trim() }
          : {}),
        ...(pCategory === "OUTSIDE_LAB_PAYOUT" && pLabExternalVendorId.trim()
          ? { labExternalVendorId: pLabExternalVendorId.trim() }
          : {}),
        ...(pPayeeHospitalStaffId.trim()
          ? { payeeHospitalStaffId: pPayeeHospitalStaffId.trim() }
          : {}),
        initialStatus: pInitial,
        ...(pExpenseDate.trim() ? { expenseDate: pExpenseDate } : {}),
        ...(pServiceMonth.trim() ? { serviceMonth: pServiceMonth } : {}),
        ...(pFundingSource === "IMPREST"
          ? {
              fundingSource: "IMPREST",
              ...(pImprestStaffId.trim()
                ? { imprestHospitalStaffId: pImprestStaffId.trim() }
                : {}),
            }
          : pPaymentMode.trim()
            ? { paymentMode: pPaymentMode.trim() }
            : {}),
      });
      const created = unwrapAxios(r) as { id: string } | undefined;
      if (pProofFile.length > 0 && created?.id) {
        try {
          for (const file of pProofFile.slice(0, MAX_EXPENSE_PROOF_FILES)) {
            await expensesApi.uploadPatientProof(created.id, file);
          }
        } catch {
          toast.error("Expense saved but proof upload failed — use Add file in the list.");
        }
      }
      return r;
    },
    onSuccess: () => {
      toast.success(
        pFundingSource === "IMPREST"
          ? "Patient expense settled from imprest"
          : pPaymentMode.trim()
            ? "Patient expense saved and marked paid"
            : "Patient expense saved"
      );
      qc.invalidateQueries({ queryKey: ["expenses-patient"] });
      qc.invalidateQueries({ queryKey: ["expenses-imprest"] });
      qc.invalidateQueries({ queryKey: ["expenses-admission-cost-lines"] });
      qc.invalidateQueries({ queryKey: ["expenses-pending"] });
      qc.invalidateQueries({ queryKey: ["expenses-unpaid"] });
      setPAdmission("");
      setPCategory("");
      setPAmount("");
      setPPaymentMode("");
      setPTdsDeducted(false);
      setPExpenseDate(todayLocalISODate());
      setPServiceMonth("");
      setPDesc("");
      setPVendor("");
      setPPayeeHospitalStaffId("");
      setPLabExternalVendorId("");
      setPB2bPartner("");
      setPInitial("DRAFT");
      setPProofFile([]);
      setPProofInputKey((k) => k + 1);
    }
  });

  const { data: patientList, isLoading: patientLoading } = useQuery({
    queryKey: [
      "expenses-patient",
      reportParams?.hospitalId,
      filterPatientId,
      patientVendorSearch,
      patientTdsFilter,
      patientCategoryFilter,
      patientNameSearch,
      patientEnteredBySearch,
      patientListFrom,
      patientListTo,
      patientPage,
    ],
    queryFn: async () => {
      const params: Record<string, string> = {
        page: String(patientPage),
        pageSize: String(expensePageSize),
      };
      if (reportParams?.hospitalId) params.hospitalId = reportParams.hospitalId;
      if (filterPatientId) params.patientId = filterPatientId;
      if (patientVendorSearch.trim()) params.vendor = patientVendorSearch.trim();
      if (patientTdsFilter !== "ALL") params.tdsDeducted = patientTdsFilter === "YES" ? "true" : "false";
      if (patientCategoryFilter !== "ALL") params.category = patientCategoryFilter;
      if (patientNameSearch.trim()) params.patientName = patientNameSearch.trim();
      if (patientEnteredBySearch.trim()) params.enteredBy = patientEnteredBySearch.trim();
      if (patientListFrom) params.from = patientListFrom;
      if (patientListTo) params.to = patientListTo;
      const r = await expensesApi.listPatient(params);
      return parseExpenseListPayload(unwrapAxios(r));
    },
    enabled: (!isSuperAdmin || !!hospitalId) && expenseTab === "patient",
  });
  const patientRows = patientList?.rows ?? [];

  const patientExpenseTotals = useMemo(() => {
    const summary = patientList?.summary;
    if (summary) {
      return {
        total: summary.totalAmount,
        paid: summary.paidAmount,
        unpaid: Math.max(0, summary.totalAmount - summary.paidAmount),
        count: summary.count,
      };
    }
    let total = 0;
    let paid = 0;
    let unpaid = 0;
    for (const row of patientRows as any[]) {
      if (row.workflowStatus === "REJECTED") continue;
      const amt = Number(row.amount) || 0;
      total += amt;
      if (row.workflowStatus === "PAID") paid += amt;
      else unpaid += amt;
    }
    return {
      total,
      paid,
      unpaid,
      count: patientRows.filter((r: any) => r.workflowStatus !== "REJECTED").length,
    };
  }, [patientList, patientRows]);

  const [editPatientId, setEditPatientId] = useState<string | null>(null);
  const [editPatientAmount, setEditPatientAmount] = useState("");
  const [editPatientVendor, setEditPatientVendor] = useState("");
  const [editPatientDesc, setEditPatientDesc] = useState("");
  const [editPatientExpenseDate, setEditPatientExpenseDate] = useState<string>("");
  const [editPatientServiceMonth, setEditPatientServiceMonth] = useState<string>("");
  const [editPatientPaymentMode, setEditPatientPaymentMode] = useState<string>("");
  const [editPatientTdsDeducted, setEditPatientTdsDeducted] = useState(false);
  const [editPatientWorkflowStatus, setEditPatientWorkflowStatus] = useState("");
  const [editPatientPaidAt, setEditPatientPaidAt] = useState("");

  const [editCentralOpen, setEditCentralOpen] = useState(false);
  const [editCentralId, setEditCentralId] = useState<string | null>(null);
  const [editCentralCategory, setEditCentralCategory] = useState("");
  const [editCentralAmount, setEditCentralAmount] = useState("");
  const [editCentralVendor, setEditCentralVendor] = useState("");
  const [editCentralPayeeHospitalStaffId, setEditCentralPayeeHospitalStaffId] = useState("");
  const [editCentralPharmaVendorId, setEditCentralPharmaVendorId] = useState("");
  const [editCentralDesc, setEditCentralDesc] = useState("");
  const [editCentralExpenseDate, setEditCentralExpenseDate] = useState<string>("");
  const [editCentralServiceMonth, setEditCentralServiceMonth] = useState<string>("");
  const [editCentralPaymentMode, setEditCentralPaymentMode] = useState<string>("");
  const [editCentralInvoiceNo, setEditCentralInvoiceNo] = useState<string>("");
  const [editCentralTdsDeducted, setEditCentralTdsDeducted] = useState(false);
  const [editCentralPaidAt, setEditCentralPaidAt] = useState("");
  const [editCentralWorkflowStatus, setEditCentralWorkflowStatus] = useState("");
  const [editCentralHospitalUnit, setEditCentralHospitalUnit] = useState("SEC_52");
  /** Chain admin edit: hospital id or CENTRAL */
  const [editCentralHospitalSelect, setEditCentralHospitalSelect] = useState(CENTRAL_HOSPITAL_SELECT_CENTRAL);

  const savePatientEditMut = useMutation({
    mutationFn: async () => {
      if (!editPatientId) throw new Error("No expense");
      if (
        isStrictHospitalAdmin &&
        editPatientWorkflowStatus === "PAID" &&
        !editPatientPaidAt.trim()
      ) {
        throw new Error("Paid date is required for paid expenses");
      }
      const paidAtIso =
        isStrictHospitalAdmin &&
        editPatientWorkflowStatus === "PAID" &&
        editPatientPaidAt.trim()
          ? parseIstDatetimeLocalToIso(editPatientPaidAt)
          : null;
      if (
        isStrictHospitalAdmin &&
        editPatientWorkflowStatus === "PAID" &&
        editPatientPaidAt.trim() &&
        !paidAtIso
      ) {
        throw new Error("Invalid paid date");
      }
      return expensesApi.updatePatient(editPatientId, {
        category: editPatientCategory,
        amount: Number(editPatientAmount),
        tdsDeducted: editPatientTdsDeducted,
        payeeVendorName:
          (editPatientCategory === B2B_PARTNER_PAYOUT_CATEGORY
            ? editPatientVendor.trim() || editPatientB2bPartner.trim()
            : editPatientVendor.trim()) || null,
        description: editPatientDesc || null,
        ...(editPatientCategory === B2B_PARTNER_PAYOUT_CATEGORY
          ? { subCategory: editPatientB2bPartner.trim() || null }
          : { subCategory: null }),
        ...(editPatientExpenseDate ? { expenseDate: editPatientExpenseDate } : {}),
        ...(editPatientServiceMonth ? { serviceMonth: editPatientServiceMonth } : {}),
        // Allow admin to edit payment mode (null = unset)
        ...(editPatientPaymentMode
          ? { paymentMode: editPatientPaymentMode }
          : { paymentMode: null }),
        ...(paidAtIso ? { paidAt: paidAtIso } : {}),
      });
    },
    onSuccess: () => {
      toast.success("Patient expense updated");
      qc.invalidateQueries({ queryKey: ["expenses-patient"] });
      setEditPatientOpen(false);
      setEditPatientId(null);
    },
    onError: (err: unknown) => {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (err instanceof Error ? err.message : null);
      toast.error(typeof msg === "string" ? msg : "Update failed");
    }
  });

  const saveCentralEditMut = useMutation({
    mutationFn: async () => {
      if (!editCentralId) throw new Error("No expense");
      if (
        isStrictHospitalAdmin &&
        editCentralWorkflowStatus === "PAID" &&
        !editCentralPaidAt.trim()
      ) {
        throw new Error("Paid date is required for paid expenses");
      }
      const paidAtIso =
        isStrictHospitalAdmin &&
        editCentralWorkflowStatus === "PAID" &&
        editCentralPaidAt.trim()
          ? parseIstDatetimeLocalToIso(editCentralPaidAt)
          : null;
      if (
        isStrictHospitalAdmin &&
        editCentralWorkflowStatus === "PAID" &&
        editCentralPaidAt.trim() &&
        !paidAtIso
      ) {
        throw new Error("Invalid paid date");
      }
      const hospitalUnit = isChainAdmin
        ? editCentralHospitalSelect === CENTRAL_HOSPITAL_SELECT_CENTRAL
          ? "CENTRAL"
          : inferCentralHospitalUnitFromHospitalName(
              chainHospitalOptions.find((o) => o.value === editCentralHospitalSelect)?.label
            )
        : editCentralHospitalUnit;
      return expensesApi.updateCentral(editCentralId, {
        category: editCentralCategory,
        amount: Number(editCentralAmount),
        tdsDeducted: editCentralTdsDeducted,
        ...(editCentralCategory === "PHARMA_PAYOUT"
          ? { pharmacyVendorId: editCentralPharmaVendorId.trim() || null }
          : isCentralDoctorPayeeCategory(editCentralCategory)
            ? {
                payeeHospitalStaffId: editCentralPayeeHospitalStaffId.trim() || null,
                vendorName: editCentralVendor.trim() || null
              }
            : { vendorName: editCentralVendor || null }),
        description: editCentralDesc || null,
        ...(editCentralInvoiceNo ? { invoiceNo: editCentralInvoiceNo } : { invoiceNo: null }),
        ...(editCentralExpenseDate ? { expenseDate: editCentralExpenseDate } : {}),
        ...(editCentralPaymentMode ? { paymentMode: editCentralPaymentMode } : { paymentMode: null }),
        hospitalUnit,
        ...(isChainAdmin && editCentralHospitalSelect !== CENTRAL_HOSPITAL_SELECT_CENTRAL
          ? { targetHospitalId: editCentralHospitalSelect }
          : isChainAdmin
            ? { targetHospitalId: null }
            : {}),
        ...(paidAtIso ? { paidAt: paidAtIso } : {}),
      });
    },
    onSuccess: () => {
      toast.success("Central expense updated");
      qc.invalidateQueries({ queryKey: ["expenses-central"] });
      setEditCentralOpen(false);
      setEditCentralId(null);
    },
    onError: (err: unknown) => {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (err instanceof Error ? err.message : null);
      toast.error(typeof msg === "string" ? msg : "Update failed");
    }
  });

  const openEditPatient = (row: any) => {
    setEditPatientId(row.id);
    setEditPatientCategory(row.category);
    setEditPatientAmount(String(row.amount));
    setEditPatientVendor(row.payeeVendorName ?? "");
    setEditPatientDesc(row.description ?? "");
    setEditPatientB2bPartner(row.subCategory ?? "");
    setEditPatientExpenseDate(expenseIsoToIstYmd(row.expenseDate, todayLocalISODate));
    setEditPatientServiceMonth(row.serviceMonth ? expenseIsoToIstYm(row.serviceMonth) : "");
    setEditPatientPaymentMode(row.paymentMode ?? "");
    setEditPatientTdsDeducted(!!row.tdsDeducted);
    setEditPatientWorkflowStatus(row.workflowStatus ?? "");
    setEditPatientPaidAt(
      row.workflowStatus === "PAID" && row.paidAt
        ? formatIstDatetimeLocal(row.paidAt)
        : ""
    );
    setEditPatientOpen(true);
  };

  const openEditCentral = (row: any) => {
    setEditCentralId(row.id);
    setEditCentralCategory(row.category);
    setEditCentralAmount(String(row.amount));
    setEditCentralVendor(row.vendorName ?? "");
    setEditCentralPharmaVendorId(row.pharmacyVendorId ?? "");
    setEditCentralPayeeHospitalStaffId(row.payeeHospitalStaffId ?? "");
    setEditCentralDesc(row.description ?? "");
    setEditCentralExpenseDate(expenseIsoToIstYmd(row.expenseDate, todayLocalISODate));
    setEditCentralServiceMonth(row.serviceMonth ? expenseIsoToIstYm(row.serviceMonth) : "");
    setEditCentralPaymentMode(row.paymentMode ?? "");
    setEditCentralInvoiceNo(row.invoiceNo ?? "");
    setEditCentralTdsDeducted(!!row.tdsDeducted);
    setEditCentralWorkflowStatus(row.workflowStatus ?? "");
    setEditCentralPaidAt(
      row.workflowStatus === "PAID" && row.paidAt
        ? formatIstDatetimeLocal(row.paidAt)
        : ""
    );
    setEditCentralHospitalUnit(
      isChainAdmin ? (row.hospitalUnit ?? "SEC_52") : lockedCentralHospitalUnit
    );
    if (isChainAdmin) {
      if (row.hospitalUnit === "CENTRAL") {
        setEditCentralHospitalSelect(CENTRAL_HOSPITAL_SELECT_CENTRAL);
      } else if (row.hospitalId && chainHospitalOptions.some((o) => o.value === row.hospitalId)) {
        setEditCentralHospitalSelect(row.hospitalId);
      } else {
        const byUnit = chainHospitalOptions.find(
          (o) =>
            o.value !== CENTRAL_HOSPITAL_SELECT_CENTRAL &&
            inferCentralHospitalUnitFromHospitalName(o.label) === (row.hospitalUnit ?? "SEC_52")
        );
        setEditCentralHospitalSelect(byUnit?.value ?? defaultChainHospitalSelect);
      }
    }
    setEditCentralOpen(true);
  };

  const [cCategory, setCCategory] = useState("");
  const [cAmount, setCAmount] = useState("");
  const [cPaymentMode, setCPaymentMode] = useState("");

  const cDerivedHospitalUnit =
    cHospitalSelect === CENTRAL_HOSPITAL_SELECT_CENTRAL
      ? "CENTRAL"
      : inferCentralHospitalUnitFromHospitalName(
          chainHospitalOptions.find((o) => o.value === cHospitalSelect)?.label
        );
  const cDerivedEmployeeScope = expenseEmployeeScopeFromHospitalSelect(
    isChainAdmin ? cHospitalSelect : lockedCentralHospitalUnit === "CENTRAL" ? "CENTRAL" : "UNIT"
  );
  // Non-chain always UNIT for their hospital site
  const salaryEmployeeScope: "CENTRAL" | "UNIT" = isChainAdmin
    ? cDerivedEmployeeScope
    : "UNIT";

  const salaryTargetHospitalId =
    isChainAdmin && cHospitalSelect !== CENTRAL_HOSPITAL_SELECT_CENTRAL
      ? cHospitalSelect
      : undefined;

  const [cTdsDeducted, setCTdsDeducted] = useState(false);
  const [cExpenseDate, setCExpenseDate] = useState(todayLocalISODate);
  const [cServiceMonth, setCServiceMonth] = useState("");
  const [cDesc, setCDesc] = useState("");
  const [cVendor, setCVendor] = useState("");
  const [cPayeeHospitalStaffId, setCPayeeHospitalStaffId] = useState("");
  const [cPharmaVendorId, setCPharmaVendorId] = useState("");

  useEffect(() => {
    setCPharmaVendorId("");
  }, [centralExpenseTargetHospitalId]);

  const [cExpenseEmployeeId, setCExpenseEmployeeId] = useState("");
  const [cInvoice, setCInvoice] = useState("");
  const [cInitial, setCInitial] = useState<"DRAFT" | "PENDING_APPROVAL">("DRAFT");
  const [cProofFile, setCProofFile] = useState<File[]>([]);
  const [cProofInputKey, setCProofInputKey] = useState(0);
  const [cOnlineChannel, setCOnlineChannel] = useState("");
  const [cOfflineSubcategory, setCOfflineSubcategory] = useState("");
  const [cNewOfflineSubcategory, setCNewOfflineSubcategory] = useState("");
  const [addOfflineSubOpen, setAddOfflineSubOpen] = useState(false);

  const [addEmpOpen, setAddEmpOpen] = useState(false);
  const [addEmpName, setAddEmpName] = useState("");
  const [centralFormResetKey, setCentralFormResetKey] = useState(0);

  const resetCentralExpenseForm = useCallback(() => {
    setCCategory("");
    setCHospitalSelect(isChainAdmin ? defaultChainHospitalSelect : lockedCentralHospitalUnit);
    setCAmount("");
    setCPaymentMode("");
    setCFundingSource(imprestData?.defaultFundingSource === "IMPREST" ? "IMPREST" : "REGULAR");
    setCTdsDeducted(false);
    setCExpenseDate(todayLocalISODate());
    setCServiceMonth("");
    setCDesc("");
    setCOnlineChannel("");
    setCOfflineSubcategory("");
    setCVendor("");
    setCPayeeHospitalStaffId("");
    setCPharmaVendorId("");
    setCExpenseEmployeeId("");
    setCInvoice("");
    setCInitial("DRAFT");
    setCProofFile([]);
    setCProofInputKey((k) => k + 1);
    setCImprestStaffId("");
    setCentralFormResetKey((k) => k + 1);
  }, [
    isChainAdmin,
    defaultChainHospitalSelect,
    lockedCentralHospitalUnit,
    imprestData?.defaultFundingSource,
  ]);

  const salaryCategory =
    cCategory === "SALARY" || cCategory === "SALARY_ADVANCE" || cCategory === "INCENTIVES_BONUS";

  const { data: centralEmployees = [], isFetching: centralEmployeesLoading } = useQuery({
    queryKey: [
      "expense-central-employees",
      reportParams?.hospitalId,
      salaryTargetHospitalId,
    ],
    queryFn: async () => {
      const r = await expensesApi.listCentralEmployees({
        ...(reportParams?.hospitalId ? { hospitalId: reportParams.hospitalId } : {}),
        ...(salaryTargetHospitalId ? { hospitalId: salaryTargetHospitalId } : {}),
      });
      return (unwrapAxios(r) as { employees?: ExpenseEmployeeRow[] } | undefined)?.employees ?? [];
    },
    enabled: (!isSuperAdmin || !!hospitalId) && salaryCategory,
    staleTime: 30_000
  });

  const addEmployeeMut = useMutation({
    mutationFn: async () => {
      const name = addEmpName.trim();
      if (!name) throw new Error("Enter employee name");
      const r = await expensesApi.createCentralEmployee({
        name,
        scope: salaryEmployeeScope,
        ...(salaryTargetHospitalId
          ? { hospitalId: salaryTargetHospitalId }
          : reportParams?.hospitalId
            ? { hospitalId: reportParams.hospitalId }
            : {}),
        ...(isChainAdmin && cHospitalSelect === CENTRAL_HOSPITAL_SELECT_CENTRAL
          ? { hospitalUnit: "CENTRAL" }
          : {}),
      });
      return unwrapAxios(r) as any;
    },
    onSuccess: (row: any) => {
      toast.success("Employee saved");
      qc.invalidateQueries({ queryKey: ["expense-central-employees"] });
      setCExpenseEmployeeId(String(row?.id || ""));
      setCVendor(String(row?.name || addEmpName.trim()));
      setAddEmpOpen(false);
      setAddEmpName("");
    },
    onError: (e: any) => toast.error(e?.message || e?.response?.data?.message || "Failed to save employee")
  });

  const { data: centralPayeeVendorNames = [] } = useQuery({
    queryKey: ["expense-central-payee-vendors", cCategory, reportParams?.hospitalId],
    queryFn: async () => {
      const r = await expensesApi.listCentralPayeeVendors({
        category: cCategory,
        ...(reportParams?.hospitalId ? { hospitalId: reportParams.hospitalId } : {})
      });
      return (unwrapAxios(r) as { names?: string[] } | undefined)?.names ?? [];
    },
    enabled: (!isSuperAdmin || !!hospitalId) && isCentralDoctorPayeeCategory(cCategory)
  });

  const { data: editCentralPayeeVendorNames = [] } = useQuery({
    queryKey: ["expense-central-payee-vendors-edit", editCentralCategory, reportParams?.hospitalId],
    queryFn: async () => {
      const r = await expensesApi.listCentralPayeeVendors({
        category: editCentralCategory,
        ...(reportParams?.hospitalId ? { hospitalId: reportParams.hospitalId } : {})
      });
      return (unwrapAxios(r) as { names?: string[] } | undefined)?.names ?? [];
    },
    enabled:
      (!isSuperAdmin || !!hospitalId) && editCentralOpen && isCentralDoctorPayeeCategory(editCentralCategory)
  });

  const { data: offlineMarketingSubs = [], isFetching: offlineMarketingSubsLoading } = useQuery({
    queryKey: ["expense-offline-marketing-subs", reportParams?.hospitalId],
    queryFn: async () => {
      const r = await expensesApi.listOfflineMarketingSubcategories(
        reportParams?.hospitalId ? { hospitalId: reportParams.hospitalId } : undefined
      );
      return (unwrapAxios(r) as { names?: string[] } | undefined)?.names ?? [];
    },
    enabled: (!isSuperAdmin || !!hospitalId) && cCategory === "OFFLINE_MARKETING",
    staleTime: 15_000
  });

  const { data: centralCategoryPreview = [], isFetching: centralCategoryPreviewLoading } = useQuery({
    queryKey: ["expenses-central-category-preview", reportParams?.hospitalId, cCategory],
    queryFn: async () => {
      const params: Record<string, string> = { category: cCategory };
      if (reportParams?.hospitalId) params.hospitalId = reportParams.hospitalId;
      params.allRows = "true";
      const r = await expensesApi.listCentral(params);
      return parseExpenseListPayload(unwrapAxios(r)).rows;
    },
    enabled: (!isSuperAdmin || !!hospitalId) && !!cCategory.trim() && expenseTab === "central",
    staleTime: 10_000
  });

  const createOfflineMarketingSubMut = useMutation({
    mutationFn: async () => {
      const name = cNewOfflineSubcategory.trim();
      if (!name) throw new Error("Enter subcategory name");
      return expensesApi.createOfflineMarketingSubcategory({
        name,
        ...(reportParams?.hospitalId ? { hospitalId: reportParams.hospitalId } : {})
      });
    },
    onSuccess: async () => {
      toast.success("Subcategory saved");
      qc.invalidateQueries({ queryKey: ["expense-offline-marketing-subs"] });
      setAddOfflineSubOpen(false);
      setCNewOfflineSubcategory("");
    },
    onError: (e: any) =>
      toast.error(e?.response?.data?.message || e?.message || "Failed to save subcategory")
  });

  const createCentral = useMutation({
    mutationFn: async () => {
      const hospitalUnit = isChainAdmin
        ? cDerivedHospitalUnit
        : lockedCentralHospitalUnit;
      const base: Record<string, unknown> = {
        category: cCategory,
        hospitalUnit,
        amount: Number(cAmount),
        tdsDeducted: cTdsDeducted,
        description: cDesc || undefined,
        invoiceNo: cInvoice || undefined,
        initialStatus: cInitial,
        ...(cExpenseDate.trim() ? { expenseDate: cExpenseDate } : {}),
        ...(cServiceMonth.trim() ? { serviceMonth: cServiceMonth } : {}),
        ...(cFundingSource === "IMPREST"
          ? {
              fundingSource: "IMPREST",
              ...(cImprestStaffId.trim()
                ? { imprestHospitalStaffId: cImprestStaffId.trim() }
                : {}),
            }
          : cPaymentMode.trim()
            ? { paymentMode: cPaymentMode.trim() }
            : {}),
        ...(isChainAdmin && cHospitalSelect !== CENTRAL_HOSPITAL_SELECT_CENTRAL
          ? { targetHospitalId: cHospitalSelect }
          : {}),
      };
      if (cCategory === "ONLINE_MARKETING") {
        base.subCategory = cOnlineChannel.trim();
      } else if (cCategory === "OFFLINE_MARKETING") {
        base.subCategory = cOfflineSubcategory.trim();
      }
      if (cCategory === "PHARMA_PAYOUT") {
        base.pharmacyVendorId = cPharmaVendorId;
      } else if (salaryCategory) {
        base.expenseEmployeeId = cExpenseEmployeeId || undefined;
        base.expenseEmployeeScope = salaryEmployeeScope;
      } else if (isCentralDoctorPayeeCategory(cCategory)) {
        base.payeeHospitalStaffId = cPayeeHospitalStaffId || undefined;
        // Allow free-text doctor name when payee isn't registered as staff.
        base.vendorName = cVendor || undefined;
      } else if (cCategory === "ONLINE_MARKETING" || cCategory === "OFFLINE_MARKETING") {
        base.vendorName = cVendor || undefined;
      } else {
        base.vendorName = cVendor || undefined;
      }
      const r = await expensesApi.createCentral(base);
      const created = unwrapAxios(r) as { id: string } | undefined;
      if (cProofFile.length > 0 && created?.id) {
        try {
          for (const file of cProofFile.slice(0, MAX_EXPENSE_PROOF_FILES)) {
            await expensesApi.uploadCentralProof(created.id, file);
          }
        } catch {
          toast.error("Expense saved but proof upload failed — use Add file in the list.");
        }
      }
      return r;
    },
    onSuccess: () => {
      const savedViaImprest = cFundingSource === "IMPREST";
      const savedAndPaid = cPaymentMode.trim().length > 0;
      resetCentralExpenseForm();
      toast.success(
        savedViaImprest
          ? "Central expense settled from imprest"
          : savedAndPaid
            ? "Central expense saved and marked paid"
            : "Central expense saved"
      );
      qc.invalidateQueries({ queryKey: ["expenses-central"] });
      qc.invalidateQueries({ queryKey: ["expenses-imprest"] });
      qc.invalidateQueries({ queryKey: ["expenses-pending"] });
      qc.invalidateQueries({ queryKey: ["expenses-unpaid"] });
      qc.invalidateQueries({ queryKey: ["expenses-central-category-preview"] });
    },
    onError: (e: unknown) => {
      const err = e as { response?: { data?: { message?: string } }; message?: string };
      toast.error(
        err?.response?.data?.message || err?.message || "Failed to save central expense"
      );
    },
  });

  const { data: centralList, isLoading: centralLoading } = useQuery({
    queryKey: [
      "expenses-central",
      reportParams?.hospitalId,
      centralVendorSearch,
      centralEnteredBySearch,
      centralCategoryFilter,
      centralHospitalUnitFilter,
      centralTdsFilter,
      centralListFrom,
      centralListTo,
      centralPage,
    ],
    queryFn: async () => {
      const params: Record<string, string> = {
        page: String(centralPage),
        pageSize: String(expensePageSize),
      };
      if (reportParams?.hospitalId) params.hospitalId = reportParams.hospitalId;
      if (centralVendorSearch.trim()) params.vendor = centralVendorSearch.trim();
      if (centralEnteredBySearch.trim()) params.enteredBy = centralEnteredBySearch.trim();
      if (centralCategoryFilter !== "ALL") params.category = centralCategoryFilter;
      if (centralHospitalUnitFilter !== "ALL") params.hospitalUnit = centralHospitalUnitFilter;
      if (centralTdsFilter !== "ALL") params.tdsDeducted = centralTdsFilter === "YES" ? "true" : "false";
      if (centralListFrom) params.from = centralListFrom;
      if (centralListTo) params.to = centralListTo;
      const r = await expensesApi.listCentral(params);
      return parseExpenseListPayload(unwrapAxios(r));
    },
    enabled: (!isSuperAdmin || !!hospitalId) && expenseTab === "central",
  });
  const centralRows = centralList?.rows ?? [];

  const centralListFilterActive =
    !!centralVendorSearch.trim() ||
    centralCategoryFilter !== "ALL" ||
    centralHospitalUnitFilter !== "ALL";

  const centralFilterTotals = useMemo(() => {
    if (!centralListFilterActive) return null;
    const rows = Array.isArray(centralRows) ? (centralRows as any[]) : [];
    let total = 0;
    let paid = 0;
    let unpaid = 0;
    for (const row of rows) {
      if (row.workflowStatus === "REJECTED") continue;
      const amt = Number(row.amount) || 0;
      total += amt;
      if (row.workflowStatus === "PAID") paid += amt;
      else unpaid += amt;
    }
    return { total, paid, unpaid, count: rows.filter((r) => r.workflowStatus !== "REJECTED").length };
  }, [centralListFilterActive, centralRows]);

  const { data: pending, isLoading: pendingLoading } = useQuery({
    queryKey: ["expenses-pending", reportParams?.hospitalId],
    queryFn: async () => {
      const r = await expensesApi.getPending(
        reportParams?.hospitalId ? { hospitalId: reportParams.hospitalId } : undefined
      );
      return unwrapAxios(r) as { patient: unknown[]; central: unknown[] } | undefined;
    },
    enabled: (!isSuperAdmin || !!hospitalId) && expenseTab === "pending",
  });

  const { data: unpaid, isLoading: unpaidLoading } = useQuery({
    queryKey: ["expenses-unpaid", reportParams?.hospitalId],
    queryFn: async () => {
      const r = await expensesApi.getUnpaid(
        reportParams?.hospitalId ? { hospitalId: reportParams.hospitalId } : undefined
      );
      return unwrapAxios(r) as { patient: unknown[]; central: unknown[] } | undefined;
    },
    enabled: (!isSuperAdmin || !!hospitalId) && expenseTab === "unpaid",
  });

  const unpaidPatientRowsFiltered = useMemo(() => {
    const rows = (unpaid?.patient ?? []) as any[];
    const patientQ = unpaidPatientSearch.trim().toLowerCase();
    const vendorQ = unpaidVendorSearch.trim().toLowerCase();
    const ipdQ = unpaidIpdSearch.trim().toLowerCase();
    return rows.filter((row) => {
      if (patientQ) {
        const name = String(row.patient?.name ?? "").toLowerCase();
        if (!name.includes(patientQ)) return false;
      }
      if (ipdQ) {
        const ipd = String(row.admission?.queue?.ipdNumber ?? "").toLowerCase();
        if (!ipd.includes(ipdQ)) return false;
      }
      if (unpaidCategoryFilter !== "ALL" && row.category !== unpaidCategoryFilter) return false;
      if (vendorQ) {
        const vendor = String(row.payeeVendorName || row.payeeDoctor?.name || "").toLowerCase();
        if (!vendor.includes(vendorQ)) return false;
      }
      return true;
    });
  }, [
    unpaid?.patient,
    unpaidPatientSearch,
    unpaidVendorSearch,
    unpaidCategoryFilter,
    unpaidIpdSearch,
  ]);

  const unpaidPatientFilterActive =
    unpaidPatientSearch.trim() !== "" ||
    unpaidVendorSearch.trim() !== "" ||
    unpaidCategoryFilter !== "ALL" ||
    unpaidIpdSearch.trim() !== "";

  const approvePatient = useMutation({
    mutationFn: (id: string) => expensesApi.approvePatient(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["expenses-pending"] });
      qc.invalidateQueries({ queryKey: ["expenses-unpaid"] });
      qc.invalidateQueries({ queryKey: ["expenses-patient"] });
    }
  });

  const approveCentral = useMutation({
    mutationFn: (id: string) => expensesApi.approveCentral(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["expenses-pending"] });
      qc.invalidateQueries({ queryKey: ["expenses-unpaid"] });
      qc.invalidateQueries({ queryKey: ["expenses-central"] });
    }
  });

  const [markPaidOpen, setMarkPaidOpen] = useState(false);
  const [markPaidTarget, setMarkPaidTarget] = useState<{
    kind: "patient" | "central";
    id: string;
    amount: number;
  } | null>(null);
  const [markPaidMode, setMarkPaidMode] = useState("CASH");
  const [markPaidAmount, setMarkPaidAmount] = useState("");

  const [tdsDialogOpen, setTdsDialogOpen] = useState(false);
  const [tdsChallanDraft, setTdsChallanDraft] = useState("");
  const [tdsExporting, setTdsExporting] = useState(false);
  const [tdsTarget, setTdsTarget] = useState<{ kind: "patient" | "central"; id: string } | null>(null);

  const paymentModeOptions = useMemo(
    () =>
      meta?.paymentModes?.length
        ? meta.paymentModes
        : ["CASH", "CARD", "UPI", "BANK_TRANSFER", "INSURANCE", "CHEQUE"],
    [meta?.paymentModes]
  );

  const markPaidMut = useMutation({
    mutationFn: async () => {
      if (!markPaidTarget) throw new Error("No expense selected");
      const raw = markPaidAmount.trim();
      const paidAmount = raw === "" ? undefined : Number(raw);
      if (paidAmount !== undefined && (!Number.isFinite(paidAmount) || paidAmount < 0)) {
        throw new Error("Enter a valid paid amount");
      }
      const body = {
        paymentMode: markPaidMode,
        ...(paidAmount !== undefined ? { paidAmount } : {})
      };
      if (markPaidTarget.kind === "patient") {
        return expensesApi.markPatientPaid(markPaidTarget.id, body);
      }
      return expensesApi.markCentralPaid(markPaidTarget.id, body);
    },
    onSuccess: () => {
      toast.success("Marked paid");
      qc.invalidateQueries({ queryKey: ["expenses-patient"] });
      qc.invalidateQueries({ queryKey: ["expenses-central"] });
      qc.invalidateQueries({ queryKey: ["expenses-pending"] });
      qc.invalidateQueries({ queryKey: ["expenses-unpaid"] });
      setMarkPaidOpen(false);
      setMarkPaidTarget(null);
    },
    onError: (err: unknown) => {
      const msg =
        err instanceof Error
          ? err.message
          : (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(typeof msg === "string" ? msg : "Could not mark paid");
    }
  });

  const unmarkPaidMut = useMutation({
    mutationFn: async (target: { kind: "patient" | "central"; id: string }) => {
      if (target.kind === "patient") return expensesApi.unmarkPatientPaid(target.id);
      return expensesApi.unmarkCentralPaid(target.id);
    },
    onSuccess: () => {
      toast.success("Marked unpaid — status set to Approved");
      qc.invalidateQueries({ queryKey: ["expenses-patient"] });
      qc.invalidateQueries({ queryKey: ["expenses-central"] });
      qc.invalidateQueries({ queryKey: ["expenses-pending"] });
      qc.invalidateQueries({ queryKey: ["expenses-unpaid"] });
    },
    onError: (err: unknown) => {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (err instanceof Error ? err.message : null);
      toast.error(typeof msg === "string" ? msg : "Could not mark unpaid");
    },
  });

  const confirmUnmarkPaid = (kind: "patient" | "central", id: string) => {
    if (
      !window.confirm(
        "Mark this expense as unpaid? It will return to Approved so you can mark it paid again correctly."
      )
    ) {
      return;
    }
    unmarkPaidMut.mutate({ kind, id });
  };

  const openMarkPaid = (kind: "patient" | "central", id: string, amount: number) => {
    setMarkPaidTarget({ kind, id, amount });
    setMarkPaidAmount(String(amount));
    const modes = paymentModeOptions;
    setMarkPaidMode(modes.includes("CASH") ? "CASH" : modes[0] ?? "CASH");
    setMarkPaidOpen(true);
  };

  const { data: tdsRows, isLoading: tdsLoading } = useQuery({
    queryKey: ["expenses-tds-status", reportParams?.hospitalId],
    queryFn: async () => {
      const r = await expensesApi.listTdsStatus(
        reportParams?.hospitalId ? { hospitalId: reportParams.hospitalId } : undefined
      );
      return (unwrapAxios(r) as any[]) ?? [];
    },
    enabled: (isHospitalAdmin || isSuperAdmin) && (!isSuperAdmin || !!hospitalId) && expenseTab === "tds",
    staleTime: 30_000
  });

  const markTdsPaidMut = useMutation({
    mutationFn: async (payload: { kind: "patient" | "central"; id: string; challanNo?: string | null }) => {
      if (payload.kind === "patient") {
        return expensesApi.markPatientTdsPaid(payload.id, { tdsChallanNo: payload.challanNo ?? null });
      }
      return expensesApi.markCentralTdsPaid(payload.id, { tdsChallanNo: payload.challanNo ?? null });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["expenses-tds-status"] });
      toast.success("TDS marked paid");
      setTdsDialogOpen(false);
      setTdsTarget(null);
      setTdsChallanDraft("");
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || "Failed to mark TDS paid")
  });

  const openTdsPaid = (kind: "patient" | "central", id: string) => {
    setTdsTarget({ kind, id });
    setTdsChallanDraft("");
    setTdsDialogOpen(true);
  };

  const runTdsExport = async () => {
    setTdsExporting(true);
    try {
      const resp = await expensesApi.exportTdsStatus(
        reportParams?.hospitalId ? { hospitalId: reportParams.hospitalId } : undefined
      );
      const dateSuffix = new Date().toISOString().slice(0, 10);
      downloadBlob(
        new Blob([resp.data], { type: "text/csv;charset=utf-8" }),
        `TDS-Status-complete-${dateSuffix}.csv`
      );
      toast.success("TDS report download started");
    } catch (e: unknown) {
      const msg =
        e && typeof e === "object" && "response" in e
          ? (e as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      toast.error(msg || "Failed to download TDS report");
    } finally {
      setTdsExporting(false);
    }
  };

  const { data: profReport } = useQuery({
    queryKey: ["expenses-report-prof", reportParams?.hospitalId, reportCostBasis],
    queryFn: async () => {
      const r = await expensesApi.reportPatientProfitability({
        ...(reportParams?.hospitalId ? { hospitalId: reportParams.hospitalId } : {}),
        costBasis: reportCostBasis
      });
      return unwrapAxios(r) as
        | {
            costBasis: string;
            costBasisDescription: string;
            rows: {
              admissionId: string;
              ipdNumber?: string;
              patient?: { name?: string };
              revenue: number;
              patientExpenses: number;
              grossMargin: number;
            }[];
          }
        | undefined;
    },
    enabled: (!isSuperAdmin || !!hospitalId) && expenseTab === "reports",
  });

  const { data: centralMonthly } = useQuery({
    queryKey: ["expenses-report-cm", reportParams?.hospitalId, reportCostBasis],
    queryFn: async () => {
      const r = await expensesApi.reportCentralMonthly({
        year: new Date().getFullYear(),
        ...(reportParams?.hospitalId ? { hospitalId: reportParams.hospitalId } : {}),
        costBasis: reportCostBasis
      });
      return unwrapAxios(r) as
        | {
            costBasis?: string;
            grouped: {
              category: string;
              _sum: { amount: number | null };
              _count: { id: number };
            }[];
          }
        | undefined;
    },
    enabled: (!isSuperAdmin || !!hospitalId) && expenseTab === "reports",
  });

  if (isSuperAdmin && !hospitalId) {
    return (
      <div className="space-y-4 p-4">
        <h1 className="text-2xl font-semibold">Expenses</h1>
        <Card>
          <CardHeader>
            <CardTitle>Select hospital</CardTitle>
          </CardHeader>
          <CardContent className="flex gap-2">
            <Select value={hospitalId || "none"} onValueChange={(v) => setHospitalId(v === "none" ? "" : v)}>
              <SelectTrigger className="w-[280px]">
                <SelectValue placeholder="Hospital" />
              </SelectTrigger>
              <SelectContent>
                {(hospitals as { id: string; name: string }[] | undefined)?.map((h) => (
                  <SelectItem key={h.id} value={h.id}>
                    {h.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="flex items-center gap-2 text-2xl font-semibold">
          <IndianRupee className="h-7 w-7" />
          Expenses (costs)
        </h1>
        {isSuperAdmin && (
          <Select value={hospitalId} onValueChange={setHospitalId}>
            <SelectTrigger className="w-[220px]">
              <SelectValue placeholder="Hospital" />
            </SelectTrigger>
            <SelectContent>
              {(hospitals as { id: string; name: string }[] | undefined)?.map((h) => (
                <SelectItem key={h.id} value={h.id}>
                  {h.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>
      <p className="text-muted-foreground text-sm">
        Patient expenses are stored against a <strong>specific IPD admission id</strong> (one stay). If the same patient is admitted again later, use a new admission — costs do not merge across stays. Central expenses are hospital-level. Billing (revenue) stays in IPD billing — never mixed.
      </p>

      {metaLoading && (
        <div className="text-muted-foreground flex items-center gap-2 text-sm">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      )}

      <Tabs value={expenseTab} onValueChange={setExpenseTab} className="w-full">
        <TabsList className="flex flex-wrap">
          <TabsTrigger value="patient">Patient (IPD)</TabsTrigger>
          <TabsTrigger value="central">Central</TabsTrigger>
          <TabsTrigger value="imprest">Imprest</TabsTrigger>
          <TabsTrigger value="salary">Salary</TabsTrigger>
          <TabsTrigger value="pending">Pending approval</TabsTrigger>
          <TabsTrigger value="unpaid">Unpaid</TabsTrigger>
          <TabsTrigger value="tds">TDS Status</TabsTrigger>
          <TabsTrigger value="reports">Reports</TabsTrigger>
        </TabsList>

        <TabsContent value="patient" className="space-y-4">
          {readOnlySuperAdmin && (
            <p className="text-amber-700 text-sm">
              Super admin: view and reports only. Enter expenses as hospital admin or receptionist.
            </p>
          )}
          <Card className={readOnlySuperAdmin ? "opacity-60 pointer-events-none" : ""}>
            <CardHeader>
              <CardTitle>New patient expense</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-3 md:grid-cols-3">
              <div className="md:col-span-3 space-y-2">
                <Label>IPD admission</Label>
                <div className="flex flex-wrap items-start gap-2">
                  <Popover open={admissionPickerOpen} onOpenChange={setAdmissionPickerOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        role="combobox"
                        aria-expanded={admissionPickerOpen}
                        className="min-h-10 min-w-[280px] flex-1 justify-between text-left font-normal"
                        disabled={readOnlySuperAdmin}
                      >
                        <span className="truncate">
                          {selectedAdmissionLabel || "Search by patient name, UHID, or IPD number…"}
                        </span>
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[min(100vw-2rem,28rem)] p-0" align="start">
                      <div className="border-b p-2">
                        <Input
                          placeholder="Type at least 2 characters to filter, or leave empty for recent"
                          value={admissionSearchInput}
                          onChange={(e) => setAdmissionSearchInput(e.target.value)}
                          autoFocus
                        />
                      </div>
                      <div className="max-h-64 overflow-y-auto p-1">
                        {admissionsFetching && (
                          <div className="text-muted-foreground flex items-center gap-2 p-3 text-sm">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Loading admissions…
                          </div>
                        )}
                        {!admissionsFetching && admissionOptions.length === 0 && (
                          <p className="text-muted-foreground p-3 text-sm">No admissions found.</p>
                        )}
                        {!admissionsFetching &&
                          admissionOptions.map((a) => (
                            <button
                              key={a.id}
                              type="button"
                              className="hover:bg-muted flex w-full flex-col items-start rounded-md px-3 py-2 text-left text-sm"
                              onClick={() => {
                                setPAdmission(a.id);
                                const admLabel = formatExpenseDateIst(a.admissionDate);
                                const discLabel = a.dischargeDate
                                  ? formatExpenseDateIst(a.dischargeDate)
                                  : "Not discharged";
                                setSelectedAdmissionLabel(
                                  `${a.patientName} · ${a.ipdNumber}${a.patientUhid ? ` · ${a.patientUhid}` : ""} · Adm ${admLabel} · Disc ${discLabel}`
                                );
                                setSelectedAdmissionDates({
                                  admissionDate: a.admissionDate ?? null,
                                  dischargeDate: a.dischargeDate ?? null,
                                });
                                // Default service month to discharge month (admission month if not discharged yet).
                                const serviceYm =
                                  expenseIsoToIstYm(a.dischargeDate) ||
                                  expenseIsoToIstYm(a.admissionDate);
                                if (serviceYm) setPServiceMonth(serviceYm);
                                setSelectedAdmissionDoctor(
                                  a.assignedDoctorName
                                    ? {
                                        name: a.assignedDoctorName,
                                        specialisation: a.assignedDoctorSpecialisation,
                                      }
                                    : null
                                );
                                setSelectedAdmissionSurgeries(
                                  Array.isArray(a.packageSurgeries) && a.packageSurgeries.length > 0
                                    ? a.packageSurgeries.map((s) => ({
                                        specialisation: s.specialisation,
                                        surgeryName: s.surgeryName,
                                      }))
                                    : a.surgeryName
                                      ? [
                                          {
                                            specialisation: a.surgerySpecialisation,
                                            surgeryName: a.surgeryName,
                                          },
                                        ]
                                      : []
                                );
                                setAdmissionPickerOpen(false);
                              }}
                            >
                              <span className="font-medium">{a.patientName}</span>
                              <span className="text-muted-foreground text-xs">
                                IPD {a.ipdNumber}
                                {a.patientUhid ? ` · UHID ${a.patientUhid}` : ""} · {a.status} · Adm{" "}
                                {formatExpenseDateIst(a.admissionDate)} · Disc{" "}
                                {a.dischargeDate
                                  ? formatExpenseDateIst(a.dischargeDate)
                                  : "—"}
                                {a.assignedDoctorName
                                  ? ` · ${ensureDoctorPrefix(a.assignedDoctorName)}`
                                  : ""}
                                {a.assignedDoctorSpecialisation
                                  ? ` (${a.assignedDoctorSpecialisation})`
                                  : ""}
                              </span>
                            </button>
                          ))}
                      </div>
                    </PopoverContent>
                  </Popover>
                  {pAdmission && !readOnlySuperAdmin && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setPAdmission("");
                        setSelectedAdmissionLabel("");
                        setSelectedAdmissionDates(null);
                        setSelectedAdmissionDoctor(null);
                        setSelectedAdmissionSurgeries([]);
                      }}
                    >
                      Clear
                    </Button>
                  )}
                </div>
                {pAdmission && (
                  <div className="space-y-2">
                    <div className="rounded-md border border-slate-200 bg-slate-50/80 px-3 py-2 text-sm">
                      <div className="grid gap-2 sm:grid-cols-2">
                        <div>
                          <p className="text-xs font-medium uppercase tracking-wide text-slate-600">
                            Admission date
                          </p>
                          <p className="mt-0.5 font-medium text-slate-900">
                            {formatExpenseDateIst(selectedAdmissionDates?.admissionDate)}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs font-medium uppercase tracking-wide text-slate-600">
                            Discharge date
                          </p>
                          <p className="mt-0.5 font-medium text-slate-900">
                            {selectedAdmissionDates?.dischargeDate
                              ? formatExpenseDateIst(selectedAdmissionDates.dischargeDate)
                              : "Not discharged yet"}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="rounded-md border border-blue-100 bg-blue-50/60 px-3 py-2 text-sm">
                      <p className="text-xs font-medium uppercase tracking-wide text-blue-800/80">
                        Admitting doctor
                      </p>
                      <p className="mt-0.5 font-medium text-slate-900">
                        {selectedAdmissionDoctor?.name
                          ? ensureDoctorPrefix(selectedAdmissionDoctor.name)
                          : "Not assigned on admission"}
                      </p>
                      {selectedAdmissionDoctor?.specialisation && (
                        <p className="text-muted-foreground text-xs">
                          {selectedAdmissionDoctor.specialisation}
                        </p>
                      )}
                    </div>
                    <div className="rounded-md border border-violet-100 bg-violet-50/60 px-3 py-2 text-sm">
                      <p className="text-xs font-medium uppercase tracking-wide text-violet-900/80">
                        Surgeries conducted
                      </p>
                      {selectedAdmissionSurgeries.length === 0 ? (
                        <p className="text-muted-foreground mt-0.5 text-xs">
                          No surgeries recorded on this admission.
                        </p>
                      ) : (
                        <ul className="mt-1 space-y-1">
                          {selectedAdmissionSurgeries.map((s, i) => (
                            <li key={`${s.surgeryName}-${i}`} className="text-sm text-slate-900">
                              <span className="font-medium">{s.surgeryName}</span>
                              {s.specialisation ? (
                                <span className="text-muted-foreground text-xs">
                                  {" "}
                                  · {s.specialisation}
                                </span>
                              ) : null}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                    <p className="text-muted-foreground font-mono text-xs">Admission ID: {pAdmission}</p>
                    {(isHospitalAdmin || isSuperAdmin) && (
                      <div className="rounded-md border border-emerald-100 bg-emerald-50/70 px-3 py-2 text-sm">
                        <p className="text-xs font-medium uppercase tracking-wide text-emerald-900/80">
                          Discharge bill amount
                        </p>
                        {admissionBillingLoading ? (
                          <p className="text-muted-foreground mt-1 flex items-center gap-2 text-xs">
                            <Loader2 className="h-3 w-3 animate-spin" />
                            Loading bill…
                          </p>
                        ) : admissionBillingSummary?.netAmount != null ? (
                          <p className="mt-0.5 text-lg font-semibold tabular-nums text-slate-900">
                            ₹{Number(admissionBillingSummary.netAmount).toLocaleString("en-IN", {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2
                            })}
                            {admissionBillingSummary.billNumber ? (
                              <span className="text-muted-foreground ml-2 text-xs font-normal">
                                Bill {admissionBillingSummary.billNumber}
                                {admissionBillingSummary.billStatus
                                  ? ` · ${admissionBillingSummary.billStatus}`
                                  : ""}
                              </span>
                            ) : null}
                          </p>
                        ) : (
                          <p className="text-muted-foreground mt-1 text-xs">
                            No revenue IPD bill found for this admission yet.
                          </p>
                        )}
                        {admissionBillingSummary?.netAmount != null && (
                          <div className="mt-2 space-y-1 text-xs">
                            {admissionBillingSummary.totalDeposits != null && (
                              <p className="text-muted-foreground">
                                Collected (desk + third party):{" "}
                                <span className="font-medium text-slate-900 tabular-nums">
                                  ₹{Number(admissionBillingSummary.totalDeposits).toLocaleString("en-IN", {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })}
                                </span>
                              </p>
                            )}
                            {(admissionBillingSummary.thirdPartyCollected ?? 0) > 0 && (
                              <p className="text-muted-foreground">
                                Third-party settled:{" "}
                                <span className="font-medium text-emerald-800 tabular-nums">
                                  ₹{Number(admissionBillingSummary.thirdPartyCollected).toLocaleString("en-IN", {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })}
                                </span>
                                {(admissionBillingSummary.deskDeposits ?? 0) > 0 && (
                                  <>
                                    {" "}
                                    (desk ₹
                                    {Number(admissionBillingSummary.deskDeposits).toLocaleString("en-IN", {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                    })}
                                    )
                                  </>
                                )}
                              </p>
                            )}
                            {admissionBillingSummary.isFullyCollected ? (
                              <p className="font-medium text-emerald-800">
                                Bill fully collected
                              </p>
                            ) : admissionBillingSummary.excessAmount != null &&
                              admissionBillingSummary.excessAmount > 0 ? (
                              <p className="font-medium text-blue-800">
                                Excess collected: ₹
                                {admissionBillingSummary.excessAmount.toLocaleString("en-IN", {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}
                              </p>
                            ) : admissionBillingSummary.pendingAmount != null &&
                              admissionBillingSummary.pendingAmount > 0 ? (
                              <p className="font-medium text-amber-800">
                                Pending collection: ₹
                                {admissionBillingSummary.pendingAmount.toLocaleString("en-IN", {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}
                              </p>
                            ) : null}
                            {(admissionBillingSummary.pendingThirdPartyCredit ?? 0) > 0 && (
                              <p className="text-amber-700">
                                Pending from third party (internal): ₹
                                {Number(admissionBillingSummary.pendingThirdPartyCredit).toLocaleString("en-IN", {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}
                              </p>
                            )}
                          </div>
                        )}
                        {admissionBillingSummary && (
                            <dl className="mt-2 grid gap-1.5 text-xs sm:grid-cols-2 border-t border-emerald-200/60 pt-2">
                              <div>
                                <dt className="text-emerald-900/70">Partner</dt>
                                <dd className="font-medium text-slate-900">
                                  {admissionBillingSummary.partnerName || "—"}
                                </dd>
                              </div>
                              <div>
                                <dt className="text-emerald-900/70">Referrer</dt>
                                <dd className="font-medium text-slate-900">
                                  {admissionBillingSummary.referrerName || "—"}
                                </dd>
                              </div>
                              <div>
                                <dt className="text-emerald-900/70">Sales person</dt>
                                <dd className="font-medium text-slate-900">
                                  {admissionBillingSummary.salesPersonName || "—"}
                                </dd>
                              </div>
                              <div>
                                <dt className="text-emerald-900/70">Financing partner</dt>
                                <dd className="font-medium text-slate-900">
                                  {admissionBillingSummary.financingPartnerName || "—"}
                                </dd>
                              </div>
                            </dl>
                          )}
                      </div>
                    )}
                  </div>
                )}
                {pAdmission && (
                  <div className="rounded-md border bg-muted/30 p-2">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-xs font-medium">
                        {canSeeAdmissionModuleCosts
                          ? "Costs for this IPD admission (expenses, pharma, lab)"
                          : "Existing expenses for this IPD admission"}
                      </p>
                      {selectedPatientExpensesLoading ? (
                        <span className="text-muted-foreground flex items-center gap-1 text-xs">
                          <Loader2 className="h-3 w-3 animate-spin" /> Loading…
                        </span>
                      ) : (
                        <span className="text-muted-foreground text-xs">
                          {selectedAdmissionCostLines.length} line(s)
                        </span>
                      )}
                    </div>
                    {!selectedPatientExpensesLoading && selectedAdmissionCostLines.length === 0 ? (
                      <p className="text-muted-foreground mt-1 text-xs">
                        {canSeeAdmissionModuleCosts
                          ? "No expenses or module costs found for this IPD admission."
                          : "No expenses found for this IPD admission."}
                      </p>
                    ) : (
                      <div className="mt-2 max-h-56 overflow-auto rounded border bg-white">
                        <table className="w-full text-xs">
                          <thead className="sticky top-0 bg-slate-50 border-b">
                            <tr>
                              <th className="px-2 py-1 text-left">Date</th>
                              <th className="px-2 py-1 text-left">Vendor / item</th>
                              <th className="px-2 py-1 text-left">Category</th>
                              <th className="px-2 py-1 text-left">Payment</th>
                              <th className="px-2 py-1 text-right">Amount</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedAdmissionCostLines.map((r) => (
                              <tr
                                key={r.id}
                                className={`border-b last:border-b-0 ${
                                  r.source !== "PATIENT_EXPENSE" ? "bg-slate-50/80" : ""
                                }`}
                              >
                                <td className="px-2 py-1 whitespace-nowrap">
                                  {formatExpenseDateIst(r.date)}
                                </td>
                                <td className="px-2 py-1 max-w-[180px]" title={r.label}>
                                  {r.labKind === "EXTERNAL" ? (
                                    <div>
                                      <div className="truncate font-medium">{r.vendorName || r.label || "—"}</div>
                                      <div className="text-[10px] text-muted-foreground">
                                        Vendor cost: ₹
                                        {Number(r.vendorCost ?? r.amount ?? 0).toLocaleString("en-IN")}
                                      </div>
                                    </div>
                                  ) : (
                                    <span className="truncate block">{r.label || "—"}</span>
                                  )}
                                </td>
                                <td className="px-2 py-1">
                                  {r.source === "PHARMA" ? (
                                    <button
                                      type="button"
                                      className="text-primary hover:underline text-left"
                                      onClick={() => setModuleCostBreakdown("pharma")}
                                    >
                                      {r.category}
                                    </button>
                                  ) : r.source === "LAB" ? (
                                    <button
                                      type="button"
                                      className="text-primary hover:underline text-left"
                                      onClick={() => setModuleCostBreakdown("lab")}
                                    >
                                      {r.category}
                                    </button>
                                  ) : r.source === "PATIENT_EXPENSE" ? (
                                    formatExpenseCategoryLabel(r.category)
                                  ) : (
                                    r.category
                                  )}
                                </td>
                                <td className="px-2 py-1 whitespace-nowrap">
                                  <AdmissionCostLinePaymentBadge line={r} />
                                </td>
                                <td className="px-2 py-1 text-right tabular-nums">
                                  ₹{Number(r.amount ?? 0).toLocaleString("en-IN")}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                    {!selectedPatientExpensesLoading && selectedAdmissionCostTotals && selectedAdmissionCostLines.length > 0 && (
                      <div className="text-muted-foreground mt-2 text-[11px]">
                        {canSeeAdmissionModuleCosts ? (
                          <>
                            <p>
                              Totals — Manual expenses: ₹
                              {selectedAdmissionCostTotals.patientExpense.toLocaleString("en-IN")} · Pharma: ₹
                              {selectedAdmissionCostTotals.pharma.toLocaleString("en-IN")} · Lab (Internal): ₹
                              {(selectedAdmissionCostTotals.labInternal ?? 0).toLocaleString("en-IN")} · Lab (External): ₹
                              {(selectedAdmissionCostTotals.labExternal ?? 0).toLocaleString("en-IN")} · Combined: ₹
                              {selectedAdmissionCostTotals.all.toLocaleString("en-IN")}
                            </p>
                            <p className="mt-0.5 text-[10px] italic text-muted-foreground/90">
                              External lab vendor costs are shown for reference and are excluded from Combined.
                            </p>
                          </>
                        ) : (
                          <p>
                            Total manual expenses: ₹
                            {selectedAdmissionCostTotals.patientExpense.toLocaleString("en-IN")}
                          </p>
                        )}
                      </div>
                    )}
                    {!selectedPatientExpensesLoading && duplicatePatientExpenseMatches.length > 0 && (
                      <p className="mt-2 text-xs text-amber-700">
                        Warning: a similar expense already exists for this IPD admission (same{" "}
                        {pCategory ? "category, " : ""}
                        vendor and amount). Please verify to avoid duplicates.
                      </p>
                    )}
                  </div>
                )}
              </div>
              <div>
                <Label>Category</Label>
                <Select value={pCategory || undefined} onValueChange={setPCategory}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    {patientCategoriesSorted.map((c) => (
                      <SelectItem key={c} value={c} className="font-normal">
                        {formatExpenseCategoryLabel(c)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {pCategory === B2B_PARTNER_PAYOUT_CATEGORY && (
                <div className="md:col-span-2">
                  <div className="grid gap-2 md:grid-cols-[1fr_auto] md:items-end">
                    <div>
                      <Label>B2B partner</Label>
                      <Select
                        value={pB2bPartner || ""}
                        onValueChange={(v) => {
                          const raw = String(v || "");
                          if (raw === "__add__") {
                            setAddB2bPartnerOpen(true);
                            return;
                          }
                          setPB2bPartner(raw);
                          setPVendor(raw);
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue
                            placeholder={
                              b2bPartnersLoading ? "Loading partners…" : "Select partner (Carelane, Hexa, …)"
                            }
                          />
                        </SelectTrigger>
                        <SelectContent className="max-h-72">
                          {b2bPartnerNames.map((n) => (
                            <SelectItem key={n} value={n}>
                              {n}
                            </SelectItem>
                          ))}
                          <SelectItem value="__add__">Add partner…</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-muted-foreground mt-1 text-xs">
                        Partner name fills Vendor name below and is saved with this expense.
                      </p>
                    </div>
                    <Button type="button" variant="outline" onClick={() => setAddB2bPartnerOpen(true)}>
                      Add
                    </Button>
                  </div>
                  <Dialog open={addB2bPartnerOpen} onOpenChange={setAddB2bPartnerOpen}>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Add B2B partner</DialogTitle>
                      </DialogHeader>
                      <div className="grid gap-3 py-2">
                        <div>
                          <Label>Partner name</Label>
                          <Input
                            value={newB2bPartnerName}
                            onChange={(e) => setNewB2bPartnerName(e.target.value)}
                            placeholder="e.g. Carelane"
                          />
                        </div>
                        <Button
                          disabled={!newB2bPartnerName.trim() || createB2bPartnerMut.isPending}
                          onClick={() => createB2bPartnerMut.mutate(newB2bPartnerName.trim())}
                        >
                          {createB2bPartnerMut.isPending && (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          )}
                          Save partner
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              )}
              <div>
                <Label>Amount (₹)</Label>
                <Input type="number" value={pAmount} onChange={(e) => setPAmount(e.target.value)} />
                <div className="mt-2 flex items-center gap-2">
                  <Checkbox
                    id="patient-tds-deducted"
                    checked={pTdsDeducted}
                    onCheckedChange={(checked) => setPTdsDeducted(!!checked)}
                  />
                  <Label htmlFor="patient-tds-deducted" className="text-sm font-normal">
                    TDS Deducted
                  </Label>
                </div>
                {pTdsDeducted && (
                  <p className="text-amber-700 mt-1 text-xs">
                    In Amount section, put the total value including TDS amount
                  </p>
                )}
              </div>
              <div>
                <Label>Expense funding</Label>
                <Select
                  value={pFundingSource}
                  onValueChange={(v) => setPFundingSource(v as ExpenseFundingSource)}
                  disabled={readOnlySuperAdmin}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="REGULAR">Regular expenses</SelectItem>
                    <SelectItem value="IMPREST">Imprest advance expenses</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-muted-foreground mt-1 text-xs">
                  Defaults to imprest when you still have advance balance.
                </p>
              </div>
              {pFundingSource === "IMPREST" ? (
                <div>
                  <Label>Imprest employee account</Label>
                  <Select
                    value={pImprestStaffId || "__none__"}
                    onValueChange={(v) => setPImprestStaffId(v === "__none__" ? "" : v)}
                    disabled={readOnlySuperAdmin}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select employee with balance" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Select employee…</SelectItem>
                      {imprestStaffOptions.map((o) => (
                        <SelectItem key={o.id} value={o.id}>
                          {o.name} (₹{o.balance.toLocaleString("en-IN")})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-muted-foreground mt-1 text-xs">
                    Settled from float — no cash payment mode. Amount cannot exceed balance.
                  </p>
                </div>
              ) : (
              <div>
                <Label>Mode of payment (if already paid)</Label>
                <Select
                  value={pPaymentMode || "__none__"}
                  onValueChange={(v) => setPPaymentMode(v === "__none__" ? "" : v)}
                  disabled={readOnlySuperAdmin}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Not paid yet" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Not paid yet</SelectItem>
                    {paymentModeOptions.map((m) => (
                      <SelectItem key={m} value={m}>
                        {String(m).replace(/_/g, " ")}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-muted-foreground mt-1 text-xs">
                  If you select a mode, this expense is saved as <strong>Paid</strong> immediately.
                </p>
              </div>
              )}
              <div>
                <Label>Expense date</Label>
                <Input
                  type="date"
                  value={pExpenseDate}
                  onChange={(e) => setPExpenseDate(e.target.value)}
                  disabled={readOnlySuperAdmin}
                />
                <p className="text-muted-foreground mt-1 text-xs">Defaults to today if left empty.</p>
              </div>
              <div>
                <Label>Service month</Label>
                <Input
                  type="month"
                  value={pServiceMonth}
                  onChange={(e) => setPServiceMonth(e.target.value)}
                  disabled={readOnlySuperAdmin}
                />
                <p className="text-muted-foreground mt-1 text-xs">
                  Defaults to the IPD discharge month (admission month if not discharged yet). Used for
                  reporting.
                </p>
              </div>
              {isHospitalAdmin ? (
                <div className="md:col-span-3 rounded-md border border-dashed p-2 text-xs">
                  <strong>Hospital admin:</strong> new patient expenses are saved as <strong>Approved</strong>{" "}
                  automatically (no separate approval step). Select a <strong>mode of payment</strong> to
                  save as <strong>Paid</strong> in one step.
                </div>
              ) : (
                <div>
                  <Label>Initial status</Label>
                  <Select value={pInitial} onValueChange={(v) => setPInitial(v as "DRAFT" | "PENDING_APPROVAL")}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="DRAFT">Draft</SelectItem>
                      <SelectItem value="PENDING_APPROVAL">Pending approval</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="md:col-span-3">
                {PATIENT_PAYOUT_CATEGORIES.has(pCategory) ? (
                  <div className="grid gap-3 md:grid-cols-2">
                    <div>
                      <Label>
                        {pCategory === "SURGEON_PAYOUT"
                          ? "Surgeon"
                          : pCategory === "ANAESTHETIST_PAYOUT"
                            ? "Anaesthetist"
                            : pCategory === "ASSISTANT_SURGEON_PAYOUT"
                              ? "Assistant surgeon"
                              : pCategory === "VISITING_CONSULTANT_FEE"
                                ? "Visiting consultant"
                                : pCategory === "OUTSIDE_LAB_PAYOUT"
                                  ? "External lab vendor"
                                : "Doctor visit"}
                      </Label>

                      {pCategory === "SURGEON_PAYOUT" ? (
                        <SearchableSelect
                          value={
                            pPayeeHospitalStaffId.trim()
                              ? `staff:${pPayeeHospitalStaffId.trim()}`
                              : pVendor.trim()
                                ? `name:${pVendor.trim()}`
                                : ""
                          }
                          onValueChange={(v) => {
                            const raw = String(v || "");
                            if (raw.startsWith("staff:")) {
                              const id = raw.slice("staff:".length);
                              setPPayeeHospitalStaffId(id);
                              const d = (doctors as any[]).find((x) => x.id === id);
                              if (d?.name) setPVendor(String(d.name));
                              return;
                            }
                            if (raw.startsWith("name:")) {
                              const name = raw.slice("name:".length);
                              setPPayeeHospitalStaffId("");
                              setPVendor(name);
                              return;
                            }
                            setPPayeeHospitalStaffId("");
                          }}
                          placeholder="Select surgeon (or type name)"
                          searchPlaceholder="Type surgeon name…"
                          options={buildStaffOrVendorPayeeOptions(
                            doctors as Array<{ id: string; name: string }>,
                            payeeVendorNames,
                            pVendor
                          )}
                        />
                      ) : pCategory === "OUTSIDE_LAB_PAYOUT" ? (
                        <SearchableSelect
                          value={pLabExternalVendorId}
                          onValueChange={(v) => {
                            setPLabExternalVendorId(v);
                            const vend = labExternalVendors.find((x) => x.id === v);
                            if (vend?.name) setPVendor(vend.name);
                          }}
                          placeholder="Select external lab vendor"
                          searchPlaceholder="Type vendor name…"
                          options={labExternalVendors.map((vendor) => ({
                            value: vendor.id,
                            label: vendor.name,
                            searchText: vendor.name,
                          }))}
                        />
                      ) : (
                        <Select
                          value={pVendor}
                          onValueChange={(v) => {
                            setPVendor(v);
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select payee" />
                          </SelectTrigger>
                          <SelectContent className="max-h-72">
                            {payeeVendorNames.map((n) => (
                              <SelectItem key={n} value={n}>
                                {n}
                              </SelectItem>
                            ))}
                            {pVendor.trim() && !payeeVendorNames.includes(pVendor.trim()) && (
                              <SelectItem value={pVendor.trim()}>Custom: {pVendor.trim()}</SelectItem>
                            )}
                          </SelectContent>
                        </Select>
                      )}
                    </div>

                    <div className="md:col-span-1">
                      <Label>{pCategory === "SURGEON_PAYOUT" ? "Doctor name" : "Vendor / payee name"}</Label>
                      <Input
                        value={pVendor}
                        onChange={(e) => setPVendor(e.target.value)}
                        placeholder={
                          pCategory === "SURGEON_PAYOUT"
                            ? "Select above or type surgeon name"
                            : pCategory === "OUTSIDE_LAB_PAYOUT"
                              ? "Auto-filled from selection"
                            : "Type to add a new name"
                        }
                      />
                      {pCategory !== "OUTSIDE_LAB_PAYOUT" && (
                        <p className="text-muted-foreground mt-1 text-xs">
                          Tip: after you save once, this name will appear in the dropdown for this category.
                        </p>
                      )}
                    </div>
                  </div>
                ) : (
                  <>
                    <Label>Vendor name (optional)</Label>
                    <Input
                      value={pVendor}
                      onChange={(e) => setPVendor(e.target.value)}
                      placeholder="External vendor or payee name"
                    />
                  </>
                )}
              </div>
              <div className="md:col-span-2">
                <Label>Description</Label>
                <Textarea value={pDesc} onChange={(e) => setPDesc(e.target.value)} rows={2} />
              </div>
              <div className="md:col-span-2">
                <Label className="flex items-center gap-2">
                  <Paperclip className="h-4 w-4" />
                  Invoices / proofs (optional, multiple)
                </Label>
                <Input
                  key={pProofInputKey}
                  type="file"
                  multiple
                  accept={PROOF_FILE_ACCEPT}
                  disabled={readOnlySuperAdmin}
                  className="cursor-pointer"
                  onChange={(e) =>
                    setPProofFile(
                      Array.from(e.target.files ?? []).slice(0, MAX_EXPENSE_PROOF_FILES)
                    )
                  }
                />
                {pProofFile.length > 0 && (
                  <p className="text-muted-foreground mt-1 text-xs">
                    Selected ({pProofFile.length}):{" "}
                    {pProofFile.map((f) => f.name).join(", ")}
                  </p>
                )}
              </div>
              <div>
                <Button
                  disabled={
                    createPatient.isPending ||
                    !pAdmission ||
                    !pCategory ||
                    !pAmount ||
                    (pCategory === B2B_PARTNER_PAYOUT_CATEGORY && !pB2bPartner.trim()) ||
                    (pCategory === "SURGEON_PAYOUT" &&
                      !pPayeeHospitalStaffId.trim() &&
                      !pVendor.trim())
                  }
                  onClick={() => createPatient.mutate()}
                >
                  {createPatient.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Save
                </Button>
              </div>
            </CardContent>
          </Card>

          {(isHospitalAdmin || isSuperAdmin) && !readOnlySuperAdmin && (
            <Card className="border-dashed">
              <CardHeader className="py-3">
                <CardTitle className="text-base">Filter list by IPD patient (admin)</CardTitle>
                <p className="text-muted-foreground text-sm">
                  Pick a patient from a recent admission to show <strong>all</strong> patient expenses for that
                  person across every IPD stay. Clear to show hospital-wide recent expenses again.
                </p>
              </CardHeader>
              <CardContent className="flex flex-wrap items-center gap-2">
                <Popover open={filterPatientPickerOpen} onOpenChange={setFilterPatientPickerOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      className="min-w-[260px] justify-between text-left font-normal"
                    >
                      <span className="truncate">
                        {filterPatientLabel || "Select patient to filter expenses…"}
                      </span>
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[min(100vw-2rem,28rem)] p-0" align="start">
                    <div className="border-b p-2">
                      <Input
                        placeholder="Search by name, UHID, or IPD #"
                        value={filterPatientSearchInput}
                        onChange={(e) => setFilterPatientSearchInput(e.target.value)}
                        autoFocus
                      />
                    </div>
                    <div className="max-h-64 overflow-y-auto p-1">
                      {filterPatientAdmissionsFetching && (
                        <div className="text-muted-foreground flex items-center gap-2 p-3 text-sm">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Loading…
                        </div>
                      )}
                      {!filterPatientAdmissionsFetching &&
                        filterPatientAdmissionOptions.length === 0 && (
                          <p className="text-muted-foreground p-3 text-sm">No admissions found.</p>
                        )}
                      {!filterPatientAdmissionsFetching &&
                        filterPatientAdmissionOptions.map((a) => (
                          <button
                            key={a.id}
                            type="button"
                            className="hover:bg-muted flex w-full flex-col items-start rounded-md px-3 py-2 text-left text-sm"
                            onClick={() => {
                              setFilterPatientId(a.patientId);
                              setFilterPatientLabel(`${a.patientName} — all admissions`);
                              setFilterPatientPickerOpen(false);
                            }}
                          >
                            <span className="font-medium">{a.patientName}</span>
                            <span className="text-muted-foreground text-xs">
                              IPD {a.ipdNumber}
                              {a.patientUhid ? ` · UHID ${a.patientUhid}` : ""}
                            </span>
                          </button>
                        ))}
                    </div>
                  </PopoverContent>
                </Popover>
                {filterPatientId && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setFilterPatientId(null);
                      setFilterPatientLabel("");
                    }}
                  >
                    Clear filter
                  </Button>
                )}
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle>Recent patient expenses</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="mb-3 flex flex-wrap gap-3 items-end">
                <div className="space-y-1">
                  <Label>Patient name</Label>
                  <Input
                    value={patientNameSearch}
                    onChange={(e) => setPatientNameSearch(e.target.value)}
                    placeholder="Search patient…"
                    className="w-48"
                  />
                </div>
                <div className="space-y-1">
                  <Label>Entered by</Label>
                  <Input
                    value={patientEnteredBySearch}
                    onChange={(e) => setPatientEnteredBySearch(e.target.value)}
                    placeholder="Search entered by…"
                    className="w-48"
                  />
                </div>
                <div className="space-y-1">
                  <Label>Category</Label>
                  <Select value={patientCategoryFilter} onValueChange={setPatientCategoryFilter}>
                    <SelectTrigger className="w-52">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ALL">All categories</SelectItem>
                      {patientCategoriesSorted.map((c) => (
                        <SelectItem key={c} value={c}>
                          {formatExpenseCategoryLabel(c)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label>Vendor name</Label>
                  <Input
                    value={patientVendorSearch}
                    onChange={(e) => setPatientVendorSearch(e.target.value)}
                    placeholder="Search vendor…"
                    className="w-48"
                  />
                </div>
                <div className="space-y-1">
                  <Label>TDS deducted</Label>
                  <Select value={patientTdsFilter} onValueChange={(v) => setPatientTdsFilter(v as any)}>
                    <SelectTrigger className="w-40">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ALL">All</SelectItem>
                      <SelectItem value="YES">Deducted</SelectItem>
                      <SelectItem value="NO">Not deducted</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label>From</Label>
                  <Input
                    type="date"
                    value={patientListFrom}
                    onChange={(e) => {
                      setPatientPage(1);
                      setPatientListFrom(e.target.value);
                    }}
                    className="w-40"
                  />
                </div>
                <div className="space-y-1">
                  <Label>To</Label>
                  <Input
                    type="date"
                    value={patientListTo}
                    onChange={(e) => {
                      setPatientPage(1);
                      setPatientListTo(e.target.value);
                    }}
                    className="w-40"
                  />
                </div>
                {(patientVendorSearch.trim() ||
                  patientTdsFilter !== "ALL" ||
                  patientCategoryFilter !== "ALL" ||
                  patientNameSearch.trim() ||
                  patientEnteredBySearch.trim()) && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setPatientVendorSearch("");
                      setPatientTdsFilter("ALL");
                      setPatientCategoryFilter("ALL");
                      setPatientNameSearch("");
                      setPatientEnteredBySearch("");
                    }}
                  >
                    Clear search
                  </Button>
                )}
              </div>
              {!patientLoading && patientExpenseTotals.count > 0 ? (
                <div className="mb-3 flex flex-wrap gap-x-6 gap-y-1 text-sm">
                  <span>
                    Total:{" "}
                    <strong>
                      ₹
                      {patientExpenseTotals.total.toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </strong>
                    <span className="text-muted-foreground"> ({patientExpenseTotals.count})</span>
                  </span>
                  <span className="text-emerald-700 dark:text-emerald-400">
                    Paid:{" "}
                    <strong>
                      ₹
                      {patientExpenseTotals.paid.toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </strong>
                  </span>
                  <span className="text-amber-700 dark:text-amber-400">
                    Unpaid:{" "}
                    <strong>
                      ₹
                      {patientExpenseTotals.unpaid.toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </strong>
                  </span>
                </div>
              ) : null}
              {patientLoading ? (
                <Loader2 className="h-6 w-6 animate-spin" />
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="whitespace-nowrap">Expense ID</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead className="whitespace-nowrap">Service month</TableHead>
                      <TableHead>IPD #</TableHead>
                      <TableHead>Patient</TableHead>
                      <TableHead className="whitespace-nowrap">Discharge date</TableHead>
                      <TableHead>Vendor name</TableHead>
                      <TableHead>Entered by</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead className="whitespace-nowrap">TDS</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="min-w-[160px]">Invoice / proof</TableHead>
                      <TableHead className="w-[120px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {patientRows.map((row: any) => (
                      <TableRow key={row.id}>
                        <TableCell className="whitespace-nowrap font-mono text-xs">
                          {row.expenseNumber || "—"}
                        </TableCell>
                        <TableCell>{formatExpenseRowDateField(row.expenseDate, row.createdAt)}</TableCell>
                        <TableCell className="whitespace-nowrap">
                          {row.serviceMonth ? expenseIsoToIstYm(row.serviceMonth) : "—"}
                        </TableCell>
                        <TableCell className="whitespace-nowrap font-mono text-xs">
                          {row.admission?.queue?.ipdNumber ?? "—"}
                        </TableCell>
                        <TableCell>{row.patient?.name}</TableCell>
                        <TableCell className="whitespace-nowrap">
                          {formatExpenseDateIst(row.admission?.dischargeDate)}
                        </TableCell>
                        <TableCell className="max-w-[140px] truncate text-sm" title={row.payeeVendorName || row.payeeDoctor?.name || ""}>
                          {row.payeeVendorName || row.payeeDoctor?.name || "—"}
                        </TableCell>
                        <TableCell className="max-w-[140px] truncate text-sm" title={row.createdBy?.name || row.createdByName || ""}>
                          {row.createdBy?.name || row.createdByName || "—"}
                        </TableCell>
                        <TableCell className="max-w-[180px]">
                          <div className="truncate" title={formatExpenseCategoryLabel(String(row.category ?? ""))}>
                            {formatExpenseCategoryLabel(String(row.category ?? ""))}
                          </div>
                          {row.subCategory ? (
                            <div className="text-muted-foreground truncate text-xs" title={String(row.subCategory)}>
                              {String(row.subCategory)}
                            </div>
                          ) : null}
                        </TableCell>
                        <TableCell className="text-right">₹{Number(row.amount).toFixed(2)}</TableCell>
                        <TableCell className="text-xs">
                          {row.tdsDeducted ? (
                            <span className="text-emerald-700 dark:text-emerald-400">Deducted</span>
                          ) : (
                            <span className="text-muted-foreground">Not deducted</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col gap-0.5">
                            <span>
                              {row.workflowStatus === "PAID" && row.paymentMode
                                ? `PAID (${String(row.paymentMode).replace(/_/g, " ")})`
                                : row.workflowStatus}
                            </span>
                            {row.workflowStatus === "PAID" && row.paidAt ? (
                              <span className="text-muted-foreground text-xs whitespace-nowrap">
                                {formatExpensePaidAtIst(row.paidAt)}
                              </span>
                            ) : null}
                          </div>
                        </TableCell>
                        <TableCell className="align-top">
                          <div className="flex flex-col gap-1">
                            {expenseProofList(row).map((proof) => (
                              <div key={proof.id ?? proof.url} className="flex items-start gap-1">
                                <a
                                  href={proof.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-primary inline-flex items-center gap-1 text-xs underline-offset-4 hover:underline"
                                >
                                  <ExternalLink className="h-3 w-3 shrink-0" />
                                  <span
                                    className="truncate max-w-[120px]"
                                    title={proof.fileName || "Proof"}
                                  >
                                    {proof.fileName || "View proof"}
                                  </span>
                                </a>
                                {proof.id &&
                                  !readOnlySuperAdmin &&
                                  canUploadExpenseProof(row.workflowStatus, user?.role) && (
                                    <button
                                      type="button"
                                      className="text-destructive text-[10px] hover:underline"
                                      onClick={() =>
                                        deletePatientProofByIdMut.mutate({
                                          id: row.id,
                                          proofId: proof.id!,
                                        })
                                      }
                                    >
                                      Remove
                                    </button>
                                  )}
                              </div>
                            ))}
                            {!readOnlySuperAdmin &&
                              canUploadExpenseProof(row.workflowStatus, user?.role) &&
                              (uploadingPatientProofId === row.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <label className="text-primary cursor-pointer text-xs hover:underline">
                                  <input
                                    type="file"
                                    multiple
                                    className="sr-only"
                                    accept={PROOF_FILE_ACCEPT}
                                    onChange={(e) => {
                                      const files = Array.from(e.target.files ?? []);
                                      if (files.length)
                                        uploadPatientProofMut.mutate({ id: row.id, files });
                                      e.target.value = "";
                                    }}
                                  />
                                  {expenseProofList(row).length > 0 ? "Add file(s)" : "Attach file(s)"}
                                </label>
                              ))}
                            {(expenseProofList(row).length > 1 ||
                              (expenseProofList(row).length === 1 && !expenseProofList(row)[0]?.id)) &&
                              !readOnlySuperAdmin &&
                              canUploadExpenseProof(row.workflowStatus, user?.role) && (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 px-0 text-xs text-destructive"
                                  onClick={() => deletePatientProofMut.mutate(row.id)}
                                >
                                  Remove all
                                </Button>
                              )}
                          </div>
                        </TableCell>
                        <TableCell className="space-x-1">
                          {canAppendOrCorrectExpenses(user?.role) &&
                            row.workflowStatus !== "REJECTED" && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="mb-1"
                                onClick={() => openEditPatient(row)}
                              >
                                <Pencil className="mr-1 h-3 w-3" />
                                Edit
                              </Button>
                            )}
                          {row.workflowStatus === "DRAFT" && (
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={() =>
                                expensesApi
                                  .submitPatient(row.id)
                                  .then(() => qc.invalidateQueries({ queryKey: ["expenses-patient"] }))
                              }
                            >
                              Submit
                            </Button>
                          )}
                          {canApprovePatientExpenses(user?.role) && row.workflowStatus === "PENDING_APPROVAL" && (
                            <Button size="sm" onClick={() => approvePatient.mutate(row.id)}>
                              Approve
                            </Button>
                          )}
                          {!readOnlySuperAdmin &&
                            canMarkPaid &&
                            canShowMarkPaidForStatus(row.workflowStatus) && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => openMarkPaid("patient", row.id, Number(row.amount))}
                              >
                                Mark paid
                              </Button>
                            )}
                          {isHospitalAdmin && row.workflowStatus === "PAID" && (
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={unmarkPaidMut.isPending}
                              onClick={() => confirmUnmarkPaid("patient", row.id)}
                            >
                              Mark unpaid
                            </Button>
                          )}
                          {isHospitalAdmin && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-destructive hover:text-destructive"
                              disabled={deletePatientExpenseMut.isPending}
                              onClick={() => {
                                if (
                                  window.confirm(
                                    "Delete this expense? This cannot be undone."
                                  )
                                ) {
                                  deletePatientExpenseMut.mutate(row.id);
                                }
                              }}
                            >
                              <Trash2 className="mr-1 h-3 w-3" />
                              Delete
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
              {!patientLoading && (patientList?.total ?? 0) > expensePageSize ? (
                <div className="mt-3 flex items-center justify-between gap-2 text-sm">
                  <span className="text-muted-foreground">
                    Page {patientList?.page ?? 1} · {patientList?.total ?? 0} total
                  </span>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={patientPage <= 1}
                      onClick={() => setPatientPage((p) => Math.max(1, p - 1))}
                    >
                      Previous
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={patientPage * expensePageSize >= (patientList?.total ?? 0)}
                      onClick={() => setPatientPage((p) => p + 1)}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              ) : null}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="central" className="space-y-4">
          <Card
            key={`central-new-expense-${centralFormResetKey}`}
            className={readOnlySuperAdmin ? "opacity-60 pointer-events-none" : ""}
          >
            <CardHeader>
              <CardTitle>New central expense</CardTitle>
              <p className="text-muted-foreground text-sm">
                Categories are filtered by your role (e.g. reception cannot add salary, rent, or equipment purchase).
              </p>
            </CardHeader>
            <CardContent className="grid gap-4 md:grid-cols-2">
              <div>
                <Label>Category</Label>
                <Select
                  value={cCategory || undefined}
                  onValueChange={(v) => {
                    setCCategory(v);
                    if (v === "PHARMA_PAYOUT") setCVendor("");
                    else setCPharmaVendorId("");
                    if (!isCentralDoctorPayeeCategory(v)) setCPayeeHospitalStaffId("");
                    setCOnlineChannel("");
                    setCOfflineSubcategory("");
                    if (!(v === "SALARY" || v === "SALARY_ADVANCE" || v === "INCENTIVES_BONUS")) {
                      setCExpenseEmployeeId("");
                    }
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    {centralCategoriesSorted.map((c) => (
                      <SelectItem key={c} value={c} className="font-normal">
                        {formatExpenseCategoryLabel(c)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Hospital</Label>
                {isChainAdmin ? (
                  <>
                    <Select value={cHospitalSelect} onValueChange={setCHospitalSelect}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select hospital" />
                      </SelectTrigger>
                      <SelectContent>
                        {chainHospitalOptions.map((u) => (
                          <SelectItem key={u.value} value={u.value}>
                            {u.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-muted-foreground mt-1 text-xs">
                      Chain admin: pick a hospital from your chain, or Central for chain-level expenses.
                    </p>
                  </>
                ) : (
                  <>
                    <Input
                      value={lockedHospitalName}
                      readOnly
                      className="bg-muted"
                    />
                    <p className="text-muted-foreground mt-1 text-xs">
                      Expenses are booked under your hospital automatically.
                    </p>
                  </>
                )}
              </div>
              <div>
                <Label>Amount (₹)</Label>
                <Input type="number" value={cAmount} onChange={(e) => setCAmount(e.target.value)} />
                <div className="mt-2 flex items-center gap-2">
                  <Checkbox
                    id="central-tds-deducted"
                    checked={cTdsDeducted}
                    onCheckedChange={(checked) => setCTdsDeducted(!!checked)}
                  />
                  <Label htmlFor="central-tds-deducted" className="text-sm font-normal">
                    TDS Deducted
                  </Label>
                </div>
                {cTdsDeducted && (
                  <p className="text-amber-700 mt-1 text-xs">
                    In Amount section, put the total value including TDS amount
                  </p>
                )}
              </div>
              <div>
                <Label>Expense funding</Label>
                <Select
                  value={cFundingSource}
                  onValueChange={(v) => setCFundingSource(v as ExpenseFundingSource)}
                  disabled={readOnlySuperAdmin}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="REGULAR">Regular expenses</SelectItem>
                    <SelectItem value="IMPREST">Imprest advance expenses</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-muted-foreground mt-1 text-xs">
                  Defaults to imprest when you still have advance balance.
                </p>
              </div>
              {cFundingSource === "IMPREST" ? (
                <div>
                  <Label>Imprest employee account</Label>
                  <Select
                    value={cImprestStaffId || "__none__"}
                    onValueChange={(v) => setCImprestStaffId(v === "__none__" ? "" : v)}
                    disabled={readOnlySuperAdmin}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select employee with balance" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Select employee…</SelectItem>
                      {imprestStaffOptions.map((o) => (
                        <SelectItem key={o.id} value={o.id}>
                          {o.name} (₹{o.balance.toLocaleString("en-IN")})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-muted-foreground mt-1 text-xs">
                    Settled from float — no cash payment mode. Amount cannot exceed balance.
                  </p>
                </div>
              ) : (
              <div>
                <Label>Mode of payment (if already paid)</Label>
                <Select
                  value={cPaymentMode || "__none__"}
                  onValueChange={(v) => setCPaymentMode(v === "__none__" ? "" : v)}
                  disabled={readOnlySuperAdmin}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Not paid yet" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Not paid yet</SelectItem>
                    {paymentModeOptions.map((m) => (
                      <SelectItem key={m} value={m}>
                        {String(m).replace(/_/g, " ")}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-muted-foreground mt-1 text-xs">
                  If you select a mode, this expense is saved as <strong>Paid</strong> immediately.
                </p>
              </div>
              )}
              <div>
                <Label>Expense date</Label>
                <Input
                  type="date"
                  value={cExpenseDate}
                  onChange={(e) => setCExpenseDate(e.target.value)}
                  disabled={readOnlySuperAdmin}
                />
                <p className="text-muted-foreground mt-1 text-xs">Defaults to today if left empty.</p>
              </div>
              <div>
                <Label>Service month</Label>
                <Input
                  type="month"
                  value={cServiceMonth}
                  onChange={(e) => setCServiceMonth(e.target.value)}
                  disabled={readOnlySuperAdmin}
                />
                <p className="text-muted-foreground mt-1 text-xs">
                  Month in which the service was taken (for reporting).
                </p>
              </div>
              {salaryCategory ? (
                <div className="md:col-span-2 grid gap-3">
                  <div>
                    <Label>Employee</Label>
                    <Select
                      value={cExpenseEmployeeId || undefined}
                      onValueChange={(v) => {
                        const raw = String(v || "");
                        if (raw === "__add__") {
                          setAddEmpOpen(true);
                          return;
                        }
                        setCExpenseEmployeeId(raw);
                        const row = (centralEmployees as any[]).find((e) => e.id === raw);
                        if (row?.name) setCVendor(String(row.name));
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={centralEmployeesLoading ? "Loading..." : "Select employee"} />
                      </SelectTrigger>
                      <SelectContent className="max-h-72">
                        {(centralEmployees || []).map((e: any) => (
                          <SelectItem key={e.id} value={e.id}>
                            {e.name}
                          </SelectItem>
                        ))}
                        <SelectItem value="__add__">Add Name…</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-muted-foreground mt-1 text-xs">
                      If name is missing, use “Add Name” once — it will appear next time.
                    </p>
                  </div>

                  <Dialog open={addEmpOpen} onOpenChange={setAddEmpOpen}>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Add employee</DialogTitle>
                      </DialogHeader>
                      <div className="grid gap-3">
                        <div>
                          <Label>Name</Label>
                          <Input value={addEmpName} onChange={(e) => setAddEmpName(e.target.value)} />
                        </div>
                      </div>
                      <DialogFooter>
                        <Button variant="outline" onClick={() => setAddEmpOpen(false)}>
                          Cancel
                        </Button>
                        <Button onClick={() => addEmployeeMut.mutate()} disabled={addEmployeeMut.isPending}>
                          {addEmployeeMut.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                          Save
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </div>
              ) : cCategory === "ONLINE_MARKETING" ? (
                <div className="md:col-span-2 grid gap-3">
                  <div>
                    <Label>Online channel</Label>
                    <Select value={cOnlineChannel || ""} onValueChange={setCOnlineChannel}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select channel" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="META">Meta</SelectItem>
                        <SelectItem value="GOOGLE">Google</SelectItem>
                        <SelectItem value="OTHERS">Others</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-muted-foreground mt-1 text-xs">
                      Stored as META / GOOGLE / OTHERS under sub-category for duplicate checks.
                    </p>
                  </div>
                  <div>
                    <Label>Vendor / agency name (optional)</Label>
                    <CentralVendorNameTypeahead
                      value={cVendor}
                      onChange={setCVendor}
                      searchAllCategories
                      queryEnabled={!isSuperAdmin || !!hospitalId}
                      hospitalId={reportParams?.hospitalId}
                      placeholder="e.g., agency invoice payee name"
                    />
                  </div>
                </div>
              ) : cCategory === "OFFLINE_MARKETING" ? (
                <div className="md:col-span-2 grid gap-3">
                  <div className="grid gap-2 md:grid-cols-[1fr_auto] md:items-end">
                    <div>
                      <Label>Offline subcategory</Label>
                      <Select
                        value={cOfflineSubcategory || ""}
                        onValueChange={(v) => {
                          const raw = String(v || "");
                          if (raw === "__add__") {
                            setAddOfflineSubOpen(true);
                            return;
                          }
                          setCOfflineSubcategory(raw);
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue
                            placeholder={
                              offlineMarketingSubsLoading ? "Loading subcategories..." : "Select subcategory"
                            }
                          />
                        </SelectTrigger>
                        <SelectContent className="max-h-72">
                          {offlineMarketingSubs.map((n) => (
                            <SelectItem key={n} value={n}>
                              {n}
                            </SelectItem>
                          ))}
                          <SelectItem value="__add__">Add subcategory…</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-muted-foreground mt-1 text-xs">
                        New subcategories are saved for this hospital and show up next time.
                      </p>
                    </div>
                    <Button type="button" variant="outline" onClick={() => setAddOfflineSubOpen(true)}>
                      Add
                    </Button>
                  </div>
                  <div>
                    <Label>Vendor name (optional)</Label>
                    <CentralVendorNameTypeahead
                      value={cVendor}
                      onChange={setCVendor}
                      searchAllCategories
                      queryEnabled={!isSuperAdmin || !!hospitalId}
                      hospitalId={reportParams?.hospitalId}
                      placeholder="Supplier / printer / billboard vendor"
                    />
                  </div>

                  <Dialog open={addOfflineSubOpen} onOpenChange={setAddOfflineSubOpen}>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Add offline marketing subcategory</DialogTitle>
                      </DialogHeader>
                      <div className="grid gap-3">
                        <div>
                          <Label>Name</Label>
                          <Input value={cNewOfflineSubcategory} onChange={(e) => setCNewOfflineSubcategory(e.target.value)} />
                        </div>
                      </div>
                      <DialogFooter>
                        <Button variant="outline" onClick={() => setAddOfflineSubOpen(false)}>
                          Cancel
                        </Button>
                        <Button
                          onClick={() => createOfflineMarketingSubMut.mutate()}
                          disabled={createOfflineMarketingSubMut.isPending}
                        >
                          {createOfflineMarketingSubMut.isPending && (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          )}
                          Save
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </div>
              ) : cCategory === "PHARMA_PAYOUT" ? (
                <div className="md:col-span-2">
                  <Label>Pharma vendor</Label>
                  <Select
                    value={cPharmaVendorId || undefined}
                    onValueChange={setCPharmaVendorId}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a pharmacy vendor" />
                    </SelectTrigger>
                    <SelectContent className="max-h-72">
                      {pharmaVendors.length === 0 ? (
                        <div className="text-muted-foreground px-2 py-3 text-sm">
                          No vendors in Pharmacy. Add vendors under Pharmacy → Vendors.
                        </div>
                      ) : (
                        pharmaVendors.map((v) => (
                          <SelectItem key={v.id} value={v.id}>
                            {v.name}
                            {v.phone ? ` · ${v.phone}` : ""}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>
              ) : isCentralDoctorPayeeCategory(cCategory) ? (
                <div className="md:col-span-2 grid gap-2">
                  <Label>Doctor</Label>
                  <SearchableSelect
                    value={
                      cPayeeHospitalStaffId.trim()
                        ? `staff:${cPayeeHospitalStaffId.trim()}`
                        : cVendor.trim()
                          ? `name:${cVendor.trim()}`
                          : ""
                    }
                    onValueChange={(v) => {
                      const raw = String(v || "");
                      if (raw.startsWith("staff:")) {
                        const id = raw.slice("staff:".length);
                        setCPayeeHospitalStaffId(id);
                        const d = (doctors as any[]).find((x) => x.id === id);
                        if (d?.name) setCVendor(String(d.name));
                        return;
                      }
                      if (raw.startsWith("name:")) {
                        const name = raw.slice("name:".length);
                        setCPayeeHospitalStaffId("");
                        setCVendor(name);
                        return;
                      }
                      setCPayeeHospitalStaffId("");
                    }}
                    placeholder="Select doctor (or type name)"
                    searchPlaceholder="Type doctor name…"
                    options={buildStaffOrVendorPayeeOptions(
                      doctors as Array<{ id: string; name: string }>,
                      centralPayeeVendorNames,
                      cVendor
                    )}
                  />
                  <CentralVendorNameTypeahead
                    value={cVendor}
                    onChange={(v) => {
                      setCVendor(v);
                      if (v.trim()) setCPayeeHospitalStaffId("");
                    }}
                    category={cCategory}
                    queryEnabled={!isSuperAdmin || !!hospitalId}
                    hospitalId={reportParams?.hospitalId}
                    placeholder="Type doctor name if not in list"
                  />
                  <p className="text-muted-foreground text-xs">
                    Tip: after you save once, this name will appear in the dropdown.
                  </p>
                </div>
              ) : (
                <div>
                  <Label>Vendor name</Label>
                  <CentralVendorNameTypeahead
                    value={cVendor}
                    onChange={setCVendor}
                    searchAllCategories
                    queryEnabled={!isSuperAdmin || !!hospitalId}
                    hospitalId={reportParams?.hospitalId}
                    placeholder="Supplier or vendor"
                  />
                </div>
              )}
              <div>
                <Label>Invoice no.</Label>
                <Input value={cInvoice} onChange={(e) => setCInvoice(e.target.value)} />
              </div>
              {isHospitalAdmin ? (
                <div className="md:col-span-2 rounded-md border border-dashed p-3 text-sm">
                  <strong>Hospital admin:</strong> new central expenses are saved as <strong>Approved</strong>{" "}
                  automatically.
                </div>
              ) : (
                <div>
                  <Label>Initial status</Label>
                  <Select value={cInitial} onValueChange={(v) => setCInitial(v as "DRAFT" | "PENDING_APPROVAL")}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="DRAFT">Draft</SelectItem>
                      <SelectItem value="PENDING_APPROVAL">Pending approval</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="md:col-span-2">
                <Label>Description</Label>
                <Textarea value={cDesc} onChange={(e) => setCDesc(e.target.value)} rows={2} />
              </div>
              <div className="md:col-span-2">
                <Label className="flex items-center gap-2">
                  <Paperclip className="h-4 w-4" />
                  Invoices / proofs (optional, multiple)
                </Label>
                <Input
                  key={cProofInputKey}
                  type="file"
                  multiple
                  accept={PROOF_FILE_ACCEPT}
                  disabled={readOnlySuperAdmin}
                  className="cursor-pointer"
                  onChange={(e) =>
                    setCProofFile(
                      Array.from(e.target.files ?? []).slice(0, MAX_EXPENSE_PROOF_FILES)
                    )
                  }
                />
                {cProofFile.length > 0 && (
                  <p className="text-muted-foreground mt-1 text-xs">
                    Selected ({cProofFile.length}):{" "}
                    {cProofFile.map((f) => f.name).join(", ")}
                  </p>
                )}
              </div>
              <Button
                disabled={
                  createCentral.isPending ||
                  !cCategory ||
                  !cAmount ||
                  (cCategory === "ONLINE_MARKETING" && !cOnlineChannel.trim()) ||
                  (cCategory === "OFFLINE_MARKETING" && !cOfflineSubcategory.trim()) ||
                  (cCategory === "PHARMA_PAYOUT" && !cPharmaVendorId.trim()) ||
                  (salaryCategory && !cExpenseEmployeeId.trim()) ||
                  (isCentralDoctorPayeeCategory(cCategory) &&
                    !cPayeeHospitalStaffId.trim() &&
                    !cVendor.trim())
                }
                onClick={() => createCentral.mutate()}
              >
                {createCentral.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Save
              </Button>
            </CardContent>
          </Card>

          {cCategory ? (
            <Card className="border-muted">
              <CardHeader className="py-3">
                <CardTitle className="text-base">Existing expenses for this category</CardTitle>
                <p className="text-muted-foreground text-sm">
                  Quick reference to reduce duplicate postings for the category you picked above.
                </p>
              </CardHeader>
              <CardContent>
                {centralCategoryPreviewLoading ? (
                  <div className="text-muted-foreground flex items-center gap-2 text-sm">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading…
                  </div>
                ) : !centralCategoryPreview?.length ? (
                  <p className="text-muted-foreground text-sm">No expenses found for this category yet.</p>
                ) : (
                  <div className="max-h-72 overflow-y-auto rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>Amount</TableHead>
                          <TableHead>Sub</TableHead>
                          <TableHead>Vendor</TableHead>
                          <TableHead>Invoice</TableHead>
                          <TableHead>Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(centralCategoryPreview as any[]).slice(0, 75).map((r) => {
                          const ds =
                            r.expenseDate && !Number.isNaN(new Date(r.expenseDate).getTime())
                              ? formatExpenseDateIst(r.expenseDate)
                              : "";
                          return (
                            <TableRow key={String(r.id)}>
                              <TableCell className="whitespace-nowrap text-xs">{ds}</TableCell>
                              <TableCell className="whitespace-nowrap text-xs">{String(r.amount ?? "")}</TableCell>
                              <TableCell className="max-w-[180px] truncate text-xs">
                                {r.subCategory ? String(r.subCategory) : "—"}
                              </TableCell>
                              <TableCell className="max-w-[220px] truncate text-xs">
                                {r.vendorName ? String(r.vendorName) : "—"}
                              </TableCell>
                              <TableCell className="max-w-[160px] truncate text-xs">
                                {r.invoiceNo ? String(r.invoiceNo) : "—"}
                              </TableCell>
                              <TableCell className="whitespace-nowrap text-xs">{String(r.workflowStatus ?? "")}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          ) : null}

          <Card>
            <CardHeader>
              <CardTitle>Recent central expenses</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="mb-3 flex flex-wrap gap-3 items-end">
                <div className="space-y-1">
                  <Label>Vendor name</Label>
                  <Input
                    value={centralVendorSearch}
                    onChange={(e) => setCentralVendorSearch(e.target.value)}
                    placeholder="Search vendor…"
                    className="w-64"
                  />
                </div>
                <div className="space-y-1">
                  <Label>Entered by</Label>
                  <Input
                    value={centralEnteredBySearch}
                    onChange={(e) => setCentralEnteredBySearch(e.target.value)}
                    placeholder="Search entered by…"
                    className="w-48"
                  />
                </div>
                <div className="space-y-1">
                  <Label>Category</Label>
                  <Select value={centralCategoryFilter} onValueChange={setCentralCategoryFilter}>
                    <SelectTrigger className="w-56">
                      <SelectValue placeholder="All categories" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ALL">All</SelectItem>
                      {centralCategoriesAllSorted.map((c) => (
                        <SelectItem key={c} value={c}>
                          {formatExpenseCategoryLabel(c)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label>Hospital</Label>
                  {isChainAdmin ? (
                    <Select value={centralHospitalUnitFilter} onValueChange={setCentralHospitalUnitFilter}>
                      <SelectTrigger className="w-56">
                        <SelectValue placeholder="All hospitals" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ALL">All</SelectItem>
                        {CENTRAL_EXPENSE_HOSPITAL_UNITS.map((u) => (
                          <SelectItem key={u.value} value={u.value}>
                            {u.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input
                      value={lockedHospitalName}
                      readOnly
                      className="w-56 bg-muted"
                    />
                  )}
                </div>
                <div className="space-y-1">
                  <Label>TDS deducted</Label>
                  <Select value={centralTdsFilter} onValueChange={(v) => setCentralTdsFilter(v as any)}>
                    <SelectTrigger className="w-40">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ALL">All</SelectItem>
                      <SelectItem value="YES">Deducted</SelectItem>
                      <SelectItem value="NO">Not deducted</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label>From</Label>
                  <Input
                    type="date"
                    value={centralListFrom}
                    onChange={(e) => {
                      setCentralPage(1);
                      setCentralListFrom(e.target.value);
                    }}
                    className="w-40"
                  />
                </div>
                <div className="space-y-1">
                  <Label>To</Label>
                  <Input
                    type="date"
                    value={centralListTo}
                    onChange={(e) => {
                      setCentralPage(1);
                      setCentralListTo(e.target.value);
                    }}
                    className="w-40"
                  />
                </div>
                {(centralVendorSearch.trim() ||
                  centralEnteredBySearch.trim() ||
                  centralCategoryFilter !== "ALL" ||
                  centralHospitalUnitFilter !== "ALL" ||
                  centralTdsFilter !== "ALL") && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setCentralVendorSearch("");
                      setCentralEnteredBySearch("");
                      setCentralCategoryFilter("ALL");
                      setCentralHospitalUnitFilter("ALL");
                      setCentralTdsFilter("ALL");
                    }}
                  >
                    Clear search
                  </Button>
                )}
              </div>
              {centralListFilterActive && centralFilterTotals && !centralLoading ? (
                <div className="mb-3 flex flex-wrap gap-x-6 gap-y-1 text-sm">
                  <span>
                    Total: <strong>₹{centralFilterTotals.total.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                    <span className="text-muted-foreground"> ({centralFilterTotals.count})</span>
                  </span>
                  <span className="text-emerald-700 dark:text-emerald-400">
                    Paid: <strong>₹{centralFilterTotals.paid.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                  </span>
                  <span className="text-amber-700 dark:text-amber-400">
                    Unpaid: <strong>₹{centralFilterTotals.unpaid.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                  </span>
                </div>
              ) : null}
              {centralLoading ? (
                <Loader2 className="h-6 w-6 animate-spin" />
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="whitespace-nowrap">Expense ID</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Hospital</TableHead>
                      <TableHead>Vendor name</TableHead>
                      <TableHead>Entered by</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead className="whitespace-nowrap">TDS</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="min-w-[160px]">Invoice / proof</TableHead>
                      <TableHead className="w-[200px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {centralRows.map((row: any) => (
                      <TableRow key={row.id}>
                        <TableCell className="whitespace-nowrap font-mono text-xs">
                          {row.expenseNumber || "—"}
                        </TableCell>
                        <TableCell>{formatExpenseRowDateField(row.expenseDate, row.createdAt)}</TableCell>
                        <TableCell>{formatExpenseCategoryLabel(String(row.category ?? ""))}</TableCell>
                        <TableCell className="whitespace-nowrap text-sm">
                          {formatCentralExpenseHospitalRowLabel(row, chainHospitals)}
                        </TableCell>
                        <TableCell className="max-w-[140px] truncate text-sm" title={row.vendorName || ""}>
                          {row.vendorName || "—"}
                        </TableCell>
                        <TableCell className="max-w-[140px] truncate text-sm" title={row.createdBy?.name || row.createdByName || ""}>
                          {row.createdBy?.name || row.createdByName || "—"}
                        </TableCell>
                        <TableCell className="text-right">₹{Number(row.amount).toFixed(2)}</TableCell>
                        <TableCell className="text-xs">
                          {row.tdsDeducted ? (
                            <span className="text-emerald-700 dark:text-emerald-400">Deducted</span>
                          ) : (
                            <span className="text-muted-foreground">Not deducted</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col gap-0.5">
                            <span>
                              {row.workflowStatus === "PAID" && row.paymentMode
                                ? `PAID (${String(row.paymentMode).replace(/_/g, " ")})`
                                : row.workflowStatus}
                            </span>
                            {row.workflowStatus === "PAID" && row.paidAt ? (
                              <span className="text-muted-foreground text-xs whitespace-nowrap">
                                {formatExpensePaidAtIst(row.paidAt)}
                              </span>
                            ) : null}
                            {row.fundedByImprest ? (
                              <span className="text-muted-foreground text-xs">Paid from imprest</span>
                            ) : null}
                          </div>
                        </TableCell>
                        <TableCell className="align-top">
                          <div className="flex flex-col gap-1">
                            {expenseProofList(row).map((proof) => (
                              <div key={proof.id ?? proof.url} className="flex items-start gap-1">
                                <a
                                  href={proof.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-primary inline-flex items-center gap-1 text-xs underline-offset-4 hover:underline"
                                >
                                  <ExternalLink className="h-3 w-3 shrink-0" />
                                  <span
                                    className="truncate max-w-[120px]"
                                    title={proof.fileName || "Proof"}
                                  >
                                    {proof.fileName || "View proof"}
                                  </span>
                                </a>
                                {proof.id &&
                                  !readOnlySuperAdmin &&
                                  canUploadExpenseProof(row.workflowStatus, user?.role) && (
                                    <button
                                      type="button"
                                      className="text-destructive text-[10px] hover:underline"
                                      onClick={() =>
                                        deleteCentralProofByIdMut.mutate({
                                          id: row.id,
                                          proofId: proof.id!,
                                        })
                                      }
                                    >
                                      Remove
                                    </button>
                                  )}
                              </div>
                            ))}
                            {!readOnlySuperAdmin &&
                              canUploadExpenseProof(row.workflowStatus, user?.role) &&
                              (uploadingCentralProofId === row.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <label className="text-primary cursor-pointer text-xs hover:underline">
                                  <input
                                    type="file"
                                    multiple
                                    className="sr-only"
                                    accept={PROOF_FILE_ACCEPT}
                                    onChange={(e) => {
                                      const files = Array.from(e.target.files ?? []);
                                      if (files.length)
                                        uploadCentralProofMut.mutate({ id: row.id, files });
                                      e.target.value = "";
                                    }}
                                  />
                                  {expenseProofList(row).length > 0 ? "Add file(s)" : "Attach file(s)"}
                                </label>
                              ))}
                            {(expenseProofList(row).length > 1 ||
                              (expenseProofList(row).length === 1 && !expenseProofList(row)[0]?.id)) &&
                              !readOnlySuperAdmin &&
                              canUploadExpenseProof(row.workflowStatus, user?.role) && (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 px-0 text-xs text-destructive"
                                  onClick={() => deleteCentralProofMut.mutate(row.id)}
                                >
                                  Remove all
                                </Button>
                              )}
                          </div>
                        </TableCell>
                        <TableCell className="space-x-1">
                          {canAppendOrCorrectExpenses(user?.role) &&
                            row.workflowStatus !== "REJECTED" && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="mb-1"
                                onClick={() => openEditCentral(row)}
                              >
                                <Pencil className="mr-1 h-3 w-3" />
                                Edit
                              </Button>
                            )}
                          {row.workflowStatus === "DRAFT" && (
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={() =>
                                expensesApi.submitCentral(row.id).then(() => qc.invalidateQueries())
                              }
                            >
                              Submit
                            </Button>
                          )}
                          {isHospitalAdmin && row.workflowStatus === "PENDING_APPROVAL" && (
                            <Button size="sm" onClick={() => approveCentral.mutate(row.id)}>
                              Approve
                            </Button>
                          )}
                          {!readOnlySuperAdmin &&
                            canMarkPaid &&
                            canShowMarkPaidForStatus(row.workflowStatus) && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => openMarkPaid("central", row.id, Number(row.amount))}
                              >
                                Mark paid
                              </Button>
                            )}
                          {isHospitalAdmin && row.workflowStatus === "PAID" && (
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={unmarkPaidMut.isPending}
                              onClick={() => confirmUnmarkPaid("central", row.id)}
                            >
                              Mark unpaid
                            </Button>
                          )}
                          {isHospitalAdmin && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-destructive hover:text-destructive"
                              disabled={deleteCentralExpenseMut.isPending}
                              onClick={() => {
                                if (
                                  window.confirm(
                                    "Delete this expense? This cannot be undone."
                                  )
                                ) {
                                  deleteCentralExpenseMut.mutate(row.id);
                                }
                              }}
                            >
                              <Trash2 className="mr-1 h-3 w-3" />
                              Delete
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
              {!centralLoading && (centralList?.total ?? 0) > expensePageSize ? (
                <div className="mt-3 flex items-center justify-between gap-2 text-sm">
                  <span className="text-muted-foreground">
                    Page {centralList?.page ?? 1} · {centralList?.total ?? 0} total
                  </span>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={centralPage <= 1}
                      onClick={() => setCentralPage((p) => Math.max(1, p - 1))}
                    >
                      Previous
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={centralPage * expensePageSize >= (centralList?.total ?? 0)}
                      onClick={() => setCentralPage((p) => p + 1)}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              ) : null}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="imprest" className="space-y-4">
          <p className="text-muted-foreground text-sm">
            Imprest is cash advanced to an employee (User Management). Only{" "}
            <strong>chain admin</strong> and <strong>sub-admin</strong> can issue advances — cash flow, not a
            P&amp;L expense. Chain admin picks hospital or Central; expenses and cash flow land on that
            entity. Chain/sub-admins for that hospital are included (and mirrored under General Department
            in User Management). When the employee files as <strong>Imprest advance expenses</strong>, the
            float reduces without double-counting cash out.
          </p>
          {canIssueImprest && !readOnlySuperAdmin && (
            <Card>
              <CardHeader>
                <CardTitle>Issue / top-up imprest</CardTitle>
              </CardHeader>
              <CardContent className="grid gap-3 md:grid-cols-3">
                {isChainAdmin ? (
                  <div>
                    <Label>Hospital / Central</Label>
                    <Select
                      value={imprestHospitalSelect}
                      onValueChange={(v) => {
                        setImprestHospitalSelect(v);
                        setImprestStaffId("");
                        setImprestTxnAdvanceId(null);
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select hospital or Central" />
                      </SelectTrigger>
                      <SelectContent>
                        {chainHospitalOptions.map((o) => (
                          <SelectItem key={o.value} value={o.value}>
                            {o.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ) : (
                  <div>
                    <Label>Hospital</Label>
                    <Input value={lockedHospitalName} disabled />
                  </div>
                )}
                <div>
                  <Label>Employee</Label>
                  <Select value={imprestStaffId || "__none__"} onValueChange={(v) => setImprestStaffId(v === "__none__" ? "" : v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select employee" />
                    </SelectTrigger>
                    <SelectContent className="max-h-72">
                      <SelectItem value="__none__">Select…</SelectItem>
                      {(hospitalStaff as Array<{ id: string; name: string; role?: string }>).map((s) => (
                        <SelectItem key={s.id} value={s.id}>
                          {s.name}{s.role ? ` (${String(s.role).replace(/_/g, " ")})` : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Amount (₹)</Label>
                  <Input type="number" value={imprestAmount} onChange={(e) => setImprestAmount(e.target.value)} />
                </div>
                <div>
                  <Label>Mode of payment</Label>
                  <Select value={imprestPaymentMode} onValueChange={setImprestPaymentMode}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {paymentModeOptions.map((m) => (
                        <SelectItem key={m} value={m}>
                          {String(m).replace(/_/g, " ")}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-3">
                  <Label>Notes (optional)</Label>
                  <Input value={imprestNotes} onChange={(e) => setImprestNotes(e.target.value)} placeholder="e.g. Petty cash for OT week" />
                </div>
                <div>
                  <Button
                    disabled={
                      !imprestStaffId ||
                      !imprestAmount ||
                      Number(imprestAmount) <= 0 ||
                      (isChainAdmin && !imprestHospitalSelect) ||
                      issueImprestMut.isPending
                    }
                    onClick={() => issueImprestMut.mutate()}
                  >
                    {issueImprestMut.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Issue advance
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
          {!canIssueImprest && !readOnlySuperAdmin && (
            <p className="text-muted-foreground text-sm">
              Issuing imprest is limited to chain admin and sub-admin. You can still view balances and settle
              expenses against an advance when filing.
            </p>
          )}
          <Card>
            <CardHeader>
              <CardTitle>Open imprest balances</CardTitle>
              <CardDescription className="max-w-3xl">
                Issuing imprest is cash advance to staff (not P&amp;L). When someone settles against imprest, THMS
                creates the linked patient or central expense for that hospital unit—it appears on the Central tab with
                &quot;Paid from imprest&quot; and reduces the employee&apos;s remaining balance here.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {openImprestAdvances.length === 0 ? (
                <p className="text-muted-foreground text-sm">No open imprest advances yet.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Employee</TableHead>
                      <TableHead>Hospital / unit</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead className="text-right">Issued (lifetime)</TableHead>
                      <TableHead className="text-right">Remaining</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {openImprestAdvances.map((a) => (
                      <TableRow key={a.id}>
                        <TableCell>{a.hospitalStaff?.name ?? a.hospitalStaffId}</TableCell>
                        <TableCell className="text-muted-foreground text-sm">
                          {a.hospitalUnit === "CENTRAL"
                            ? "Central"
                            : a.hospital?.name ?? formatCentralExpenseHospitalUnitLabel(a.hospitalUnit || "SEC_52")}
                        </TableCell>
                        <TableCell className="text-muted-foreground text-sm">
                          {a.hospitalStaff?.role ? String(a.hospitalStaff.role).replace(/_/g, " ") : "—"}
                        </TableCell>
                        <TableCell className="text-right">
                          ₹{Number(a.issuedAmount).toLocaleString("en-IN")}
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          ₹{Number(a.remainingBalance).toLocaleString("en-IN")}
                        </TableCell>
                        <TableCell>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() =>
                              setImprestTxnAdvanceId((cur) => (cur === a.id ? null : a.id))
                            }
                          >
                            {imprestTxnAdvanceId === a.id ? "Hide ledger" : "Ledger"}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
              {imprestTxnAdvanceId && (
                <div className="mt-4 space-y-2">
                  <h4 className="text-sm font-medium">Recent transactions</h4>
                  {imprestTxns.length === 0 ? (
                    <p className="text-muted-foreground text-sm">No transactions.</p>
                  ) : (
                    <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>When</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead className="whitespace-nowrap">Expense ID</TableHead>
                          <TableHead>Category</TableHead>
                          <TableHead>Vendor</TableHead>
                          <TableHead>Patient</TableHead>
                          <TableHead>Entered by</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead className="min-w-[120px]">Description</TableHead>
                          <TableHead className="text-right">Amount</TableHead>
                          <TableHead>Mode / note</TableHead>
                          {isHospitalAdmin && !readOnlySuperAdmin && <TableHead className="w-[88px]" />}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {imprestTxns.map((t: ImprestTxnRow) => {
                          const linked = t.type === "SETTLEMENT" ? imprestSettlementExpenseSummary(t) : null;
                          return (
                          <TableRow key={t.id}>
                            <TableCell className="text-sm whitespace-nowrap">
                              {new Date(t.txnAt).toLocaleString("en-IN")}
                            </TableCell>
                            <TableCell>{t.type}</TableCell>
                            <TableCell className="font-mono text-xs whitespace-nowrap">
                              {linked?.expenseNumber ?? "—"}
                            </TableCell>
                            <TableCell className="text-sm">{linked?.category ?? "—"}</TableCell>
                            <TableCell className="text-sm max-w-[120px] truncate" title={linked?.vendor}>
                              {linked?.vendor ?? "—"}
                            </TableCell>
                            <TableCell className="text-sm max-w-[120px] truncate" title={linked?.patientName}>
                              {linked?.patientName ?? "—"}
                            </TableCell>
                            <TableCell className="text-sm max-w-[120px] truncate" title={linked?.enteredBy}>
                              {linked?.enteredBy ?? "—"}
                            </TableCell>
                            <TableCell className="text-sm whitespace-nowrap">{linked?.status ?? "—"}</TableCell>
                            <TableCell className="text-muted-foreground text-sm max-w-[160px] truncate" title={linked?.description}>
                              {linked?.description ?? "—"}
                            </TableCell>
                            <TableCell className="text-right whitespace-nowrap">
                              ₹{Number(t.amount).toLocaleString("en-IN")}
                            </TableCell>
                            <TableCell className="text-muted-foreground text-sm max-w-[180px]" title={[t.paymentMode ? String(t.paymentMode).replace(/_/g, " ") : null, t.notes].filter(Boolean).join(" · ") || undefined}>
                              {[t.paymentMode ? String(t.paymentMode).replace(/_/g, " ") : null, t.notes]
                                .filter(Boolean)
                                .join(" · ") || "—"}
                            </TableCell>
                            {isHospitalAdmin && !readOnlySuperAdmin && (
                              <TableCell>
                                {t.type === "SETTLEMENT" && (
                                  <div className="flex justify-end gap-1">
                                    <Button
                                      type="button"
                                      size="icon"
                                      variant="ghost"
                                      className="h-8 w-8"
                                      title="Edit expense"
                                      onClick={() =>
                                        setImprestEditTxn({
                                          id: t.id,
                                          amount: String(t.amount),
                                          notes: t.notes ?? "",
                                        })
                                      }
                                    >
                                      <Pencil className="h-4 w-4" />
                                    </Button>
                                    <Button
                                      type="button"
                                      size="icon"
                                      variant="ghost"
                                      className="h-8 w-8 text-red-600 hover:text-red-700"
                                      title="Delete expense"
                                      disabled={deleteImprestSettlementMut.isPending}
                                      onClick={() => {
                                        if (
                                          window.confirm(
                                            `Delete this imprest expense of ₹${Number(t.amount).toLocaleString("en-IN")}? The amount will be added back to the employee's remaining imprest balance.`
                                          )
                                        ) {
                                          deleteImprestSettlementMut.mutate(t.id);
                                        }
                                      }}
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </div>
                                )}
                              </TableCell>
                            )}
                          </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
          <Dialog open={!!imprestEditTxn} onOpenChange={(open) => !open && setImprestEditTxn(null)}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Edit imprest expense</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <div className="space-y-2">
                  <Label htmlFor="imprest-edit-amount">Amount (₹)</Label>
                  <Input
                    id="imprest-edit-amount"
                    type="number"
                    min={0}
                    step={0.01}
                    value={imprestEditTxn?.amount ?? ""}
                    onChange={(e) =>
                      setImprestEditTxn((cur) =>
                        cur ? { ...cur, amount: e.target.value } : cur
                      )
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="imprest-edit-notes">Note / description</Label>
                  <Textarea
                    id="imprest-edit-notes"
                    value={imprestEditTxn?.notes ?? ""}
                    onChange={(e) =>
                      setImprestEditTxn((cur) =>
                        cur ? { ...cur, notes: e.target.value } : cur
                      )
                    }
                    rows={3}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setImprestEditTxn(null)}>
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={() => updateImprestSettlementMut.mutate()}
                  disabled={updateImprestSettlementMut.isPending}
                >
                  {updateImprestSettlementMut.isPending && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  Save
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>

        <TabsContent value="salary" className="space-y-4">
          <SalaryRosterPanel
            canManage={canManageSalaryRoster && !readOnlySuperAdmin}
            canBookSalaries={canManageSalaryRosterAdmin && !readOnlySuperAdmin}
            hospitalScopedOnly={canManageSalaryRosterOps && !canManageSalaryRosterAdmin}
            readOnly={readOnlySuperAdmin}
            isChainAdmin={!!isChainAdmin}
            chainHospitalOptions={chainHospitalOptions}
            paymentModes={paymentModeOptions}
            hospitalIdParam={reportParams?.hospitalId}
          />
        </TabsContent>

        <TabsContent value="pending" className="space-y-4">
          {pendingLoading ? (
            <Loader2 className="h-6 w-6 animate-spin" />
          ) : (
            <>
              <Card>
                <CardHeader>
                  <CardTitle>Patient expenses — pending</CardTitle>
                  <p className="text-muted-foreground text-sm">
                    Hospital admin can approve. Receptionist or admin can <strong>Mark paid</strong> (with payment
                    mode) without waiting for approval — payment records the expense as approved for audit.
                  </p>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Patient</TableHead>
                        <TableHead>Amount</TableHead>
                        <TableHead className="whitespace-nowrap">TDS</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead>Entered by</TableHead>
                        <TableHead>Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pending?.patient?.map((row: any) => (
                        <TableRow key={row.id}>
                          <TableCell>{row.patient?.name}</TableCell>
                          <TableCell>₹{Number(row.amount).toFixed(2)}</TableCell>
                          <TableCell className="text-xs">
                            {row.tdsDeducted ? (
                              <span className="text-emerald-700 dark:text-emerald-400">Deducted</span>
                            ) : (
                              <span className="text-muted-foreground">Not deducted</span>
                            )}
                          </TableCell>
                          <TableCell>{row.category}</TableCell>
                          <TableCell className="max-w-[140px] truncate text-sm" title={row.createdBy?.name || row.createdByName || ""}>
                            {row.createdBy?.name || row.createdByName || "—"}
                          </TableCell>
                          <TableCell className="space-x-1">
                            {!readOnlySuperAdmin && canApprovePatientExpenses(user?.role) && (
                              <Button size="sm" onClick={() => approvePatient.mutate(row.id)}>
                                Approve
                              </Button>
                            )}
                            {!readOnlySuperAdmin && canMarkPaid && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => openMarkPaid("patient", row.id, Number(row.amount))}
                              >
                                Mark paid
                              </Button>
                            )}
                            {!readOnlySuperAdmin &&
                              !canApprovePatientExpenses(user?.role) &&
                              !canMarkPaid && (
                                <span className="text-muted-foreground text-sm">No action</span>
                              )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle>Central expenses — pending</CardTitle>
                  <p className="text-muted-foreground text-sm">
                    Admin can approve. Receptionist or admin can mark paid directly (allowed categories only).
                  </p>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Category</TableHead>
                        <TableHead>Amount</TableHead>
                        <TableHead className="whitespace-nowrap">TDS</TableHead>
                        <TableHead>Entered by</TableHead>
                        <TableHead>Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pending?.central?.map((row: any) => (
                        <TableRow key={row.id}>
                          <TableCell>{row.category}</TableCell>
                          <TableCell>₹{Number(row.amount).toFixed(2)}</TableCell>
                          <TableCell className="text-xs">
                            {row.tdsDeducted ? (
                              <span className="text-emerald-700 dark:text-emerald-400">Deducted</span>
                            ) : (
                              <span className="text-muted-foreground">Not deducted</span>
                            )}
                          </TableCell>
                          <TableCell className="max-w-[140px] truncate text-sm" title={row.createdBy?.name || row.createdByName || ""}>
                            {row.createdBy?.name || row.createdByName || "—"}
                          </TableCell>
                          <TableCell className="space-x-1">
                            {isHospitalAdmin && (
                              <Button size="sm" onClick={() => approveCentral.mutate(row.id)}>
                                Approve
                              </Button>
                            )}
                            {!readOnlySuperAdmin && canMarkPaid && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => openMarkPaid("central", row.id, Number(row.amount))}
                              >
                                Mark paid
                              </Button>
                            )}
                            {!readOnlySuperAdmin && !isHospitalAdmin && !canMarkPaid && (
                              <span className="text-muted-foreground text-sm">—</span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        <TabsContent value="unpaid" className="space-y-4">
          {unpaidLoading ? (
            <Loader2 className="h-6 w-6 animate-spin" />
          ) : (
            <>
              <Card>
                <CardHeader>
                  <CardTitle>Patient expenses — unpaid</CardTitle>
                  <p className="text-muted-foreground text-sm">
                    Expenses where payment is not complete (unpaid or partially paid). Use{" "}
                    <strong>Mark paid</strong> when the vendor or payee has been settled.
                  </p>
                </CardHeader>
                <CardContent>
                  {(unpaid?.patient?.length ?? 0) === 0 ? (
                    <p className="text-muted-foreground text-sm">No unpaid patient expenses.</p>
                  ) : (
                    <>
                    <div className="mb-3 flex flex-wrap gap-3 items-end">
                      <div className="space-y-1">
                        <Label>Patient</Label>
                        <Input
                          value={unpaidPatientSearch}
                          onChange={(e) => setUnpaidPatientSearch(e.target.value)}
                          placeholder="Search patient…"
                          className="w-44"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label>IPD #</Label>
                        <Input
                          value={unpaidIpdSearch}
                          onChange={(e) => setUnpaidIpdSearch(e.target.value)}
                          placeholder="Search IPD…"
                          className="w-36"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label>Category</Label>
                        <Select value={unpaidCategoryFilter} onValueChange={setUnpaidCategoryFilter}>
                          <SelectTrigger className="w-52">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="ALL">All categories</SelectItem>
                            {patientCategoriesSorted.map((c) => (
                              <SelectItem key={c} value={c}>
                                {formatExpenseCategoryLabel(c)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label>Vendor</Label>
                        <Input
                          value={unpaidVendorSearch}
                          onChange={(e) => setUnpaidVendorSearch(e.target.value)}
                          placeholder="Search vendor…"
                          className="w-44"
                        />
                      </div>
                      {unpaidPatientFilterActive && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setUnpaidPatientSearch("");
                            setUnpaidVendorSearch("");
                            setUnpaidCategoryFilter("ALL");
                            setUnpaidIpdSearch("");
                          }}
                        >
                          Clear filters
                        </Button>
                      )}
                    </div>
                    {unpaidPatientFilterActive && (
                      <p className="text-muted-foreground mb-2 text-xs">
                        Showing {unpaidPatientRowsFiltered.length} of {unpaid?.patient?.length ?? 0}{" "}
                        unpaid rows
                      </p>
                    )}
                    {unpaidPatientRowsFiltered.length === 0 ? (
                      <p className="text-muted-foreground text-sm">No rows match these filters.</p>
                    ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>IPD #</TableHead>
                          <TableHead>Patient</TableHead>
                          <TableHead>Category</TableHead>
                          <TableHead>Vendor name</TableHead>
                          <TableHead>Entered by</TableHead>
                          <TableHead className="text-right">Amount</TableHead>
                          <TableHead>Payment</TableHead>
                          <TableHead>Approval</TableHead>
                          <TableHead>Action</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {unpaidPatientRowsFiltered.map((row: any) => (
                          <TableRow key={row.id}>
                            <TableCell className="whitespace-nowrap text-sm">
                              {formatExpenseRowDateField(row.expenseDate, row.createdAt)}
                            </TableCell>
                            <TableCell className="font-mono text-xs whitespace-nowrap">
                              {row.admission?.queue?.ipdNumber ?? "—"}
                            </TableCell>
                            <TableCell>{row.patient?.name ?? "—"}</TableCell>
                            <TableCell>{row.category}</TableCell>
                            <TableCell className="max-w-[140px] truncate text-sm" title={row.payeeVendorName || row.payeeDoctor?.name || ""}>
                              {row.payeeVendorName || row.payeeDoctor?.name || "—"}
                            </TableCell>
                            <TableCell className="max-w-[140px] truncate text-sm" title={row.createdBy?.name || row.createdByName || ""}>
                              {row.createdBy?.name || row.createdByName || "—"}
                            </TableCell>
                            <TableCell className="text-right">
                              ₹{Number(row.amount).toFixed(2)}
                              {row.paymentStatus === "PARTIALLY_PAID" && row.paidAmount != null && (
                                <span className="text-muted-foreground block text-xs">
                                  Paid ₹{Number(row.paidAmount).toFixed(2)}
                                </span>
                              )}
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className="text-amber-800 border-amber-300">
                                {row.paymentStatus === "PARTIALLY_PAID" ? "Partial" : "Unpaid"}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-xs">{row.workflowStatus}</TableCell>
                            <TableCell className="space-x-1">
                              {!readOnlySuperAdmin &&
                                canMarkPaid &&
                                canShowMarkPaidForStatus(row.workflowStatus) && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => openMarkPaid("patient", row.id, Number(row.amount))}
                                  >
                                    Mark paid
                                  </Button>
                                )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    )}
                    </>
                  )}
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle>Central expenses — unpaid</CardTitle>
                </CardHeader>
                <CardContent>
                  {(unpaid?.central?.length ?? 0) === 0 ? (
                    <p className="text-muted-foreground text-sm">No unpaid central expenses.</p>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>Category</TableHead>
                          <TableHead>Vendor</TableHead>
                          <TableHead>Entered by</TableHead>
                          <TableHead className="text-right">Amount</TableHead>
                          <TableHead>Payment</TableHead>
                          <TableHead>Approval</TableHead>
                          <TableHead>Action</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {unpaid?.central?.map((row: any) => (
                          <TableRow key={row.id}>
                            <TableCell className="whitespace-nowrap text-sm">
                              {formatExpenseRowDateField(row.expenseDate, row.createdAt)}
                            </TableCell>
                            <TableCell>{row.category}</TableCell>
                            <TableCell className="max-w-[160px] truncate" title={row.vendorName || ""}>
                              {row.vendorName || row.payeeDoctor?.name || "—"}
                            </TableCell>
                            <TableCell className="max-w-[140px] truncate text-sm" title={row.createdBy?.name || row.createdByName || ""}>
                              {row.createdBy?.name || row.createdByName || "—"}
                            </TableCell>
                            <TableCell className="text-right">
                              ₹{Number(row.amount).toFixed(2)}
                              {row.paymentStatus === "PARTIALLY_PAID" && row.paidAmount != null && (
                                <span className="text-muted-foreground block text-xs">
                                  Paid ₹{Number(row.paidAmount).toFixed(2)}
                                </span>
                              )}
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className="text-amber-800 border-amber-300">
                                {row.paymentStatus === "PARTIALLY_PAID" ? "Partial" : "Unpaid"}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-xs">{row.workflowStatus}</TableCell>
                            <TableCell className="space-x-1">
                              {!readOnlySuperAdmin &&
                                canMarkPaid &&
                                canShowMarkPaidForStatus(row.workflowStatus) && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => openMarkPaid("central", row.id, Number(row.amount))}
                                  >
                                    Mark paid
                                  </Button>
                                )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        <TabsContent value="tds" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <CardTitle>TDS Status</CardTitle>
                  <p className="text-muted-foreground text-sm">
                    All expenses where TDS was deducted, newest-first. Hospital admin can mark TDS paid with optional challan no.
                  </p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={tdsExporting || tdsLoading}
                  onClick={runTdsExport}
                >
                  {tdsExporting ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Download className="mr-2 h-4 w-4" />
                  )}
                  {tdsExporting ? "Exporting…" : "Download TDS report"}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {tdsLoading ? (
                <div className="text-muted-foreground flex items-center gap-2 text-sm">
                  <Loader2 className="h-4 w-4 animate-spin" /> Loading…
                </div>
              ) : (
                <div className="overflow-x-auto border rounded-md">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="whitespace-nowrap">Expense ID</TableHead>
                        <TableHead className="whitespace-nowrap">Type</TableHead>
                        <TableHead className="whitespace-nowrap">Expense date</TableHead>
                        <TableHead className="whitespace-nowrap">Service month</TableHead>
                        <TableHead className="whitespace-nowrap">Category</TableHead>
                        <TableHead className="whitespace-nowrap">Vendor</TableHead>
                        <TableHead className="whitespace-nowrap">Entered by</TableHead>
                        <TableHead className="whitespace-nowrap">PAN Number</TableHead>
                        <TableHead className="whitespace-nowrap">Description</TableHead>
                        <TableHead className="whitespace-nowrap text-right">Amount</TableHead>
                        <TableHead className="whitespace-nowrap">Invoice</TableHead>
                        <TableHead className="whitespace-nowrap">TDS Paid</TableHead>
                        <TableHead className="whitespace-nowrap">Challan No</TableHead>
                        <TableHead className="whitespace-nowrap">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(tdsRows || []).map((r: any) => (
                        <TableRow key={`${r.kind}-${r.id}`}>
                          <TableCell className="whitespace-nowrap font-mono text-xs">
                            {r.expenseNumber || "—"}
                          </TableCell>
                          <TableCell className="whitespace-nowrap">{r.kind === "patient" ? "Patient" : "Central"}</TableCell>
                          <TableCell className="whitespace-nowrap">
                            {formatExpenseDateIst(r.expenseDate)}
                          </TableCell>
                          <TableCell className="whitespace-nowrap">{r.serviceMonth || "—"}</TableCell>
                          <TableCell className="whitespace-nowrap">{String(r.category || "")}</TableCell>
                          <TableCell className="whitespace-nowrap">{r.vendorName || "—"}</TableCell>
                          <TableCell className="whitespace-nowrap max-w-[140px] truncate" title={r.createdByName || ""}>
                            {r.createdByName || "—"}
                          </TableCell>
                          <TableCell className="whitespace-nowrap uppercase">{r.panNumber || "—"}</TableCell>
                          <TableCell className="max-w-[360px] truncate" title={r.description || ""}>{r.description || "—"}</TableCell>
                          <TableCell className="text-right whitespace-nowrap">₹{Number(r.amount || 0).toFixed(2)}</TableCell>
                          <TableCell className="whitespace-nowrap">
                            {Array.isArray(r.proofs) && r.proofs.length > 0 ? (
                              <div className="flex flex-col gap-0.5">
                                {r.proofs.map((p: { id: string; url: string; fileName: string }) => (
                                  <a
                                    key={p.id}
                                    href={p.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-primary inline-flex items-center gap-1 text-xs underline-offset-4 hover:underline"
                                  >
                                    <ExternalLink className="h-3 w-3 shrink-0" />
                                    <span className="truncate max-w-[100px]" title={p.fileName}>
                                      {p.fileName || "View"}
                                    </span>
                                  </a>
                                ))}
                              </div>
                            ) : (
                              "—"
                            )}
                          </TableCell>
                          <TableCell className="whitespace-nowrap">
                            {r.tdsPaid ? (
                              <Badge variant="outline" className="text-green-900 border-green-700 bg-green-50">
                                Paid
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-amber-900 border-amber-700 bg-amber-50">
                                Pending
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell className="whitespace-nowrap">{r.tdsChallanNo || "—"}</TableCell>
                          <TableCell className="whitespace-nowrap">
                            {isHospitalAdmin && !r.tdsPaid ? (
                              <Button size="sm" variant="outline" onClick={() => openTdsPaid(r.kind, r.id)}>
                                Mark TDS paid
                              </Button>
                            ) : (
                              <span className="text-muted-foreground text-sm">—</span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                      {(!tdsRows || tdsRows.length === 0) && (
                        <TableRow>
                          <TableCell colSpan={14} className="text-muted-foreground text-sm">
                            No TDS deducted expenses found.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="reports" className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <Label className="text-sm font-medium">Report cost basis</Label>
            <Select
              value={reportCostBasis}
              onValueChange={(v) => setReportCostBasis(v as "accrual" | "cash")}
            >
              <SelectTrigger className="w-[220px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="accrual">
                  Accrual — include approved &amp; paid (default)
                </SelectItem>
                <SelectItem value="cash">Cash — only after mark paid</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Card>
            <CardHeader>
              <CardTitle>Patient profitability (per IPD admission)</CardTitle>
              <p className="text-muted-foreground text-sm">
                Each row is one <strong>IPD admission</strong> (one stay). The same patient can appear on multiple rows if they had multiple admissions.
                Revenue = IPD bill net for that admission. Costs = patient expenses{" "}
                <strong>linked to that admission id only</strong>.
              </p>
              {profReport?.costBasisDescription && (
                <p className="text-muted-foreground border-l-primary mt-2 border-l-2 pl-3 text-sm">
                  <span className="font-medium uppercase">{profReport.costBasis}</span>:{" "}
                  {profReport.costBasisDescription}
                </p>
              )}
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>IPD #</TableHead>
                    <TableHead>Patient</TableHead>
                    <TableHead className="text-right">Revenue</TableHead>
                    <TableHead className="text-right">Expenses</TableHead>
                    <TableHead className="text-right">Margin</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {profReport?.rows?.map((r) => (
                    <TableRow key={r.admissionId}>
                      <TableCell className="font-mono text-xs">{r.ipdNumber ?? r.admissionId.slice(0, 8)}</TableCell>
                      <TableCell>{r.patient?.name}</TableCell>
                      <TableCell className="text-right">₹{Number(r.revenue).toFixed(2)}</TableCell>
                      <TableCell className="text-right">₹{Number(r.patientExpenses).toFixed(2)}</TableCell>
                      <TableCell className="text-right">₹{Number(r.grossMargin).toFixed(2)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>Central expenses (YTD monthly view)</CardTitle>
              {centralMonthly?.costBasis && (
                <p className="text-muted-foreground text-sm">
                  Using <strong>{centralMonthly.costBasis}</strong> basis (same as selector above).
                </p>
              )}
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Category</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead className="text-right">Count</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {centralMonthly?.grouped?.map((g) => (
                    <TableRow key={g.category}>
                      <TableCell>{g.category}</TableCell>
                      <TableCell className="text-right">₹{Number(g._sum.amount ?? 0).toFixed(2)}</TableCell>
                      <TableCell className="text-right">{g._count.id}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog
        open={markPaidOpen}
        onOpenChange={(o) => {
          setMarkPaidOpen(o);
          if (!o) setMarkPaidTarget(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mark expense paid</DialogTitle>
          </DialogHeader>
          {markPaidTarget && (
            <div className="grid gap-4 py-2">
              <p className="text-muted-foreground text-sm">
                Amount: <strong>₹{markPaidTarget.amount.toFixed(2)}</strong> (
                {markPaidTarget.kind === "patient" ? "Patient" : "Central"} expense)
              </p>
              <div>
                <Label>Payment mode</Label>
                <Select value={markPaidMode} onValueChange={setMarkPaidMode}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {paymentModeOptions.map((m) => (
                      <SelectItem key={m} value={m}>
                        {m.replace(/_/g, " ")}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Paid amount (₹)</Label>
                <Input
                  type="number"
                  value={markPaidAmount}
                  onChange={(e) => setMarkPaidAmount(e.target.value)}
                />
                <p className="text-muted-foreground mt-1 text-xs">
                  Defaults to full amount; change for a partial payment.
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setMarkPaidOpen(false)}>
              Cancel
            </Button>
            <Button
              disabled={markPaidMut.isPending || !markPaidTarget}
              onClick={() => markPaidMut.mutate()}
            >
              {markPaidMut.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Confirm paid
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={editPatientOpen}
        onOpenChange={(o) => {
          setEditPatientOpen(o);
          if (!o) setEditPatientId(null);
        }}
      >
        <DialogContent className="max-w-md max-h-[90vh] flex flex-col gap-0 overflow-hidden p-0">
          <DialogHeader className="shrink-0 px-6 pt-6 pb-2">
            <DialogTitle>Edit patient expense (admin)</DialogTitle>
          </DialogHeader>
          {editPatientId && (
            <div className="min-h-0 flex-1 overflow-y-auto px-6 py-2">
            <div className="grid gap-3">
              <div>
                <Label>Category</Label>
                <Select value={editPatientCategory} onValueChange={setEditPatientCategory}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    {patientCategoriesSorted.map((c) => (
                      <SelectItem key={c} value={c} className="font-normal">
                        {formatExpenseCategoryLabel(c)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {editPatientCategory === B2B_PARTNER_PAYOUT_CATEGORY && (
                <div>
                  <Label>B2B partner</Label>
                  <Select
                    value={editPatientB2bPartner || ""}
                    onValueChange={(v) => {
                      const raw = String(v || "");
                      if (raw === "__add__") {
                        setAddB2bPartnerOpen(true);
                        return;
                      }
                      setEditPatientB2bPartner(raw);
                      setEditPatientVendor(raw);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select partner" />
                    </SelectTrigger>
                    <SelectContent className="max-h-72">
                      {Array.from(
                        new Set([
                          ...b2bPartnerNames,
                          ...(editPatientB2bPartner ? [editPatientB2bPartner] : []),
                        ])
                      ).map((n) => (
                        <SelectItem key={n} value={n}>
                          {n}
                        </SelectItem>
                      ))}
                      <SelectItem value="__add__">Add partner…</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label>Amount (₹)</Label>
                <Input
                  type="number"
                  value={editPatientAmount}
                  onChange={(e) => setEditPatientAmount(e.target.value)}
                />
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="edit-patient-tds-deducted"
                  checked={editPatientTdsDeducted}
                  onCheckedChange={(checked) => setEditPatientTdsDeducted(!!checked)}
                />
                <Label htmlFor="edit-patient-tds-deducted" className="text-sm font-normal">
                  TDS deducted (amount is net paid)
                </Label>
              </div>
              <div>
                <Label>Expense date</Label>
                <Input
                  type="date"
                  value={editPatientExpenseDate}
                  onChange={(e) => setEditPatientExpenseDate(e.target.value)}
                />
              </div>
              <div>
                <Label>Service month</Label>
                <Input
                  type="month"
                  value={editPatientServiceMonth}
                  onChange={(e) => setEditPatientServiceMonth(e.target.value)}
                />
              </div>
              <div>
                <Label>Payment mode</Label>
                <Select
                  value={editPatientPaymentMode || "NONE"}
                  onValueChange={(v) => setEditPatientPaymentMode(v === "NONE" ? "" : v)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    <SelectItem value="NONE">Not set</SelectItem>
                    {meta?.paymentModes?.map((m) => (
                      <SelectItem key={m} value={m}>
                        {m.replace(/_/g, " ")}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {isStrictHospitalAdmin && editPatientWorkflowStatus === "PAID" ? (
                <div>
                  <Label>Paid date</Label>
                  <Input
                    type="datetime-local"
                    value={editPatientPaidAt}
                    onChange={(e) => setEditPatientPaidAt(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Cash flow and reports use this date for paid IPD expenses (IST).
                  </p>
                </div>
              ) : null}
              <div>
                <Label>Vendor name</Label>
                <Input value={editPatientVendor} onChange={(e) => setEditPatientVendor(e.target.value)} />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea value={editPatientDesc} onChange={(e) => setEditPatientDesc(e.target.value)} rows={2} />
              </div>
            </div>
            </div>
          )}
          <DialogFooter className="shrink-0 border-t px-6 py-4">
            <Button variant="outline" onClick={() => setEditPatientOpen(false)}>
              Cancel
            </Button>
            <Button
              disabled={
                savePatientEditMut.isPending ||
                !editPatientId ||
                !editPatientCategory ||
                !editPatientAmount ||
                (isStrictHospitalAdmin &&
                  editPatientWorkflowStatus === "PAID" &&
                  !editPatientPaidAt.trim())
              }
              onClick={() => savePatientEditMut.mutate()}
            >
              {savePatientEditMut.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={editCentralOpen}
        onOpenChange={(o) => {
          setEditCentralOpen(o);
          if (!o) setEditCentralId(null);
        }}
      >
        <DialogContent className="max-w-md max-h-[90vh] flex flex-col gap-0 overflow-hidden p-0">
          <DialogHeader className="shrink-0 px-6 pt-6 pb-2">
            <DialogTitle>Edit central expense (admin)</DialogTitle>
          </DialogHeader>
          {editCentralId && (
            <div className="min-h-0 flex-1 overflow-y-auto px-6 py-2">
            <div className="grid gap-3">
              <div>
                <Label>Category</Label>
                <Select
                  value={editCentralCategory}
                  onValueChange={(v) => {
                    setEditCentralCategory(v);
                    if (v === "PHARMA_PAYOUT") setEditCentralVendor("");
                    else setEditCentralPharmaVendorId("");
                    if (!isCentralDoctorPayeeCategory(v)) setEditCentralPayeeHospitalStaffId("");
                  }}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    {centralCategoriesAllSorted.map((c) => (
                      <SelectItem key={c} value={c} className="font-normal">
                        {formatExpenseCategoryLabel(c)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Hospital</Label>
                {isChainAdmin ? (
                  <Select value={editCentralHospitalSelect} onValueChange={setEditCentralHospitalSelect}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {chainHospitalOptions.map((u) => (
                        <SelectItem key={u.value} value={u.value}>
                          {u.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    value={lockedHospitalName}
                    readOnly
                    className="bg-muted"
                  />
                )}
              </div>
              <div>
                <Label>Expense date</Label>
                <Input
                  type="date"
                  value={editCentralExpenseDate}
                  onChange={(e) => setEditCentralExpenseDate(e.target.value)}
                />
              </div>
              <div>
                <Label>Service month</Label>
                <Input
                  type="month"
                  value={editCentralServiceMonth}
                  onChange={(e) => setEditCentralServiceMonth(e.target.value)}
                />
              </div>
              <div>
                <Label>Payment mode</Label>
                <Select
                  value={editCentralPaymentMode || "NONE"}
                  onValueChange={(v) => setEditCentralPaymentMode(v === "NONE" ? "" : v)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    <SelectItem value="NONE">Not set</SelectItem>
                    {meta?.paymentModes?.map((m) => (
                      <SelectItem key={m} value={m}>
                        {m.replace(/_/g, " ")}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {isStrictHospitalAdmin && editCentralWorkflowStatus === "PAID" ? (
                <div>
                  <Label>Paid date</Label>
                  <Input
                    type="datetime-local"
                    value={editCentralPaidAt}
                    onChange={(e) => setEditCentralPaidAt(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Cash flow and reports use this date for paid central expenses (IST).
                  </p>
                </div>
              ) : null}
              <div>
                <Label>Amount (₹)</Label>
                <Input
                  type="number"
                  value={editCentralAmount}
                  onChange={(e) => setEditCentralAmount(e.target.value)}
                />
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="edit-central-tds-deducted"
                  checked={editCentralTdsDeducted}
                  onCheckedChange={(checked) => setEditCentralTdsDeducted(!!checked)}
                />
                <Label htmlFor="edit-central-tds-deducted" className="text-sm font-normal">
                  TDS deducted (amount is net paid)
                </Label>
              </div>
              <div>
                <Label>Invoice no</Label>
                <Input value={editCentralInvoiceNo} onChange={(e) => setEditCentralInvoiceNo(e.target.value)} />
              </div>
              {editCentralCategory === "PHARMA_PAYOUT" ? (
                <div>
                  <Label>Pharma vendor</Label>
                  <Select value={editCentralPharmaVendorId} onValueChange={setEditCentralPharmaVendorId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select vendor" />
                    </SelectTrigger>
                    <SelectContent className="max-h-72">
                      {pharmaVendors.map((v) => (
                        <SelectItem key={v.id} value={v.id}>
                          {v.name}
                          {v.phone ? ` · ${v.phone}` : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : isCentralDoctorPayeeCategory(editCentralCategory) ? (
                <div>
                  <Label>Doctor</Label>
                  <SearchableSelect
                    value={
                      editCentralPayeeHospitalStaffId.trim()
                        ? `staff:${editCentralPayeeHospitalStaffId.trim()}`
                        : editCentralVendor.trim()
                          ? `name:${editCentralVendor.trim()}`
                          : ""
                    }
                    onValueChange={(v) => {
                      const raw = String(v || "");
                      if (raw.startsWith("staff:")) {
                        const id = raw.slice("staff:".length);
                        setEditCentralPayeeHospitalStaffId(id);
                        const d = (doctors as any[]).find((x) => x.id === id);
                        if (d?.name) setEditCentralVendor(String(d.name));
                        return;
                      }
                      if (raw.startsWith("name:")) {
                        const name = raw.slice("name:".length);
                        setEditCentralPayeeHospitalStaffId("");
                        setEditCentralVendor(name);
                        return;
                      }
                      setEditCentralPayeeHospitalStaffId("");
                    }}
                    placeholder="Select doctor (or type name)"
                    searchPlaceholder="Type doctor name…"
                    options={buildStaffOrVendorPayeeOptions(
                      doctors as Array<{ id: string; name: string }>,
                      editCentralPayeeVendorNames,
                      editCentralVendor
                    )}
                  />
                  <Input
                    value={editCentralVendor}
                    onChange={(e) => {
                      setEditCentralVendor(e.target.value);
                      if (e.target.value.trim()) setEditCentralPayeeHospitalStaffId("");
                    }}
                    placeholder="Type doctor name if not in list"
                  />
                </div>
              ) : (
                <div>
                  <Label>Vendor name</Label>
                  <CentralVendorNameTypeahead
                    value={editCentralVendor}
                    onChange={setEditCentralVendor}
                    searchAllCategories
                    queryEnabled={(!isSuperAdmin || !!hospitalId) && editCentralOpen}
                    hospitalId={reportParams?.hospitalId}
                    placeholder="Supplier or vendor"
                  />
                </div>
              )}
              <div>
                <Label>Description</Label>
                <Textarea value={editCentralDesc} onChange={(e) => setEditCentralDesc(e.target.value)} rows={2} />
              </div>
            </div>
            </div>
          )}
          <DialogFooter className="shrink-0 border-t px-6 py-4">
            <Button variant="outline" onClick={() => setEditCentralOpen(false)}>
              Cancel
            </Button>
            <Button
              disabled={
                saveCentralEditMut.isPending ||
                !editCentralId ||
                !editCentralCategory ||
                !editCentralAmount ||
                  (isStrictHospitalAdmin &&
                    editCentralWorkflowStatus === "PAID" &&
                    !editCentralPaidAt.trim()) ||
                  (editCentralCategory === "PHARMA_PAYOUT" && !editCentralPharmaVendorId.trim()) ||
                  (isCentralDoctorPayeeCategory(editCentralCategory) &&
                    !editCentralPayeeHospitalStaffId.trim() &&
                    !editCentralVendor.trim())
              }
              onClick={() => saveCentralEditMut.mutate()}
            >
              {saveCentralEditMut.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={moduleCostBreakdown != null}
        onOpenChange={(o) => {
          if (!o) setModuleCostBreakdown(null);
        }}
      >
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {moduleCostBreakdown === "pharma" ? "Pharma cost breakdown" : "Lab cost breakdown (Internal / External)"}
            </DialogTitle>
          </DialogHeader>
          {moduleCostBreakdown === "pharma" ? (
            selectedPharmaCostItems.length === 0 ? (
              <p className="text-sm text-muted-foreground">No pharma cost lines for this admission.</p>
            ) : (
              <div className="rounded-md border overflow-auto max-h-[50vh]">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-slate-50 border-b">
                    <tr>
                      <th className="px-2 py-2 w-10" aria-label="Include in reports" />
                      <th className="px-3 py-2 text-left">Date</th>
                      <th className="px-3 py-2 text-left">Item</th>
                      <th className="px-3 py-2 text-right">Qty</th>
                      <th className="px-3 py-2 text-right">Unit cost</th>
                      <th className="px-3 py-2 text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedPharmaCostItems.map((item) => {
                      const included = item.includedInReports !== false;
                      return (
                        <tr
                          key={item.id}
                          className={cn(
                            "border-b last:border-b-0",
                            !included && "opacity-60 bg-slate-50/50"
                          )}
                        >
                          <td className="px-2 py-2 align-top">
                            <PharmaCostReportInclusionCheckbox item={item} />
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">{formatExpenseDateIst(item.date)}</td>
                          <td className={cn("px-3 py-2", !included && "line-through text-muted-foreground")}>
                            {item.label}
                            {item.kind === "OT_PHARMA" ? (
                              <span className="text-muted-foreground ml-1 text-xs">(OT)</span>
                            ) : null}
                            {!included ? (
                              <span className="block text-[10px] text-muted-foreground no-underline">
                                Excluded from reports
                              </span>
                            ) : null}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums">{item.quantity}</td>
                          <td className="px-3 py-2 text-right tabular-nums">
                            ₹{item.unitCost.toLocaleString("en-IN", { maximumFractionDigits: 2 })}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums font-medium">
                            ₹{item.amount.toLocaleString("en-IN", { maximumFractionDigits: 2 })}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot className="border-t bg-slate-50">
                    <tr>
                      <td colSpan={5} className="px-3 py-2 text-right font-medium">
                        Total (included in reports)
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums font-semibold">
                        ₹{(selectedAdmissionCostTotals?.pharma ?? 0).toLocaleString("en-IN", {
                          maximumFractionDigits: 2,
                        })}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )
          ) : selectedLabCostItems.length === 0 ? (
            <p className="text-sm text-muted-foreground">No lab cost lines for this admission.</p>
          ) : (
            <div className="rounded-md border overflow-auto max-h-[50vh]">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-slate-50 border-b">
                  <tr>
                    <th className="px-3 py-2 text-left">Date</th>
                    <th className="px-3 py-2 text-left">Test</th>
                    <th className="px-3 py-2 text-left">Type</th>
                    <th className="px-3 py-2 text-left">Vendor</th>
                    <th className="px-3 py-2 text-right">Customer price</th>
                    <th className="px-3 py-2 text-right">Vendor cost</th>
                    <th className="px-3 py-2 text-right">Margin</th>
                    <th className="px-3 py-2 text-right">Total cost</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedLabCostItems.map((item) => (
                    <tr key={item.id} className="border-b last:border-b-0">
                      <td className="px-3 py-2 whitespace-nowrap">{formatExpenseDateIst(item.date)}</td>
                      <td className="px-3 py-2">{item.label}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-xs">
                        {item.processingType === "EXTERNAL" ? "External" : "Internal"}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-xs">
                        {item.processingType === "EXTERNAL" ? item.vendor || "—" : "—"}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        ₹{item.customerPrice.toLocaleString("en-IN", { maximumFractionDigits: 2 })}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        ₹{item.externalCost.toLocaleString("en-IN", { maximumFractionDigits: 2 })}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        ₹{item.margin.toLocaleString("en-IN", { maximumFractionDigits: 2 })}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums font-medium">
                        ₹{item.total.toLocaleString("en-IN", { maximumFractionDigits: 2 })}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="border-t bg-slate-50">
                  <tr>
                    <td colSpan={5} className="px-3 py-2 text-right font-medium">
                      Internal
                    </td>
                    <td colSpan={3} className="px-3 py-2 text-right tabular-nums font-semibold">
                      ₹{(selectedAdmissionCostTotals?.labInternal ?? 0).toLocaleString("en-IN", {
                        maximumFractionDigits: 2,
                      })}
                    </td>
                  </tr>
                  <tr>
                    <td colSpan={5} className="px-3 py-2 text-right font-medium">
                      External (vendor cost)
                    </td>
                    <td colSpan={3} className="px-3 py-2 text-right tabular-nums font-semibold">
                      ₹{(selectedAdmissionCostTotals?.labExternal ?? 0).toLocaleString("en-IN", {
                        maximumFractionDigits: 2,
                      })}
                    </td>
                  </tr>
                  <tr>
                    <td colSpan={5} className="px-3 py-2 text-right font-medium">
                      Lab total (internal + external vendor)
                    </td>
                    <td colSpan={3} className="px-3 py-2 text-right tabular-nums font-semibold">
                      ₹{(selectedAdmissionCostTotals?.lab ?? 0).toLocaleString("en-IN", {
                        maximumFractionDigits: 2,
                      })}
                    </td>
                  </tr>
                  <tr>
                    <td colSpan={8} className="px-3 py-2 text-[11px] italic text-muted-foreground">
                      External vendor costs are excluded from the IPD Combined expense total.
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setModuleCostBreakdown(null)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={tdsDialogOpen}
        onOpenChange={(o) => {
          setTdsDialogOpen(o);
          if (!o) setTdsTarget(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mark TDS paid</DialogTitle>
          </DialogHeader>
          <div className="grid gap-3 py-2">
            <div>
              <Label>Challan no (optional)</Label>
              <Input
                value={tdsChallanDraft}
                onChange={(e) => setTdsChallanDraft(e.target.value)}
                placeholder="e.g. Challan reference"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTdsDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              disabled={markTdsPaidMut.isPending || !tdsTarget}
              onClick={() => {
                if (!tdsTarget) return;
                markTdsPaidMut.mutate({
                  kind: tdsTarget.kind,
                  id: tdsTarget.id,
                  challanNo: tdsChallanDraft.trim() || null
                });
              }}
            >
              {markTdsPaidMut.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Mark paid
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
